import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getDeviceFingerprint } from './deviceFingerprint';
import * as passkeyUtils from './passkeyUtils';
import { detectInAppBrowser, getPlatformSummary } from './browserDetection';
import { reportClientEvent } from './clientTelemetry';
import KaraFunWebSocketHandler from './KaraFunWebSocketHandler';
import { IoMusicalNotes, IoMusicalNote, IoArrowForwardCircle } from 'react-icons/io5';
import { GiMusicalNotes, GiMicrophone } from 'react-icons/gi';
import { BsMusicNote } from 'react-icons/bs';
import { FaMicrophone } from 'react-icons/fa';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

console.log('App initialized with BACKEND_URL:', BACKEND_URL);

// Browser notification utility
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

const sendBrowserNotification = (title, body, tag = null) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const options = {
      body: body,
      icon: '/favicon.ico',
      tag: tag || 'karaoke-notification',
      requireInteraction: false
    };
    
    return new Notification(title, options);
  }
  return null;
};

// Components
// Components

/* ── Song-history dropdown row (2026-09-24) ─────────────────────────────────
   Reusable row that supports:
     • Normal click → select the song (existing behavior).
     • Long-press (500ms, movement threshold 10px) → invoke `onLongPress(item)`.
   Pointer events cover both mouse and touch. Long-press is designed to open
   a confirmation modal to delete the entry from the user's song_history.
   The row still uses `onMouseDown={preventDefault + stopPropagation}` so it
   doesn't collapse the parent dropdown's outside-click backdrop. */
const HistoryDropdownRow = ({ item, index, testIdPrefix, onSelect, onLongPress, className }) => {
  const timerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const longPressFiredRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handlePointerDown = (e) => {
    longPressFiredRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      timerRef.current = null;
      if (typeof onLongPress === 'function') onLongPress(item);
    }, 500);
  };

  const handlePointerMove = (e) => {
    if (!timerRef.current) return;
    const dx = e.clientX - startPosRef.current.x;
    const dy = e.clientY - startPosRef.current.y;
    if ((dx * dx + dy * dy) > 100) {  // > 10px movement cancels
      clearTimer();
    }
  };

  const handlePointerUpOrCancel = () => {
    clearTimer();
  };

  const handleClick = (e) => {
    if (longPressFiredRef.current) {
      // Long-press already fired the delete flow; swallow this click.
      longPressFiredRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (typeof onSelect === 'function') onSelect(item);
  };

  return (
    <div
      key={`${item.song_id ?? item.song_title}-${index}`}
      data-testid={`${testIdPrefix}-${index}`}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUpOrCancel}
      onPointerCancel={handlePointerUpOrCancel}
      onPointerLeave={handlePointerUpOrCancel}
      onClick={handleClick}
      className={className}
      title="Tap to select · Long-press to delete from history"
    >
      <div className="font-semibold text-white">{item.song_title}</div>
      <div className="text-sm text-gray-400">{item.song_artist}</div>
      <div className="text-xs text-gray-500 mt-1">
        {item.play_count > 1 ? `Sung ${item.play_count} times` : 'Sung 1 time'}
        {item.last_played && (
          <> · Last: {new Date(item.last_played).toLocaleDateString()}</>
        )}
      </div>
    </div>
  );
};

/* ── Song-history delete confirmation modal (2026-09-24) ────────────────────
   Displays "Remove [song] — [artist] from [subject] history?" with Yes/Cancel.
   Fire-and-forget (no undo). Fully controlled — parent owns state. */
const HistoryDeleteConfirmModal = ({ item, subjectLabel, isSubmitting, onConfirm, onCancel }) => {
  if (!item) return null;
  return (
    <div
      data-testid="history-delete-confirm-modal"
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-gray-900 border-2 border-red-500/60 rounded-lg shadow-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-red-200 mb-3 flex items-center gap-2">
          <span aria-hidden>🗑️</span>
          Remove from history?
        </h3>
        <p className="text-gray-200 text-sm leading-relaxed">
          Remove <span className="font-semibold text-white">{item.song_title}</span>
          {' — '}
          <span className="font-semibold text-white">{item.song_artist}</span>
          {' '}from {subjectLabel} history?
        </p>
        <p className="text-gray-400 text-xs mt-2">
          This won't affect any live queue entries — only the saved history record. Cannot be undone.
        </p>
        <div className="flex gap-3 mt-5">
          <button
            type="button"
            data-testid="history-delete-cancel-btn"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="history-delete-confirm-btn"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Removing…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Alias row 2026-09-22 ──────────────────────────────────────────────────
   Reusable row that supports a short-hold (500ms) long-press to open a
   delete-confirm modal. Mirrors HistoryDropdownRow's interaction shape:
   pointer events cover mouse + touch, 10px movement threshold cancels
   the hold. Aliases are display-only otherwise (no click action). */
const AliasRow = ({ alias, index, onLongPress, className }) => {
  const timerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handlePointerDown = (e) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (typeof onLongPress === 'function') onLongPress(alias);
    }, 500);
  };

  const handlePointerMove = (e) => {
    if (!timerRef.current) return;
    const dx = e.clientX - startPosRef.current.x;
    const dy = e.clientY - startPosRef.current.y;
    if ((dx * dx + dy * dy) > 100) clearTimer();  // > 10px cancels
  };

  const handlePointerUpOrCancel = () => clearTimer();

  return (
    <div
      data-testid={`manage-users-alias-${index}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUpOrCancel}
      onPointerCancel={handlePointerUpOrCancel}
      onPointerLeave={handlePointerUpOrCancel}
      className={className}
      title="Long-press to delete this alias"
    >
      <span className="text-gray-200">{alias.name}</span>
      <span className="text-xs text-gray-500">
        used {alias.count}× · last {alias.last_seen ? new Date(alias.last_seen).toLocaleDateString() : '—'}
      </span>
    </div>
  );
};

const AliasDeleteConfirmModal = ({ alias, subjectLabel, isSubmitting, onConfirm, onCancel }) => {
  if (!alias) return null;
  return (
    <div
      data-testid="manage-users-alias-delete-modal"
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-gray-900 border-2 border-red-500/60 rounded-lg shadow-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-red-200 mb-3 flex items-center gap-2">
          <span aria-hidden>🗑️</span>
          Remove alias?
        </h3>
        <p className="text-gray-200 text-sm leading-relaxed">
          Remove the alias{' '}
          <span className="font-semibold text-white">"{alias.name}"</span>{' '}
          from {subjectLabel} previously-used names?
        </p>
        <p className="text-gray-400 text-xs mt-2">
          This only scrubs the alias record — no queue entries, songs, or the
          user's main name are affected. Cannot be undone.
        </p>
        <div className="flex gap-3 mt-5">
          <button
            type="button"
            data-testid="manage-users-alias-delete-cancel-btn"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="manage-users-alias-delete-confirm-btn"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Removing…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SongSelector = ({ songs, selectedSong, onSongSelect, disabled }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search to avoid too many API calls
  React.useEffect(() => {
    const searchSongs = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await axios.get(`${API}/songs`, {
          params: { search: searchTerm }
        });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Error searching songs:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timer = setTimeout(searchSongs, 300); // Debounce for 300ms
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get selected song details
  const selectedSongDetails = songs.find(song => song.id === selectedSong) || 
                               searchResults.find(song => song.id === selectedSong);

  const handleSongSelect = (songId) => {
    const songDetails = searchResults.find(song => song.id === songId);
    onSongSelect(songId, songDetails);
    setSearchTerm('');
    setIsFocused(false);
  };

  const clearSelection = () => {
    onSongSelect('');
    setSearchTerm('');
  };

  return (
    <div className="mb-4 relative">
      <label className="block text-sm font-medium text-white mb-2">
        Choose Your Song
      </label>
      
      {/* Show selected song if one is chosen */}
      {selectedSong && selectedSongDetails && (
        <div className="mb-2 p-3 bg-blue-900/30 border border-blue-400 rounded-lg flex justify-between items-center">
          <div>
            <div className="font-medium text-white">{selectedSongDetails.title}</div>
            <div className="text-sm text-gray-300">{selectedSongDetails.artist}</div>
          </div>
          <button
            type="button"
            onClick={clearSelection}
            disabled={disabled}
            className="text-gray-400 hover:text-white text-xl"
          >
            ✕
          </button>
        </div>
      )}

      {/* Single Search Field */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay to allow click on results
            setTimeout(() => setIsFocused(false), 200);
          }}
          placeholder="Search by song title or artist..."
          disabled={disabled}
          className="form-input w-full p-3 rounded-lg"
        />

        {/* Search Results Dropdown */}
        {isFocused && searchTerm.length >= 2 && (
          <div className="absolute z-10 w-full mt-1 card border border-blue-400 rounded-lg shadow-lg max-h-80 overflow-y-auto custom-scrollbar">
            {isSearching ? (
              <div className="p-4 text-center text-gray-400">
                Searching...
              </div>
            ) : searchResults.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                No songs found. Try a different search term.
              </div>
            ) : (
              <>
                <div className="p-2 text-xs text-gray-400 border-b border-gray-700">
                  {searchResults.length} song{searchResults.length !== 1 ? 's' : ''} found
                </div>
                {searchResults.map((song) => (
                  <div
                    key={song.id}
                    onClick={() => handleSongSelect(song.id)}
                    className="p-3 cursor-pointer border-b border-gray-700 last:border-b-0 hover:bg-blue-900/20"
                  >
                    <div className="font-medium text-white">{song.title}</div>
                    <div className="text-sm text-gray-300">{song.artist}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Helper text */}
        {!selectedSong && searchTerm.length < 2 && searchTerm.length > 0 && (
          <div className="text-xs text-gray-400 mt-1">
            Type at least 2 characters to search
          </div>
        )}
      </div>
    </div>
  );
};

const HoldToResetButton = ({ onResetSession }) => {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const holdTimeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startPositionRef = useRef(null);
  const HOLD_DURATION = 3000; // 3 seconds

  const startHold = (e) => {
    // Store starting position for mouse events
    if (e.type === 'mousedown') {
      startPositionRef.current = { x: e.clientX, y: e.clientY };
    }
    
    setIsHolding(true);
    setProgress(0);

    // Update progress every 50ms
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (50 / HOLD_DURATION) * 100;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 50);

    // Show confirmation modal after hold duration
    holdTimeoutRef.current = setTimeout(() => {
      setIsHolding(false);
      setProgress(0);
      clearInterval(progressIntervalRef.current);
      setShowConfirmModal(true);
    }, HOLD_DURATION);
  };

  const cancelHold = () => {
    setIsHolding(false);
    setProgress(0);
    startPositionRef.current = null;
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  };
  
  // Allow small mouse movements without canceling
  const handleMouseMove = (e) => {
    if (isHolding && startPositionRef.current) {
      const dx = Math.abs(e.clientX - startPositionRef.current.x);
      const dy = Math.abs(e.clientY - startPositionRef.current.y);
      // Cancel only if mouse moved more than 20 pixels
      if (dx > 20 || dy > 20) {
        cancelHold();
      }
    }
  };

  const handleConfirmReset = () => {
    setShowConfirmModal(false);
    onResetSession();
  };

  const handleCancelReset = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  return (
    <>
      <button
        onMouseDown={startHold}
        onMouseUp={cancelHold}
        onMouseMove={handleMouseMove}
        onTouchStart={startHold}
        onTouchEnd={cancelHold}
        className="relative bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800 font-semibold text-sm overflow-hidden select-none"
        title="Hold for 3 seconds to reset session"
      >
        {/* Progress bar background */}
        <div
          className="absolute inset-0 bg-red-900 transition-all duration-75"
          style={{ 
            width: `${progress}%`,
            opacity: 0.7
          }}
        />
        
        {/* Button text */}
        <span className="relative z-10">
          {isHolding ? `Hold... ${Math.ceil((100 - progress) / 33)}s` : '🔄 Reset Session'}
        </span>
      </button>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <>
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={handleCancelReset}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border-2 border-gray-600 rounded-xl shadow-2xl p-6 max-w-md w-full">
              <div className="text-white font-bold mb-2 text-lg">
                🔄 Reset Session
              </div>
              <div className="text-gray-400 text-sm mb-4">
                Are you sure you want to reset the session? This will clear all played statuses and restart the queue rotation.
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={handleConfirmReset}
                  className="w-full bg-red-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-red-700 transition-all"
                >
                  ✅ Yes, Reset Session
                </button>
                <button
                  onClick={handleCancelReset}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────
// HoldToDeleteUserButton — mirrors HoldToResetButton (3s hold + modal).
// Used inside the Manage Users detail view to permanently delete a
// user identity (archives to `deleted_users_archive` for 30-day undo).
// ─────────────────────────────────────────────────────────────────────
const HoldToDeleteUserButton = ({ userLabel, onDelete, disabled = false }) => {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const holdTimeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startPositionRef = useRef(null);
  const HOLD_DURATION = 3000;

  const startHold = (e) => {
    if (disabled || isSubmitting) return;
    if (e.type === 'mousedown') {
      startPositionRef.current = { x: e.clientX, y: e.clientY };
    }
    setIsHolding(true);
    setProgress(0);

    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (50 / HOLD_DURATION) * 100;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 50);

    holdTimeoutRef.current = setTimeout(() => {
      setIsHolding(false);
      setProgress(0);
      clearInterval(progressIntervalRef.current);
      setShowConfirmModal(true);
    }, HOLD_DURATION);
  };

  const cancelHold = () => {
    setIsHolding(false);
    setProgress(0);
    startPositionRef.current = null;
    if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
  };

  const handleMouseMove = (e) => {
    if (isHolding && startPositionRef.current) {
      const dx = Math.abs(e.clientX - startPositionRef.current.x);
      const dy = Math.abs(e.clientY - startPositionRef.current.y);
      if (dx > 20 || dy > 20) cancelHold();
    }
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDelete();
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDelete = () => {
    if (isSubmitting) return;
    setShowConfirmModal(false);
  };

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  return (
    <>
      <button
        data-testid="manage-users-delete-btn"
        onMouseDown={startHold}
        onMouseUp={cancelHold}
        onMouseMove={handleMouseMove}
        onTouchStart={startHold}
        onTouchEnd={cancelHold}
        disabled={disabled || isSubmitting}
        className="relative w-full bg-red-700 text-white px-4 py-3 rounded-lg hover:bg-red-800 font-semibold text-sm overflow-hidden select-none disabled:opacity-50 disabled:cursor-not-allowed"
        title="Hold for 3 seconds to delete this user"
      >
        <div
          className="absolute inset-0 bg-red-900 transition-all duration-75"
          style={{ width: `${progress}%`, opacity: 0.7 }}
        />
        <span className="relative z-10">
          {isSubmitting
            ? 'Deleting…'
            : isHolding
              ? `Hold... ${Math.ceil((100 - progress) / 33)}s`
              : '🗑️ Delete User'}
        </span>
      </button>

      {showConfirmModal && (
        <>
          <div
            className="fixed inset-0 z-[80] bg-black bg-opacity-60"
            onClick={handleCancelDelete}
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div
              data-testid="manage-users-delete-confirm-modal"
              className="bg-gray-800 border-2 border-red-600 rounded-xl shadow-2xl p-6 max-w-md w-full"
            >
              <div className="text-white font-bold mb-2 text-lg">
                🗑️ Delete User
              </div>
              <div className="text-gray-300 text-sm mb-4">
                Permanently delete <span className="text-white font-semibold">{userLabel || 'this user'}</span>?
                Their record will be archived to <code className="bg-gray-900/70 px-1 rounded text-xs">deleted_users_archive</code> for 30-day undo safety, and their notifications + legacy snapshot rows will be swept clean.
              </div>
              <div className="text-xs text-amber-300 mb-4">
                Blocked if the user has active queue entries — remove those first.
              </div>
              <div className="space-y-2">
                <button
                  data-testid="manage-users-delete-confirm-yes"
                  onClick={handleConfirmDelete}
                  disabled={isSubmitting}
                  className="w-full bg-red-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Deleting…' : '✅ Yes, Delete User'}
                </button>
                <button
                  data-testid="manage-users-delete-confirm-cancel"
                  onClick={handleCancelDelete}
                  disabled={isSubmitting}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────
// DiagnosticsButton — opens a modal showing /api/health/version output
// (uptime, git sha, in-memory counter values) with a "Reset Counters"
// action. Live-show safe: no DB writes, no KaraFun WS commands.
//
// See /app/memory/CRITICAL_AGENT_NOTES.md Rule #5 for background on
// counter drift during long uptimes on Render Standard tier.
// ─────────────────────────────────────────────────────────────────────────
const DiagnosticsButton = () => {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Debug log state (Rule #10 — self-hosted log stream)
  const [urlCopyStatus, setUrlCopyStatus] = React.useState(''); // '', 'copied', 'failed'
  const [shareUrl, setShareUrl] = React.useState('');
  const [rotating, setRotating] = React.useState(false);
  // Rotate-token long-press + confirmation (prevents accidental token rotation)
  const [rotatePressProgress, setRotatePressProgress] = React.useState(0); // 0-100
  const [rotateConfirmOpen, setRotateConfirmOpen] = React.useState(false);
  const rotatePressIntervalRef = React.useRef(null);
  const rotatePressStartRef = React.useRef(0);
  const ROTATE_HOLD_MS = 800;
  // Debug-logging master toggle (2026-08-18 A/B test)
  const [debugLoggingEnabled, setDebugLoggingEnabled] = React.useState(null);
  const [debugTogglePending, setDebugTogglePending] = React.useState(false);
  // Database Test state (M0 throttling probe suite)
  const [dbTestOpen, setDbTestOpen] = React.useState(false);
  const [dbTestState, setDbTestState] = React.useState(null); // { status, progress_pct, current_probe, elapsed_sec, result, error }
  const [dbTestStarting, setDbTestStarting] = React.useState(false);
  const [dbTestCopyStatus, setDbTestCopyStatus] = React.useState(''); // '', 'copied', 'failed'
  const dbTestPollRef = React.useRef(null);

  const fetchDiagnostics = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/health/version`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch the share URL when the modal opens
  const fetchShareUrl = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/debug-events?hours=2&limit=1`);
      const token = res.data?.share_token;
      if (token) {
        // Use the BACKEND origin (not window.location.origin — that's
        // the frontend Static Site, which doesn't proxy /api/*).
        setShareUrl(`${BACKEND_URL}/api/public/debug-log/${token}?hours=2`);
      }
    } catch (err) {
      // silent; just don't populate share URL
    }
  }, []);

  const handleCopyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setUrlCopyStatus('copied');
      setTimeout(() => setUrlCopyStatus(''), 2000);
    } catch (err) {
      setUrlCopyStatus('failed');
      setTimeout(() => setUrlCopyStatus(''), 3000);
    }
  };

  const handleRotateToken = async () => {
    setRotateConfirmOpen(false);
    setRotating(true);
    try {
      const res = await axios.post(`${API}/admin/debug-events/rotate-token`, {});
      const token = res.data?.share_token;
      if (token) {
        // Backend origin, not frontend origin (see fetchShareUrl comment).
        setShareUrl(`${BACKEND_URL}/api/public/debug-log/${token}?hours=2`);
      }
    } catch (err) {
      setError('Rotate failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRotating(false);
    }
  };

  const clearRotatePress = () => {
    if (rotatePressIntervalRef.current) {
      clearInterval(rotatePressIntervalRef.current);
      rotatePressIntervalRef.current = null;
    }
    rotatePressStartRef.current = 0;
    setRotatePressProgress(0);
  };

  const handleRotatePressStart = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (rotating || rotateConfirmOpen) return;
    if (rotatePressIntervalRef.current) return; // already pressing
    rotatePressStartRef.current = Date.now();
    rotatePressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - rotatePressStartRef.current;
      const pct = Math.min(100, (elapsed / ROTATE_HOLD_MS) * 100);
      setRotatePressProgress(pct);
      if (elapsed >= ROTATE_HOLD_MS) {
        clearInterval(rotatePressIntervalRef.current);
        rotatePressIntervalRef.current = null;
        rotatePressStartRef.current = 0;
        setRotatePressProgress(0);
        setRotateConfirmOpen(true);
      }
    }, 30);
  };

  const handleRotatePressEnd = () => {
    // If the modal has already been armed, keep it open; just clear any leftover interval.
    clearRotatePress();
  };

  React.useEffect(() => {
    return () => {
      if (rotatePressIntervalRef.current) clearInterval(rotatePressIntervalRef.current);
    };
  }, []);

  const handleOpen = () => {
    setOpen(true);
    fetchDiagnostics();
    fetchShareUrl();
    fetchDebugLoggingState();
  };

  const fetchDebugLoggingState = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/debug-logging`);
      setDebugLoggingEnabled(!!res.data?.enabled);
    } catch (err) {
      // silent — if endpoint isn't deployed yet, hide the row
    }
  }, []);

  const handleToggleDebugLogging = async () => {
    const next = !debugLoggingEnabled;
    const msg = next
      ? 'Turn debug event persistence BACK ON?\n\nNew events will be written to the debug_events collection again.'
      : 'Turn debug event persistence OFF?\n\nThe Python logger (Render) is unaffected — you\'ll still see [DBG …] lines in Render logs. Only the /api/public/debug-log stream will pause.\n\nUse this to A/B-test whether instrumentation writes are contributing to DB throttling on busy nights.';
    if (!window.confirm(msg)) return;
    setDebugTogglePending(true);
    try {
      const res = await axios.post(`${API}/admin/debug-logging`, { enabled: next });
      setDebugLoggingEnabled(!!res.data?.enabled);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Toggle failed');
    } finally {
      setDebugTogglePending(false);
    }
  };

  // In-Memory Counters UI hidden 2026-10 (host decision). The counter-reset
  // handler + classify/rowColor helpers were removed with the UI. Backend
  // endpoints remain intact (GET /api/health/version, POST /api/admin/counters/reset).

  // ─── Database Test polling ───────────────────────────────────────
  const fetchDbTestStatus = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/diagnostics/db-test/status`);
      setDbTestState(res.data);
      if (res.data?.status === 'complete' || res.data?.status === 'error') {
        if (dbTestPollRef.current) {
          clearInterval(dbTestPollRef.current);
          dbTestPollRef.current = null;
        }
      }
      return res.data;
    } catch (err) {
      // silent — polling errors shouldn't spam UI
      return null;
    }
  }, []);

  const handleStartDbTest = async () => {
    if (!window.confirm('Run the Database Test (General)?\n\nThis takes ~2.5 minutes and induces sustained load to force any M0 throttling into the open. Measures raw M0 headroom in isolation.\n\nBest run when you\'re not mid-session.\n\nSafe to run: all writes go to an ephemeral collection that self-cleans.')) {
      return;
    }
    setDbTestStarting(true);
    setDbTestState(null);
    try {
      await axios.post(`${API}/admin/diagnostics/db-test/start`, {});
      await fetchDbTestStatus();
      if (dbTestPollRef.current) clearInterval(dbTestPollRef.current);
      dbTestPollRef.current = setInterval(fetchDbTestStatus, 1500);
    } catch (err) {
      if (err.response?.status === 409) {
        await fetchDbTestStatus();
        if (dbTestPollRef.current) clearInterval(dbTestPollRef.current);
        dbTestPollRef.current = setInterval(fetchDbTestStatus, 1500);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to start database test');
      }
    } finally {
      setDbTestStarting(false);
    }
  };

  const handleStartWorkloadTest = async () => {
    if (!window.confirm('Run Workload Test?\n\nSimulates ~60 seconds of production-like traffic (5 singer-join workers + 3 reconciler workers + 2 SSE-reader workers, all against ephemeral collections with realistic multi-op sequences).\n\nAnswers the real question: how close is your actual busy-night workload to the M0 ceiling?\n\nSafe: all writes go to ephemeral _wl_* collections that self-clean.')) {
      return;
    }
    setDbTestStarting(true);
    setDbTestState(null);
    try {
      await axios.post(`${API}/admin/diagnostics/db-workload-test/start`, {});
      await fetchDbTestStatus();
      if (dbTestPollRef.current) clearInterval(dbTestPollRef.current);
      dbTestPollRef.current = setInterval(fetchDbTestStatus, 1500);
    } catch (err) {
      if (err.response?.status === 409) {
        await fetchDbTestStatus();
        if (dbTestPollRef.current) clearInterval(dbTestPollRef.current);
        dbTestPollRef.current = setInterval(fetchDbTestStatus, 1500);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to start workload test');
      }
    } finally {
      setDbTestStarting(false);
    }
  };

  const handleCopyDbTestReport = async () => {
    if (!dbTestState?.result) return;
    try {
      const report = JSON.stringify(dbTestState.result, null, 2);
      await navigator.clipboard.writeText(report);
      setDbTestCopyStatus('copied');
      setTimeout(() => setDbTestCopyStatus(''), 2000);
    } catch (err) {
      setDbTestCopyStatus('failed');
      setTimeout(() => setDbTestCopyStatus(''), 3000);
    }
  };

  // On open, attach to any in-flight test so re-opening the modal doesn't drop progress.
  React.useEffect(() => {
    if (open && dbTestOpen) {
      fetchDbTestStatus();
    }
  }, [open, dbTestOpen, fetchDbTestStatus]);

  // Cleanup poll interval on unmount / modal close
  React.useEffect(() => {
    return () => {
      if (dbTestPollRef.current) clearInterval(dbTestPollRef.current);
    };
  }, []);
  React.useEffect(() => {
    if (!open && dbTestPollRef.current) {
      clearInterval(dbTestPollRef.current);
      dbTestPollRef.current = null;
    }
  }, [open]);

  // ASCII bar chart renderer for sustained-load windows
  const renderLatencyChart = (windows) => {
    if (!windows || windows.length === 0) return null;
    const maxP95 = Math.max(...windows.map((w) => w.p95_ms || 0), 1);
    const barMax = 30; // chars
    return (
      <div className="font-mono text-xs text-gray-300 bg-gray-900 rounded p-2 overflow-x-auto">
        <div className="text-gray-500 mb-1">Sustained-load p95 latency by 10s window (throttling shows as climb →)</div>
        {windows.map((w) => {
          const barLen = Math.max(1, Math.round((w.p95_ms / maxP95) * barMax));
          const bar = '█'.repeat(barLen);
          const color = w.p95_ms > 500 ? 'text-red-400' : w.p95_ms > 200 ? 'text-yellow-400' : 'text-green-400';
          return (
            <div key={w.window} className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-gray-500 w-8">W{w.window}</span>
              <span className={`${color}`}>{bar}</span>
              <span className="text-gray-400">{w.p95_ms} ms</span>
              <span className="text-gray-600">({w.ops_per_sec}/s)</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <button
        data-testid="diagnostics-btn"
        onClick={handleOpen}
        className="bg-slate-600 text-white px-3 py-2 sm:py-1 rounded hover:bg-slate-700 text-sm font-semibold"
        title="Live diagnostics — uptime + in-memory counter drift check"
      >
        🩺 Diagnostics
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-60"
            onClick={() => setOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border-2 border-gray-600 rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-3">
                <div className="text-white font-bold text-lg">🩺 Live Diagnostics</div>
                <button
                  data-testid="diagnostics-close-btn"
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-white text-xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {loading && (
                <div className="text-gray-400 text-sm">Loading…</div>
              )}

              {error && (
                <div className="text-red-400 text-sm mb-3">{error}</div>
              )}

              {data && !loading && (
                <>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
                    <div className="text-gray-400">Uptime</div>
                    <div className="text-white font-mono" data-testid="diag-uptime">{data.uptime_human} ({data.uptime_seconds}s)</div>
                    <div className="text-gray-400">Git SHA</div>
                    <div className="text-white font-mono">{data.git_sha}</div>
                    <div className="text-gray-400">Started</div>
                    <div className="text-white font-mono text-xs">{new Date(data.server_start_time).toLocaleString()}</div>
                  </div>

                  {/* Debug-log section (Rule #10) */}
                  <div className="pt-3 border-t border-gray-700 space-y-2">
                    <div className="text-white font-semibold text-sm">📋 Debug Log</div>

                    {shareUrl ? (
                      <>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs text-gray-400">
                            Public share URL — paste to an AI agent:
                          </div>
                          <button
                            data-testid="diagnostics-rotate-token-btn"
                            onMouseDown={handleRotatePressStart}
                            onMouseUp={handleRotatePressEnd}
                            onMouseLeave={handleRotatePressEnd}
                            onTouchStart={handleRotatePressStart}
                            onTouchEnd={handleRotatePressEnd}
                            onTouchCancel={handleRotatePressEnd}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={rotating}
                            title="Hold for ~1s to rotate — invalidates the current share URL"
                            className="relative overflow-hidden shrink-0 bg-gray-800 text-gray-300 px-2 py-1 rounded text-xs hover:bg-gray-700 transition-all disabled:opacity-50 select-none"
                          >
                            <span
                              aria-hidden="true"
                              className="absolute inset-y-0 left-0 bg-yellow-600/60 pointer-events-none transition-[width] duration-75"
                              style={{ width: `${rotatePressProgress}%` }}
                            />
                            <span className="relative z-10">
                              {rotating
                                ? 'Rotating…'
                                : rotatePressProgress > 0
                                  ? 'Hold to rotate…'
                                  : '🔄 Rotate token'}
                            </span>
                          </button>
                        </div>
                        <input
                          data-testid="diagnostics-share-url"
                          readOnly
                          value={shareUrl}
                          onFocus={(e) => e.target.select()}
                          className="w-full bg-gray-900 text-gray-200 text-xs px-2 py-1 rounded border border-gray-700 font-mono"
                        />
                        <button
                          data-testid="diagnostics-copy-share-url-btn"
                          onClick={handleCopyShareUrl}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all"
                        >
                          {urlCopyStatus === 'copied' && '✅ URL Copied to Clipboard'}
                          {urlCopyStatus === 'failed' && '❌ Copy Failed'}
                          {urlCopyStatus === '' && '📋 Copy Share URL'}
                        </button>
                        {debugLoggingEnabled !== null && (
                          <button
                            data-testid="diagnostics-toggle-debug-logging-btn"
                            onClick={handleToggleDebugLogging}
                            disabled={debugTogglePending}
                            className={`w-full ${debugLoggingEnabled ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-700 hover:bg-green-800'} text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50`}
                            title={debugLoggingEnabled ? 'Debug events currently being written to Mongo — click to disable (A/B test throttling hypothesis)' : 'Debug events NOT being written — click to re-enable'}
                          >
                            {debugTogglePending
                              ? 'Applying…'
                              : debugLoggingEnabled
                                ? '📉 Turn OFF Debug Event Writes (test)'
                                : '📈 Turn ON Debug Event Writes'}
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="text-xs text-gray-500">Loading share URL…</div>
                    )}
                  </div>

                  <div className="border-t border-gray-600 pt-3 mt-4">
                    {/* In-Memory Counters section hidden 2026-10 (host decision).
                        Rationale: Fix #29 (2026-09-18) and the 2026-10-03 Railway
                        migration systematically closed the race-condition class
                        these counters were built to surface. Zero stuck-counter
                        incidents since. Backend endpoints kept intact:
                          GET  /api/health/version        — counters still in payload
                          POST /api/admin/counters/reset  — still callable via curl
                        If a future regression surfaces, un-hide this section by
                        reinstating the removed JSX + the countersOpen/handleReset
                        state (see git history of this file). */}
                  </div>

                  {/* Database Test — M0 throttling probe suite */}
                  <div className="border-t border-gray-600 pt-3 mt-4">
                    <button
                      data-testid="db-test-toggle"
                      onClick={() => setDbTestOpen((v) => !v)}
                      className="w-full flex items-center justify-between text-white font-semibold text-sm hover:text-gray-300 transition-colors"
                    >
                      <span>🧪 Database Test</span>
                      <span className="text-gray-400 text-xs">{dbTestOpen ? '▼' : '▶'}</span>
                    </button>
                    {dbTestOpen && (
                      <div className="mt-2 space-y-2">
                        <div className="text-xs text-gray-400">
                          <div className="mb-1">
                            <strong className="text-gray-300">General Test</strong> — ~2.5 min. Measures raw M0 headroom in
                            isolation (baseline, sustained load, connection ramp, indexes).
                          </div>
                          <div>
                            <strong className="text-gray-300">Workload Test</strong> — ~60s. Simulates production-like busy-night
                            traffic (5 singer-join + 3 reconciler + 2 SSE-reader workers, realistic multi-op sequences).
                            <em className="text-gray-500"> Answers "how close is my actual workload to the ceiling?"</em>
                          </div>
                          <div className="mt-1 text-gray-500">
                            All writes go to ephemeral collections that self-clean. Safe to run any time you're not mid-session.
                          </div>
                        </div>

                        {(!dbTestState || dbTestState.status === 'idle') && (
                          <div className="space-y-2">
                            <button
                              data-testid="db-test-run-btn"
                              onClick={handleStartDbTest}
                              disabled={dbTestStarting}
                              className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-all disabled:opacity-50"
                              title="Isolated M0 headroom probe — 2.5 min"
                            >
                              {dbTestStarting ? 'Starting…' : '🧪 Run General Test (2.5 min)'}
                            </button>
                            <button
                              data-testid="db-workload-test-run-btn"
                              onClick={handleStartWorkloadTest}
                              disabled={dbTestStarting}
                              className="w-full bg-fuchsia-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-fuchsia-800 transition-all disabled:opacity-50"
                              title="Production-like busy-night workload — 60s"
                            >
                              🎯 Run Workload Test (60s)
                            </button>
                          </div>
                        )}

                        {dbTestState?.status === 'running' && (
                          <div className="bg-gray-900 rounded p-3 space-y-2" data-testid="db-test-running">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-300 font-semibold">{dbTestState.current_probe || 'Working…'}</span>
                              <span className="text-gray-400">{dbTestState.elapsed_sec}s elapsed</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${dbTestState.progress_pct || 0}%` }}
                              />
                            </div>
                            <div className="text-xs text-gray-500 text-right">{dbTestState.progress_pct || 0}%</div>
                          </div>
                        )}

                        {dbTestState?.status === 'error' && (
                          <div className="bg-red-900/40 border border-red-700 rounded p-3 text-sm text-red-300" data-testid="db-test-error">
                            <div className="font-semibold mb-1">❌ Test failed</div>
                            <div className="text-xs font-mono">{dbTestState.error}</div>
                            <button
                              onClick={handleStartDbTest}
                              className="mt-2 bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700"
                            >
                              Retry
                            </button>
                          </div>
                        )}

                        {dbTestState?.status === 'complete' && dbTestState.result && (
                          <div className="space-y-3" data-testid="db-test-result">
                            {/* Mode indicator */}
                            <div className="text-xs text-gray-500 flex justify-between">
                              <span>
                                Mode: <span className="text-gray-300 font-semibold">
                                  {dbTestState.result.mode === 'workload' ? 'Workload' : 'General'}
                                </span>
                              </span>
                              <span>Ran {dbTestState.elapsed_sec}s</span>
                            </div>

                            {/* Verdict banner */}
                            <div
                              className={`rounded p-3 border-2 ${
                                dbTestState.result.verdict.level === 'healthy'
                                  ? 'bg-green-900/40 border-green-600'
                                  : dbTestState.result.verdict.level === 'borderline'
                                    ? 'bg-yellow-900/40 border-yellow-600'
                                    : 'bg-red-900/40 border-red-600'
                              }`}
                              data-testid="db-test-verdict"
                            >
                              <div className="text-white font-bold text-base">
                                {dbTestState.result.verdict.emoji} {dbTestState.result.verdict.level.toUpperCase()}
                                <span className="text-xs font-normal text-gray-300 ml-2">
                                  (score {dbTestState.result.verdict.score}/{dbTestState.result.verdict.max_score})
                                </span>
                              </div>
                              <div className="text-sm text-gray-200 mt-1">{dbTestState.result.verdict.summary}</div>
                            </div>

                            {/* Signal breakdown */}
                            <div className="bg-gray-900 rounded p-2">
                              <div className="text-xs text-gray-500 mb-1">Signal breakdown</div>
                              <table className="w-full text-xs font-mono">
                                <tbody>
                                  {dbTestState.result.verdict.signals.map((sig, i) => (
                                    <tr key={i} className="border-b border-gray-800 last:border-0">
                                      <td className="text-gray-300 py-1 pr-2">{sig.name}</td>
                                      <td className={`py-1 pr-2 text-right ${
                                        sig.score === 0 ? 'text-green-400' : sig.score === 1 ? 'text-yellow-400' : 'text-red-400'
                                      }`}>{sig.value}</td>
                                      <td className={`py-1 text-right ${
                                        sig.score === 0 ? 'text-green-400' : sig.score === 1 ? 'text-yellow-400' : 'text-red-400'
                                      }`}>{['🟢','🟡','🔴'][sig.score]}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Latency charts — general mode: single sustained_load chart */}
                            {dbTestState.result.mode !== 'workload' &&
                              renderLatencyChart(dbTestState.result.sustained_load?.windows)}

                            {/* Latency charts — workload mode: one per worker type */}
                            {dbTestState.result.mode === 'workload' && (
                              <>
                                <div className="text-xs text-gray-500">
                                  Per-worker latency by 10s window
                                  {dbTestState.result.workload?.debug_events_written != null && (
                                    <span className="ml-2 text-gray-600">
                                      · {dbTestState.result.workload.debug_events_written} debug events written
                                    </span>
                                  )}
                                </div>
                                {['singer_join', 'reconciler', 'sse_reader'].map((k) => {
                                  const s = dbTestState.result.workload?.[k];
                                  if (!s) return null;
                                  return (
                                    <div key={k}>
                                      <div className="text-xs text-gray-400 font-semibold mb-1">
                                        {k.replace('_', ' ')} — {s.total_ops} ops, {s.overall_ops_per_sec}/s overall,
                                        p95 {s.overall_p95_ms}ms
                                      </div>
                                      {renderLatencyChart(s.windows)}
                                    </div>
                                  );
                                })}
                              </>
                            )}

                            {/* Detailed metrics — collapsible */}
                            <details className="bg-gray-900 rounded p-2">
                              <summary className="text-xs text-gray-400 cursor-pointer">Full metrics (JSON)</summary>
                              <pre className="text-xs text-gray-300 mt-2 overflow-x-auto font-mono max-h-64 overflow-y-auto">
{JSON.stringify(dbTestState.result, null, 2)}
                              </pre>
                            </details>

                            <div className="flex gap-2">
                              <button
                                data-testid="db-test-copy-btn"
                                onClick={handleCopyDbTestReport}
                                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-blue-700"
                              >
                                {dbTestCopyStatus === 'copied' && '✅ Copied'}
                                {dbTestCopyStatus === 'failed' && '❌ Copy failed'}
                                {dbTestCopyStatus === '' && '📋 Copy Full Report'}
                              </button>
                              <button
                                data-testid="db-test-back-btn"
                                onClick={() => setDbTestState(null)}
                                className="flex-1 bg-gray-700 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-gray-600"
                                title="Return to test menu"
                              >
                                ← Back to menu
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 mt-3">
                    <button
                      onClick={fetchDiagnostics}
                      disabled={loading}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-600 transition-all disabled:opacity-50"
                    >
                      🔄 Refresh
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {rotateConfirmOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black bg-opacity-70"
            onClick={() => setRotateConfirmOpen(false)}
          />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div
              data-testid="diagnostics-rotate-token-confirm-modal"
              className="bg-gray-800 border-2 border-yellow-600 rounded-xl shadow-2xl p-6 max-w-md w-full"
            >
              <div className="text-white font-bold text-lg mb-2">🔄 Rotate share token?</div>
              <div className="text-sm text-gray-300 mb-4">
                This will <strong className="text-yellow-400">invalidate the current share URL</strong>.
                Anyone still using the old URL will get a 403. A fresh URL will replace it here.
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="diagnostics-rotate-token-cancel-btn"
                  onClick={() => setRotateConfirmOpen(false)}
                  className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-600 transition-all"
                >
                  Cancel
                </button>
                <button
                  data-testid="diagnostics-rotate-token-confirm-btn"
                  onClick={handleRotateToken}
                  disabled={rotating}
                  className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-700 transition-all disabled:opacity-50"
                >
                  {rotating ? 'Rotating…' : 'Rotate token'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};


// ═════════════════════════════════════════════════════════════════════
// DBToolsButton — unified Database Tools entry point.
// Single admin button that opens a modal exposing both DB Import
// (Compass JSON folder OR .zip backup) and DB Export (streaming zip
// of every non-system collection). Replaces the two separate
// DBImportButton + DBExportButton top-level buttons.
// Built 2026-10-20.
// ═════════════════════════════════════════════════════════════════════
const DBToolsButton = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('menu'); // 'menu' | 'import'
  // Import-side state
  const [files, setFiles] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [sourceKind, setSourceKind] = useState(null); // 'folder' | 'zip' | null
  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);
  // Export-side state
  const [downloading, setDownloading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportInfo, setExportInfo] = useState(''); // success message after a download

  const handleFolderSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    // Filter to .json only for the preview list
    const jsonFiles = selected.filter((f) => (f.name || '').endsWith('.json') && !(f.name || '').endsWith('.metadata.json'));
    setFiles(jsonFiles);
    setSourceKind(jsonFiles.length > 0 ? 'folder' : null);
    setResult(null);
    setError('');
    // Clear the other picker so switching sources is unambiguous.
    if (zipInputRef.current) zipInputRef.current.value = '';
  };

  const handleZipSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const zipFiles = selected.filter((f) => (f.name || '').toLowerCase().endsWith('.zip'));
    setFiles(zipFiles);
    setSourceKind(zipFiles.length > 0 ? 'zip' : null);
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleReset = () => {
    setFiles([]);
    setConfirmText('');
    setResult(null);
    setError('');
    setSourceKind(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (zipInputRef.current) zipInputRef.current.value = '';
  };

  const handleClose = () => {
    setOpen(false);
    handleReset();
    setUploading(false);
    setProgress('');
    setView('menu');
    setExportError('');
    setExportInfo('');
  };

  // One-click DB export — streams a timestamped zip from the server
  // via fetch → Blob → transient <a download>. Kept here (rather than
  // its own component) so the status surfaces inside the shared modal.
  const handleExport = async () => {
    if (downloading) return;
    setExportError('');
    setExportInfo('');
    setDownloading(true);
    try {
      const resp = await fetch(`${API}/admin/db-export/download`, { method: 'GET' });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const errBody = await resp.json();
          detail = errBody?.detail || detail;
        } catch (_) { /* body wasn't JSON */ }
        throw new Error(detail);
      }
      const collCount = resp.headers.get('X-DB-Export-Collections') || '?';
      const docCount = resp.headers.get('X-DB-Export-Docs') || '?';
      const blob = await resp.blob();
      let filename = '';
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      if (!filename) {
        const now = new Date();
        const stamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19) + 'Z';
        filename = `karaoke-db-backup-${stamp}.zip`;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
      setExportInfo(`Downloaded ${filename} (${collCount} collections, ${docCount} docs, ${sizeMB} MB)`);
    } catch (err) {
      setExportError(err?.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (confirmText.trim() !== 'REPLACE') return;

    setUploading(true);
    setError('');
    setResult(null);
    setProgress('Uploading files…');

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f, f.name));

      const response = await axios.post(
        `${API}/admin/db-import/upload-batch`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(`Uploading: ${pct}%`);
            }
          },
        }
      );

      setProgress('Processing on server…');
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  const canConfirm = files.length > 0 && confirmText.trim() === 'REPLACE' && !uploading;

  return (
    <>
      <button
        data-testid="db-tools-btn"
        onClick={() => setOpen(true)}
        className="bg-fuchsia-700 text-white px-3 py-2 sm:py-1 rounded hover:bg-fuchsia-800 text-sm font-semibold"
        title="Database tools — import a backup or export the current DB"
      >
        🛠️ DB Tools
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-70"
            onClick={handleClose}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div
              className="bg-gray-800 border-2 border-fuchsia-500 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col pointer-events-auto"
              data-testid="db-tools-modal"
            >
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <h3 className="text-white font-bold text-lg">
                  {view === 'import'
                    ? <>📥 DB Import — Compass JSON <span className="text-gray-500 font-normal">(folder or .zip)</span></>
                    : <>🛠️ Database Tools</>
                  }
                </h3>
                <button
                  data-testid="db-tools-close-btn"
                  onClick={handleClose}
                  disabled={uploading || downloading}
                  className="text-gray-400 hover:text-white text-xl leading-none disabled:opacity-50"
                >×</button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {view === 'menu' && (
                  <div className="space-y-5">
                    <div className="text-gray-300 text-sm">
                      Pick a tool. Imports fully <span className="text-red-300 font-semibold">replace</span> the current database; exports produce a timestamped zip backup you keep on your computer.
                    </div>

                    {/* Import tile */}
                    <button
                      type="button"
                      data-testid="db-tools-choose-import"
                      onClick={() => {
                        setView('import');
                        setExportError('');
                        setExportInfo('');
                      }}
                      className="w-full text-left bg-gray-900/70 hover:bg-gray-900 border border-fuchsia-700 hover:border-fuchsia-500 rounded-lg p-4 transition"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl leading-none">📥</div>
                        <div className="flex-1">
                          <div className="text-white font-semibold">DB Import</div>
                          <div className="text-xs text-gray-400 mt-1">
                            Restore from a Compass JSON folder <em>or</em> a <code className="text-fuchsia-300">.zip</code> backup produced by DB Export. <span className="text-red-300">Wipes the entire database first</span> — type REPLACE to confirm on the next screen.
                          </div>
                        </div>
                        <div className="text-gray-500">›</div>
                      </div>
                    </button>

                    {/* Export tile */}
                    <button
                      type="button"
                      data-testid="db-tools-choose-export"
                      onClick={handleExport}
                      disabled={downloading}
                      className="w-full text-left bg-gray-900/70 hover:bg-gray-900 border border-emerald-700 hover:border-emerald-500 rounded-lg p-4 transition disabled:opacity-60 disabled:cursor-wait"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl leading-none">📤</div>
                        <div className="flex-1">
                          <div className="text-white font-semibold">
                            DB Export {downloading && <span className="text-emerald-300 text-xs font-normal ml-2">preparing…</span>}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Download the entire database as a timestamped <code className="text-emerald-300">.zip</code>. Non-destructive — safe to run any time. Round-trips through DB Import without extraction.
                          </div>
                        </div>
                        <div className="text-gray-500">⬇</div>
                      </div>
                    </button>

                    {exportError && (
                      <div
                        data-testid="db-tools-export-error"
                        className="text-red-400 text-sm bg-red-900/30 border border-red-700 rounded p-2"
                        role="alert"
                      >
                        Export failed: {exportError}
                      </div>
                    )}
                    {exportInfo && (
                      <div
                        data-testid="db-tools-export-info"
                        className="text-emerald-300 text-sm bg-emerald-900/30 border border-emerald-700 rounded p-2"
                      >
                        ✅ {exportInfo}
                      </div>
                    )}
                  </div>
                )}

                {view === 'import' && (
                <div className="space-y-4">
                <div className="bg-red-900/40 border border-red-500 rounded p-3 text-sm">
                  <div className="text-red-100 font-semibold mb-1">⚠️ Wipes entire database first</div>
                  <div className="text-red-200 text-xs">
                    Before import, <span className="font-semibold">every non-system collection is dropped</span> — including any collection not in the uploaded folder. Only the files you upload will exist afterward. Use only for full-DB restores from a Compass JSON export.
                  </div>
                </div>

                {!result && (
                  <>
                    {/* Folder picker */}
                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        1. Pick the exported folder <span className="text-gray-500 font-normal">— or a .zip backup below</span>
                      </label>
                      <div className="text-xs text-gray-400 mb-2">
                        Select the folder containing your Compass JSON exports (one <code className="text-fuchsia-300">.json</code> file per collection). Files that don't end in <code className="text-fuchsia-300">.json</code> are skipped.
                      </div>
                      <input
                        ref={fileInputRef}
                        data-testid="db-import-folder-input"
                        type="file"
                        // The webkitdirectory attributes must be lowercase in
                        // JSX to work in Chrome/Edge/Safari. React strips
                        // "webkitdirectory" if capitalized.
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={handleFolderSelect}
                        disabled={uploading}
                        className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-fuchsia-600 file:text-white file:font-semibold hover:file:bg-fuchsia-500"
                      />
                    </div>

                    {/* Zip picker — alternative to folder picker. Mutually
                        exclusive via state so the admin can't accidentally
                        mix a folder + zip in one submission. */}
                    <div className="pt-2 border-t border-gray-700">
                      <label className="block text-sm font-semibold text-white mb-2">
                        <span className="text-gray-500 font-normal">— or —</span> Pick a <code className="text-fuchsia-300 font-mono">.zip</code> backup file
                      </label>
                      <div className="text-xs text-gray-400 mb-2">
                        Direct round-trip of the zip produced by <span className="text-fuchsia-300 font-mono">📤 DB Export</span>. The server unpacks it automatically — no manual extraction needed.
                      </div>
                      <input
                        ref={zipInputRef}
                        data-testid="db-import-zip-input"
                        type="file"
                        accept=".zip,application/zip"
                        onChange={handleZipSelect}
                        disabled={uploading}
                        className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-fuchsia-600 file:text-white file:font-semibold hover:file:bg-fuchsia-500"
                      />
                    </div>

                    {/* File preview */}
                    {files.length > 0 && (
                      <div>
                        <div className="text-sm font-semibold text-white mb-1">
                          2. {sourceKind === 'zip' ? 'Zip to unpack & import' : `Files to import (${files.length})`}
                        </div>
                        <div className="text-xs text-gray-400 mb-2">
                          {sourceKind === 'zip'
                            ? <>Server will unpack and import every <code>.json</code> entry inside.</>
                            : <>Filename (minus <code>.json</code>) = target collection name.</>
                          }
                        </div>
                        <div className="max-h-52 overflow-y-auto bg-gray-900/70 rounded p-2 space-y-0.5" data-testid="db-import-file-list">
                          {files.map((f, i) => {
                            const base = (f.name || '').split('/').pop().split('\\').pop();
                            const isZip = (base || '').toLowerCase().endsWith('.zip');
                            const label = isZip ? base : base.replace(/\.json$/, '');
                            const sizeKB = (f.size / 1024).toFixed(1);
                            return (
                              <div
                                key={`${f.name}-${i}`}
                                data-testid={`db-import-file-${i}`}
                                className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-gray-800"
                              >
                                <span className="text-gray-200 font-mono truncate">
                                  {isZip ? (
                                    <>
                                      <span className="text-fuchsia-400">📦 {label}</span>
                                      <span className="text-gray-500"> (server-side unpack)</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-fuchsia-400">{label}</span>
                                      <span className="text-gray-500"> ← {base}</span>
                                    </>
                                  )}
                                </span>
                                <span className="text-gray-500 flex-shrink-0 ml-2">{sizeKB} KB</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Type-to-confirm */}
                    {files.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-white mb-1">
                          3. Type <span className="text-fuchsia-300 font-mono">REPLACE</span> to confirm
                        </label>
                        <input
                          data-testid="db-import-confirm-input"
                          type="text"
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="REPLACE"
                          disabled={uploading}
                          className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-600 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500 font-mono"
                        />
                      </div>
                    )}
                  </>
                )}

                {progress && (
                  <div className="text-fuchsia-300 text-sm">{progress}</div>
                )}
                {error && (
                  <div className="text-red-400 text-sm">{error}</div>
                )}

                {/* Result */}
                {result && (
                  <div data-testid="db-import-result">
                    <div className="text-white font-semibold text-lg mb-2">Import Complete</div>
                    <div className="bg-gray-900/70 rounded p-3 text-sm space-y-1">
                      <div className="text-gray-200">
                        <span className="text-red-400 font-semibold">🧹 Wiped:</span> {result.summary?.wiped_count || 0} collection{result.summary?.wiped_count === 1 ? '' : 's'}
                      </div>
                      <div className="text-gray-200">
                        <span className="text-green-400 font-semibold">✅ Imported:</span> {result.summary?.imported || 0} collection{result.summary?.imported === 1 ? '' : 's'}
                      </div>
                      {(result.summary?.skipped || 0) > 0 && (
                        <div className="text-gray-400 text-xs">
                          ↷ Skipped: {result.summary.skipped}
                        </div>
                      )}
                      {(result.summary?.failed || 0) > 0 && (
                        <div className="text-red-400 text-xs">
                          ✗ Failed: {result.summary.failed}
                        </div>
                      )}
                      <div className="text-gray-500 text-xs">
                        Total time: {((result.summary?.total_duration_ms || 0) / 1000).toFixed(2)}s
                      </div>
                    </div>
                    <div className="mt-3 max-h-64 overflow-y-auto bg-gray-900/50 rounded p-2 text-xs space-y-0.5">
                      {(result.results || []).map((r, i) => {
                        const status = r.ok ? '✅' : r.skipped ? '↷' : '✗';
                        const color = r.ok ? 'text-green-400' : r.skipped ? 'text-gray-500' : 'text-red-400';
                        return (
                          <div key={i} className={`font-mono ${color}`}>
                            {status} <span className="text-gray-200">{r.collection || r.filename}</span>
                            {r.ok && <span className="text-gray-500"> — {r.inserted} docs, {r.duration_ms}ms</span>}
                            {r.skipped && <span className="text-gray-600"> — {r.reason}</span>}
                            {r.ok === false && <span className="text-red-500"> — {r.error}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-700 flex gap-3">
                {view === 'menu' && (
                  <button
                    type="button"
                    data-testid="db-tools-menu-close-btn"
                    onClick={handleClose}
                    disabled={downloading}
                    className="flex-1 px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold disabled:opacity-50"
                  >
                    Close
                  </button>
                )}
                {view === 'import' && !result && (
                  <>
                    <button
                      type="button"
                      data-testid="db-import-back-btn"
                      onClick={() => {
                        handleReset();
                        setView('menu');
                      }}
                      disabled={uploading}
                      className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold disabled:opacity-50"
                      title="Back to Database Tools menu"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={uploading}
                      className="flex-1 px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="db-import-submit-btn"
                      onClick={handleUpload}
                      disabled={!canConfirm}
                      className="flex-1 px-4 py-2 rounded bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {uploading ? 'Importing…' : (sourceKind === 'zip' ? 'Import zip backup' : `Import ${files.length || 0} file${files.length === 1 ? '' : 's'}`)}
                    </button>
                  </>
                )}
                {view === 'import' && result && (
                  <>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="flex-1 px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold"
                    >
                      Import More
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleReset();
                        setView('menu');
                      }}
                      className="flex-1 px-4 py-2 rounded bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold"
                    >
                      Back to Menu
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};


// ═════════════════════════════════════════════════════════════════════
// DBRecoverPrefixedButton — one-time recovery for the Compass import bug
// where `<db>.<collection>.json` filenames created `karaoke.users`-style
// duplicate collections instead of overwriting `users`.
// Hidden by default (flip SHOW_DB_RECOVER_PREFIXED to true when needed).
// ═════════════════════════════════════════════════════════════════════
const DBRecoverPrefixedButton = () => {
  const [open, setOpen] = useState(false);
  const [prefix, setPrefix] = useState('karaoke');
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleClose = () => {
    setOpen(false);
    setConfirmText('');
    setResult(null);
    setError('');
  };

  const handleRun = async () => {
    if (!prefix.trim() || confirmText.trim() !== 'RECOVER') return;
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/db-import/recover-prefixed`,
        null,
        { params: { prefix: prefix.trim() } }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Recovery failed');
    } finally {
      setRunning(false);
    }
  };

  const canConfirm = prefix.trim() && confirmText.trim() === 'RECOVER' && !running;

  return (
    <>
      <button
        data-testid="db-recover-prefixed-btn"
        onClick={() => setOpen(true)}
        className="bg-amber-700 text-white px-3 py-2 sm:py-1 rounded hover:bg-amber-800 text-sm font-semibold"
        title="Recover prefixed collections from a botched Compass import"
      >
        🔧 Recover Prefixed
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-70" onClick={handleClose} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div
              className="bg-gray-800 border-2 border-amber-500 rounded-xl shadow-2xl w-full max-w-lg pointer-events-auto"
              data-testid="db-recover-modal"
            >
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <h3 className="text-white font-bold text-lg">🔧 Recover Prefixed Collections</h3>
                <button
                  onClick={handleClose}
                  disabled={running}
                  className="text-gray-400 hover:text-white text-xl leading-none disabled:opacity-50"
                >×</button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-amber-900/40 border border-amber-500 rounded p-3 text-sm">
                  <div className="text-amber-100 font-semibold mb-1">One-time recovery</div>
                  <div className="text-amber-200 text-xs">
                    Renames every collection matching <code className="text-amber-300">&lt;prefix&gt;.&lt;coll&gt;</code> to <code className="text-amber-300">&lt;coll&gt;</code>, dropping the target if it exists.
                    Use this only if a prior Compass import created dot-prefixed duplicates.
                  </div>
                </div>
                {!result && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-white mb-1">
                        Prefix to strip
                      </label>
                      <input
                        data-testid="db-recover-prefix-input"
                        type="text"
                        value={prefix}
                        onChange={(e) => setPrefix(e.target.value)}
                        placeholder="karaoke"
                        disabled={running}
                        className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-600 focus:border-amber-500 font-mono"
                      />
                      <div className="text-xs text-gray-400 mt-1">
                        e.g. entering <code>karaoke</code> renames <code>karaoke.users</code> → <code>users</code>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-white mb-1">
                        Type <span className="text-amber-300 font-mono">RECOVER</span> to confirm
                      </label>
                      <input
                        data-testid="db-recover-confirm-input"
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="RECOVER"
                        disabled={running}
                        className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-600 focus:border-amber-500 font-mono"
                      />
                    </div>
                  </>
                )}
                {error && <div className="text-red-400 text-sm">{error}</div>}
                {result && (
                  <div data-testid="db-recover-result">
                    <div className="text-white font-semibold text-lg mb-2">
                      Recovery Complete — renamed {result.renamed || 0}
                    </div>
                    <div className="max-h-64 overflow-y-auto bg-gray-900/50 rounded p-2 text-xs space-y-0.5">
                      {(result.results || []).map((r, i) => {
                        const status = r.ok ? '✅' : r.skipped ? '↷' : '✗';
                        const color = r.ok ? 'text-green-400' : r.skipped ? 'text-gray-500' : 'text-red-400';
                        return (
                          <div key={i} className={`font-mono ${color}`}>
                            {status} <span className="text-gray-200">{r.source}</span> → <span className="text-amber-300">{r.target}</span>
                            {r.skipped && <span className="text-gray-600"> — {r.reason}</span>}
                            {r.ok === false && <span className="text-red-500"> — {r.error}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-gray-700 flex gap-3">
                {!result ? (
                  <>
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={running}
                      className="flex-1 px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="db-recover-submit-btn"
                      onClick={handleRun}
                      disabled={!canConfirm}
                      className="flex-1 px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {running ? 'Recovering…' : 'Run Recovery'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white font-semibold"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};



// ═════════════════════════════════════════════════════════════════════
// ManageUsersButton — admin panel for viewing / editing persistent user
// identities (passkey users + admin-managed regulars).
// Ships 2026-09-25. First-cut feature set:
//   • Set an admin_name (admin-only label; real name for cross-referencing)
//   • View historical aliases (populated going forward by /queue/join hook)
//   • Delete individual songs from a user's song_history (history-only,
//     never touches queue_entries)
// Future roadmap: merge identity, bulk operations.
// ═════════════════════════════════════════════════════════════════════
const ManageUsersButton = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [adminNameDraft, setAdminNameDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [histTarget, setHistTarget] = useState(null); // pending delete-confirm target
  const [histDeleting, setHistDeleting] = useState(false);
  const [aliasTarget, setAliasTarget] = useState(null); // pending alias delete-confirm target 2026-09-22
  const [aliasDeleting, setAliasDeleting] = useState(false);

  // Merge Identity state (Railway migration, 2026-09-19)
  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [legacyUsers, setLegacyUsers] = useState([]);
  const [loadingLegacy, setLoadingLegacy] = useState(false);
  const [legacySearch, setLegacySearch] = useState('');
  const [mergeCandidate, setMergeCandidate] = useState(null); // selected legacy user for confirm modal
  const [merging, setMerging] = useState(false);
  const legacySearchTimerRef = useRef(null);

  // Legacy users dropdown expand state 2026-10-04. Collapses legacy users
  // into an expandable panel at the bottom of the main list (styled like
  // the Deleted Entries section on the queue). Auto-expands on search when
  // there are legacy matches so admin sees them immediately.
  const [legacyExpanded, setLegacyExpanded] = useState(false);

  // Merge picker's own legacy-expanded state 2026-10-07. Kept independent
  // from the main view's `legacyExpanded` so toggling in one place doesn't
  // affect the other.
  const [mergeLegacyExpanded, setMergeLegacyExpanded] = useState(false);

  // 2026-10-11 — One-timer auto-cleanup admin toggle (lives at the top
  // of the Manage Users list). Mirrors the auto-advance / auto-insert
  // pattern: GET on dialog open, POST on toggle. State lives in
  // `app_settings.one_timer_cleanup_enabled` (default True). See
  // `_run_auto_cleanup_one_timer_inactive_passkey_users()` in server.py
  // for the gated cleanup semantics (archives passkey users with
  // exactly 1 song unplayed >14 days at 6 AM Pacific).
  const [oneTimerCleanupEnabled, setOneTimerCleanupEnabled] = useState(true);
  const [oneTimerCleanupLoading, setOneTimerCleanupLoading] = useState(false);

  // 2026-10-14 — Recently-removed users modal. Shows cumulative list
  // of users archived by the daily one-timer sweep (across all
  // sweeps, not just the most recent). Pulled from
  // GET /api/admin/auto-cleanup/recently-removed. View-only; the
  // per-user "spare from cleanup" toggle lives in the per-user menu.
  const [recentlyRemovedModalOpen, setRecentlyRemovedModalOpen] = useState(false);
  const [recentlyRemovedData, setRecentlyRemovedData] = useState(null);
  const [recentlyRemovedLoading, setRecentlyRemovedLoading] = useState(false);
  const [recentlyRemovedError, setRecentlyRemovedError] = useState(null);
  // Client-side search for the archive modal. Filters the already-loaded
  // `entries` by display name, past aliases (name_history), and user_id
  // substring. Case-insensitive. Added 2026-11-03.
  const [recentlyRemovedSearch, setRecentlyRemovedSearch] = useState('');

  // Archive Lifecycle Project Phase 1 (2026-10-16) — batch-merge orphan-pair
  // dry-run preview + confirm execution. Lives inside the Recently Removed
  // modal. "Scan" fires GET /admin/archive/orphan-pairs; preview renders
  // the pairs; "Confirm Batch Merge" fires POST /admin/archive/batch-merge.
  const [orphanPairsData, setOrphanPairsData] = useState(null); // {count, pairs} | null
  const [orphanPairsLoading, setOrphanPairsLoading] = useState(false);
  const [orphanPairsError, setOrphanPairsError] = useState(null);
  const [batchMergeRunning, setBatchMergeRunning] = useState(false);
  const [batchMergeResult, setBatchMergeResult] = useState(null); // {merged, failed, scanned, failures} | null

  // Archive Lifecycle Project Phase 1 (2026-10-16) — individual smart-restore
  // confirmation prompt. When a restore triggers an auto-merge (archive doc's
  // fingerprint collides with a live user), admin sees a confirmation modal
  // naming the merge target before the restore button fires the write.
  const [smartRestoreConfirm, setSmartRestoreConfirm] = useState(null);
  // Shape: { userId, userName, archiveFpPrefix, liveUserId, liveUserName } | null

  // 2026-10-14 — Per-user auto_cleanup_exempt toggle state inside
  // the user detail panel. Loading flag so rapid toggles don't race.
  const [cleanupExemptLoading, setCleanupExemptLoading] = useState(false);

  // Preserve list scroll position across row-click → detail → Back navigation
  // 2026-10-04. Without this, Back always snaps the list to the top which
  // forces admin to re-scroll on every edit. Captured on row-click, restored
  // explicitly after Back/Delete via requestAnimationFrame once the refetch
  // has resolved (useLayoutEffect-based approach was race-prone with the
  // async fetch; see git log). Cleared on cohort switch and modal reopen.
  const listScrollRef = useRef(null);
  const savedListScrollTopRef = useRef(0);

  // Debounced search
  const searchTimerRef = useRef(null);

  const fetchUsers = React.useCallback(async (q = '') => {
    setLoadingUsers(true);
    setError('');
    try {
      // Fetches the "new" cohort (users with created_at >= Railway migration
      // cutoff). Legacy users are fetched separately via fetchLegacyUsers()
      // and rendered in a collapsible dropdown at the bottom of the main
      // list 2026-10-04.
      const res = await axios.get(
        `${API}/admin/users?q=${encodeURIComponent(q)}&limit=1000&cohort=new`
      );
      setUsers(res.data?.results || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchLegacyUsers = React.useCallback(async (q = '') => {
    setLoadingLegacy(true);
    try {
      const res = await axios.get(
        `${API}/admin/users?q=${encodeURIComponent(q)}&limit=1000&cohort=legacy`
      );
      setLegacyUsers(res.data?.results || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load legacy users');
    } finally {
      setLoadingLegacy(false);
    }
  }, []);

  const fetchDetail = React.useCallback(async (userId) => {
    setLoadingDetail(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(res.data);
      setAdminNameDraft(res.data?.admin_name || '');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load user');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setView('list');
    setSelectedUser(null);
    setSearch('');
    setLegacyExpanded(false);
    // Fresh modal entry — don't carry a stale offset from a previous session.
    savedListScrollTopRef.current = 0;
    // Parallel fetch both cohorts so the legacy dropdown is populated 2026-10-04.
    Promise.all([fetchUsers(''), fetchLegacyUsers('')]);
    // Fetch one-timer cleanup toggle state 2026-10-11.
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/auto-cleanup/one-timer`);
        setOneTimerCleanupEnabled(!!r.data?.enabled);
      } catch (_e) {
        // Non-fatal — leave default. Toggle button will still work.
      }
    })();
  };

  // 2026-10-11 — Toggle handler for the daily one-timer auto-cleanup.
  // Mirrors handleToggleAutoInsert pattern: optimistic UI, revert on
  // error. Hits POST /admin/auto-cleanup/one-timer.
  const handleToggleOneTimerCleanup = async () => {
    if (oneTimerCleanupLoading) return;
    const next = !oneTimerCleanupEnabled;
    setOneTimerCleanupLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/auto-cleanup/one-timer`,
        { enabled: next }
      );
      setOneTimerCleanupEnabled(!!r.data?.enabled);
    } catch (e) {
      setError('Failed to update one-timer cleanup toggle. Try again.');
    } finally {
      setOneTimerCleanupLoading(false);
    }
  };

  // 2026-10-14 — Open the recently-removed users modal. Fetches
  // GET /api/admin/auto-cleanup/recently-removed on open; shows
  // all users archived by the sweep with deleted_reason set to
  // "auto_cleanup_inactive_one_timer".
  const handleOpenRecentlyRemoved = async () => {
    setRecentlyRemovedModalOpen(true);
    setRecentlyRemovedLoading(true);
    setRecentlyRemovedError(null);
    // Archive Lifecycle Project Phase 1 (2026-10-16) — fire orphan-pair
    // scan alongside the main recently-removed list so each row knows
    // whether a restore would trigger auto-merge.
    setOrphanPairsLoading(true);
    setOrphanPairsError(null);
    setBatchMergeResult(null);
    try {
      const [recentResp, pairsResp] = await Promise.allSettled([
        axios.get(`${API}/admin/auto-cleanup/recently-removed`),
        axios.get(`${API}/admin/archive/orphan-pairs`),
      ]);
      if (recentResp.status === 'fulfilled') {
        setRecentlyRemovedData(recentResp.value.data || { total: 0, entries: [] });
      } else {
        setRecentlyRemovedError(
          recentResp.reason?.response?.data?.detail
          || 'Failed to load recently-removed users.'
        );
      }
      if (pairsResp.status === 'fulfilled') {
        setOrphanPairsData(pairsResp.value.data || { count: 0, pairs: [] });
      } else {
        setOrphanPairsError(
          pairsResp.reason?.response?.data?.detail
          || 'Failed to scan for orphan pairs.'
        );
      }
    } finally {
      setRecentlyRemovedLoading(false);
      setOrphanPairsLoading(false);
    }
  };

  const handleCloseRecentlyRemoved = () => {
    setRecentlyRemovedModalOpen(false);
    setRecentlyRemovedData(null);
    setRecentlyRemovedError(null);
    setRecentlyRemovedSearch('');
    // Archive Lifecycle Project Phase 1 (2026-10-16) — reset batch-merge state.
    setOrphanPairsData(null);
    setOrphanPairsError(null);
    setBatchMergeResult(null);
    setSmartRestoreConfirm(null);
  };

  // 2026-10-14 — Per-row restore handler for the Recently Removed
  // modal. POSTs to /admin/users/archive/{user_id}/restore. On
  // success, removes the row from the modal list and shows a toast.
  // On 409 (collision), surfaces the backend's descriptive message
  // inline. Keyed by user_id in a Set so we can disable just the
  // row being restored.
  const [restoringUserIds, setRestoringUserIds] = useState(new Set());
  const [restoreRowError, setRestoreRowError] = useState({}); // {user_id: msg}

  const handleRestoreArchivedUser = async (userId, userName) => {
    if (restoringUserIds.has(userId)) return;
    setRestoringUserIds((prev) => {
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
    setRestoreRowError((prev) => {
      const { [userId]: _removed, ...rest } = prev;
      return rest;
    });
    try {
      const resp = await axios.post(`${API}/admin/users/archive/${userId}/restore`);
      const wasMerged = Boolean(resp?.data?.merged);
      const liveUserName = resp?.data?.name || resp?.data?.user_name || userName;
      // Remove the row optimistically. The row disappearing IS the
      // success feedback — no toast needed since ManageUsersPanel
      // doesn't have a local success state and the modal stays open
      // for further restores.
      setRecentlyRemovedData((prev) => {
        if (!prev) return prev;
        const nextEntries = (prev.entries || []).filter((r) => r.user_id !== userId);
        return { ...prev, entries: nextEntries, total: nextEntries.length };
      });
      // Also remove from any current orphan-pairs preview since this
      // row is no longer collision-ineligible.
      setOrphanPairsData((prev) => {
        if (!prev) return prev;
        const nextPairs = (prev.pairs || []).filter((p) => p.archive_id !== userId);
        return { ...prev, pairs: nextPairs, count: nextPairs.length };
      });
      // Refetch main user list so the restored user appears again.
      try { await fetchUsers(); } catch {}
      // Merge-outcome toast via inline row-info surface (no error state).
      if (wasMerged) {
        setRestoreRowError((prev) => ({
          ...prev,
          [userId]: `✓ Restored and auto-merged into ${liveUserName}.`,
        }));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to restore user.';
      setRestoreRowError((prev) => ({ ...prev, [userId]: msg }));
    } finally {
      setRestoringUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  // Archive Lifecycle Project Phase 1 (2026-10-16) — click handler that
  // interposes a confirmation modal when restore would trigger auto-merge.
  // Determines merge intent by checking orphanPairsData (loaded alongside
  // the Recently Removed list). If collision → confirm modal; else direct.
  const handleRestoreButtonClick = (row) => {
    const pair = (orphanPairsData?.pairs || []).find((p) => p.archive_id === row.user_id);
    if (pair) {
      // Collision — show the confirm modal with the live-user target.
      setSmartRestoreConfirm({
        userId: row.user_id,
        userName: row.user_name,
        archiveFpPrefix: pair.archive_fp_prefix,
        liveUserId: pair.live_id,
        liveUserName: pair.live_name,
      });
      return;
    }
    // No collision — direct restore.
    handleRestoreArchivedUser(row.user_id, row.user_name);
  };

  // Archive Lifecycle Project Phase 1 (2026-10-16) — "Scan for Orphan Pairs"
  // button handler. Fetches dry-run preview. The scan auto-fires when the
  // modal opens, but admin can refresh explicitly via this button too.
  const handleScanOrphanPairs = async () => {
    setOrphanPairsLoading(true);
    setOrphanPairsError(null);
    setBatchMergeResult(null);
    try {
      const r = await axios.get(`${API}/admin/archive/orphan-pairs`);
      setOrphanPairsData(r.data);
    } catch (e) {
      setOrphanPairsError(e.response?.data?.detail || 'Failed to scan for orphan pairs.');
    } finally {
      setOrphanPairsLoading(false);
    }
  };

  // Archive Lifecycle Project Phase 1 (2026-10-16) — "Confirm Batch Merge"
  // handler. Executes auto-merge on all orphan pairs server-side.
  const handleConfirmBatchMerge = async () => {
    setBatchMergeRunning(true);
    setBatchMergeResult(null);
    try {
      const r = await axios.post(`${API}/admin/archive/batch-merge`);
      setBatchMergeResult(r.data);
      // Refresh recently-removed + orphan-pairs after a successful batch.
      try {
        const refreshRecently = await axios.get(`${API}/admin/auto-cleanup/recently-removed`);
        setRecentlyRemovedData(refreshRecently.data);
      } catch {}
      try {
        const refreshPairs = await axios.get(`${API}/admin/archive/orphan-pairs`);
        setOrphanPairsData(refreshPairs.data);
      } catch {}
      try { await fetchUsers(); } catch {}
    } catch (e) {
      setBatchMergeResult({
        error: e.response?.data?.detail || 'Batch merge failed.',
        merged: 0, failed: 0, scanned: 0,
      });
    } finally {
      setBatchMergeRunning(false);
    }
  };

  // 2026-10-14 — Toggle auto_cleanup_exempt on the currently-selected
  // user. Mirrors handleSaveAdminName pattern: optimistic server
  // call, refetch user detail on success.
  const handleToggleCleanupExempt = async () => {
    if (!selectedUser || cleanupExemptLoading) return;
    const next = !bool(selectedUser.auto_cleanup_exempt);
    setCleanupExemptLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/users/${selectedUser.id}/cleanup-exempt`,
        { exempt: next }
      );
      setSelectedUser((prev) => prev ? { ...prev, auto_cleanup_exempt: !!r.data?.exempt } : prev);
    } catch (e) {
      setError('Failed to update cleanup exempt flag. Try again.');
    } finally {
      setCleanupExemptLoading(false);
    }
  };

  // Local coercer — avoid depending on React's default state init semantics
  // when the field is absent from the user doc (pre-migration docs).
  function bool(v) { return !!v; }

  const handleClose = () => {
    setOpen(false);
    setView('list');
    setSelectedUser(null);
    setHistTarget(null);
    setError('');
  };

  // Cross-component "open merge for user" listener 2026-09-25.
  // The per-entry admin popover (QueueEntryWithMenu) dispatches
  // `karaoke:open-merge-for-user` with `{ user_id }` when the host
  // taps the 🔗 Merge… button. We open the modal, jump straight to
  // that user's detail, then open the merge picker so the host lands
  // in the right place with one tap.
  //
  // 2026-10-07: delegate to handleOpenMergePicker() so this launch
  // path stays in sync with the primary button in the detail view —
  // post-merge-picker-rewrite the picker needs BOTH cohorts loaded
  // (not just legacy) and the mergeLegacyExpanded state reset.
  useEffect(() => {
    const handler = async (e) => {
      const uid = e?.detail?.user_id;
      if (!uid) return;
      setOpen(true);
      setError('');
      setView('detail');
      await fetchDetail(uid);
      handleOpenMergePicker();
    };
    window.addEventListener('karaoke:open-merge-for-user', handler);
    return () => window.removeEventListener('karaoke:open-merge-for-user', handler);
  }, [fetchDetail]);
  // Restore scroll position after list refetch resolves. rAF ensures the
  // DOM has committed the refreshed users[] array before we set scrollTop,
  // so the scroll offset lands on real content (not an empty list) 2026-10-04.
  const restoreListScroll = () => {
    requestAnimationFrame(() => {
      if (listScrollRef.current) {
        listScrollRef.current.scrollTop = savedListScrollTopRef.current;
      }
    });
  };


  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    // Debounced parallel fetch — both cohorts filter against the same query.
    // Auto-expand legacy dropdown when a non-empty query likely has matches,
    // so admin sees matches immediately 2026-10-04. The actual auto-expand
    // once results arrive is also handled by the useEffect below.
    searchTimerRef.current = setTimeout(() => {
      Promise.all([fetchUsers(val), fetchLegacyUsers(val)]);
    }, 250);
  };

  // Auto-expand/collapse the legacy dropdown based on search state 2026-10-04.
  // When admin is actively searching AND there's at least one passkey-category
  // legacy match (admin regulars + fallbacks are hoisted into the main section,
  // so only passkeys appear in the Legacy panel), expand so matches are
  // immediately visible. On idle (empty search), collapse back to default.
  // Admin can still manually override via the toggle.
  const prevSearchRef = useRef('');
  useEffect(() => {
    const trimmed = search.trim();
    const legacyPasskeyMatchCount = legacyUsers.filter((u) => u.category === 'passkey').length;
    if (trimmed.length > 0 && legacyPasskeyMatchCount > 0 && prevSearchRef.current !== trimmed) {
      setLegacyExpanded(true);
    } else if (trimmed.length === 0 && prevSearchRef.current.length > 0) {
      // Cleared search → collapse legacy back to default.
      setLegacyExpanded(false);
    }
    prevSearchRef.current = trimmed;
  }, [search, legacyUsers]);

  const handleRowClick = (u) => {
    // Capture current scroll position so Back can restore it 2026-10-04.
    if (listScrollRef.current) {
      savedListScrollTopRef.current = listScrollRef.current.scrollTop;
    }
    setView('detail');
    fetchDetail(u.id);
  };

  const handleBackToList = async () => {
    setView('list');
    setSelectedUser(null);
    setError('');
    // Refresh both cohorts in parallel then restore scroll 2026-10-04.
    await Promise.all([fetchUsers(search), fetchLegacyUsers(search)]);
    restoreListScroll();
  };

  // ── Delete user 2026-09-25 — invoked by HoldToDeleteUserButton after
  // long-press + modal confirm. Backend archives to deleted_users_archive
  // (30-day undo) and blocks if the user has active queue entries.
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setError('');
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`);
      // Go back to the list and refresh both cohorts.
      setView('list');
      setSelectedUser(null);
      // Preserve scroll position so bulk-cleanup flow isn't broken 2026-10-04.
      // savedListScrollTopRef still holds the offset captured on row-click.
      // Rows have uniform height; the admin lands in the same neighborhood
      // minus the deleted user. Browser clamps scrollTop if list is shorter.
      await Promise.all([fetchUsers(search), fetchLegacyUsers(search)]);
      restoreListScroll();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete user');
      // Re-throw so the button's isSubmitting resolves back to false via .finally,
      // but the modal stays closed. Actually — we want to keep the modal state
      // in HoldToDeleteUserButton, so we surface the error via `error` state and
      // let the button close its modal normally.
    }
  };

  const handleSaveAdminName = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError('');
    try {
      const res = await axios.patch(
        `${API}/admin/users/${selectedUser.id}`,
        { admin_name: adminNameDraft }
      );
      setSelectedUser((prev) => prev ? { ...prev, admin_name: res.data?.admin_name || null } : prev);
      setAdminNameDraft(res.data?.admin_name || '');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleHistLongPress = (item) => {
    setHistTarget(item);
  };

  const handleHistConfirmDelete = async () => {
    if (!selectedUser || !histTarget) return;
    setHistDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/users/${selectedUser.id}/song-history/entry`,
        {
          data: {
            song_id: histTarget.song_id ?? null,
            song_title: histTarget.song_title || '',
            song_artist: histTarget.song_artist || '',
          },
        }
      );
      // Optimistic remove
      setSelectedUser((prev) => prev
        ? { ...prev, song_history: (prev.song_history || []).filter((h) => h !== histTarget) }
        : prev);
      setHistTarget(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setHistDeleting(false);
    }
  };

  // ── Alias delete 2026-09-22 — long-press an alias row to open confirm ──
  // Mirrors the song-history delete pattern above.
  const handleAliasLongPress = (alias) => {
    setAliasTarget(alias);
  };

  const handleAliasConfirmDelete = async () => {
    if (!selectedUser || !aliasTarget) return;
    setAliasDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/users/${selectedUser.id}/name-history/entry`,
        { data: { name: aliasTarget.name || '' } }
      );
      // Optimistic remove
      setSelectedUser((prev) => prev
        ? { ...prev, name_history: (prev.name_history || []).filter((a) => a !== aliasTarget) }
        : prev);
      setAliasTarget(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Alias delete failed');
    } finally {
      setAliasDeleting(false);
    }
  };

  // ── Merge Identity handlers (Railway migration, 2026-09-19) ──
  const handleOpenMergePicker = () => {
    setMergePickerOpen(true);
    setLegacySearch('');
    setMergeCandidate(null);
    setMergeLegacyExpanded(false);
    // Refresh BOTH cohorts so the merge picker (visual twin of the main
    // Manage Users view, 2026-10-07) has fresh data for all sections.
    // Previously only legacy users were fetched here because the picker
    // only showed legacy users.
    Promise.all([fetchUsers(''), fetchLegacyUsers('')]);
  };

  const handleLegacySearchChange = (e) => {
    const val = e.target.value;
    setLegacySearch(val);
    if (legacySearchTimerRef.current) clearTimeout(legacySearchTimerRef.current);
    // Search both cohorts (merge picker now shows all users, 2026-10-07).
    legacySearchTimerRef.current = setTimeout(() => {
      Promise.all([fetchUsers(val), fetchLegacyUsers(val)]);
    }, 250);
  };

  const handleCloseMergePicker = () => {
    setMergePickerOpen(false);
    // DON'T clear `legacyUsers` or `users` — the parent Manage Users view
    // still needs them when the picker closes. Only reset picker-local state.
    setLegacySearch('');
    setMergeCandidate(null);
    setMergeLegacyExpanded(false);
    // Restore the main-view search results (picker's own search may have
    // overwritten them). Fire-and-forget.
    Promise.all([fetchUsers(search), fetchLegacyUsers(search)]);
  };

  const handleSelectLegacy = async (legacyUser) => {
    // Fetch full detail so the confirmation preview shows accurate counts.
    try {
      const res = await axios.get(`${API}/admin/users/${legacyUser.id}`);
      setMergeCandidate(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load legacy user');
    }
  };

  const handleConfirmMerge = async () => {
    if (!selectedUser || !mergeCandidate) return;
    setMerging(true);
    setError('');
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/merge-with/${mergeCandidate.id}`
      );
      // Refresh the current user detail (song_history + name_history changed).
      await fetchDetail(selectedUser.id);
      handleCloseMergePicker();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const nameDraftDirty = (selectedUser?.admin_name || '') !== adminNameDraft;

  // Category badge styles.
  const categoryBadge = (cat) => {
    if (cat === 'passkey') return { text: 'Passkey', cls: 'bg-blue-800/70 text-blue-100' };
    if (cat === 'admin_regular') return { text: 'Admin', cls: 'bg-amber-800/70 text-amber-100' };
    if (cat === 'fallback') return { text: '⚠️ Fallback', cls: 'bg-orange-800/70 text-orange-100' };
    return { text: cat, cls: 'bg-gray-700 text-gray-200' };
  };

  // Legacy pseudo-badge 2026-10-04 — replaces whichever category badge
  // (passkey/admin/fallback) a user would normally wear, so admin can tell
  // at a glance that a user is pre-Railway-migration without digging into
  // detail view. Purple to differentiate from the existing badge palette.
  const legacyBadge = () => ({ text: 'Legacy', cls: 'bg-purple-800/70 text-purple-100' });

  return (
    <>
      <button
        data-testid="manage-users-btn"
        onClick={handleOpen}
        className="bg-slate-600 text-white px-3 py-2 sm:py-1 rounded hover:bg-slate-700 text-sm font-semibold"
        title="View and edit persistent user identities"
      >
        👥 Manage Users
      </button>

      {/* Modal rendered via React portal at document.body 2026-09-26 —
          escapes any collapsed ancestor's `display: none`. Without the
          portal, when Admin Controls is collapsed, the modal DOM would
          render inside a `hidden` parent and be invisible even though
          the state opened correctly. This was the exact "Merge button
          click renders nothing" bug reported in the wild. */}
      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-70"
            onClick={handleClose}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-gray-800 border-2 border-gray-600 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col pointer-events-auto">
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  {view === 'detail' && (
                    <button
                      data-testid="manage-users-back-btn"
                      onClick={handleBackToList}
                      className="text-gray-400 hover:text-white text-sm"
                    >
                      ← Back
                    </button>
                  )}
                  <h3 className="text-white font-bold text-lg">
                    {view === 'list' ? '👥 Manage Users' : '✏️ Edit User'}
                  </h3>
                </div>
                <button
                  data-testid="manage-users-close-btn"
                  onClick={handleClose}
                  className="text-gray-400 hover:text-white text-xl leading-none"
                  aria-label="Close"
                >×</button>
              </div>

              {error && (
                <div className="px-4 pt-3 text-red-400 text-sm">{error}</div>
              )}

              {/* Body */}
              <div ref={listScrollRef} className="flex-1 overflow-y-auto p-4">
                {view === 'list' && (() => {
                  // Backend's cohort=new query already includes ALL admin
                  // regulars and ALL fallbacks via an OR clause, regardless
                  // of their created_at. So `users` already has everything
                  // we want in the main section — no hoisting needed. We
                  // just filter `legacyUsers` down to passkey-only for the
                  // Legacy dropdown (which should ONLY show pre-migration
                  // passkey users who may need to be merged) 2026-10-04.
                  const displayedMainUsers = users;
                  const displayedLegacyUsers = legacyUsers.filter((u) => u.category === 'passkey');
                  return (
                  <>
                    <input
                      data-testid="manage-users-search-input"
                      type="text"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search by name, admin name, or previous alias…"
                      className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 mb-3"
                    />
                    {/* 2026-10-11 — One-timer auto-cleanup admin toggle.
                        Fires at 6 AM Pacific inside _reset_session_internal().
                        Archives passkey users with exactly 1 song unplayed
                        >14 days. Archive-first (30-day restore via admin
                        tooling). Mirrors the auto-advance/auto-insert
                        toggle color grammar (green=ON, gray=OFF). */}
                    <div
                      data-testid="manage-users-one-timer-cleanup-row"
                      className="flex items-start justify-between gap-3 px-3 py-2 mb-3 rounded border border-gray-700 bg-gray-900/60"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-semibold">
                          Auto-cleanup inactive users
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="manage-users-one-timer-cleanup-toggle-btn"
                        onClick={handleToggleOneTimerCleanup}
                        disabled={oneTimerCleanupLoading}
                        aria-pressed={oneTimerCleanupEnabled}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition-colors disabled:opacity-50 ${
                          oneTimerCleanupEnabled
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-600 hover:bg-gray-500 text-gray-100'
                        }`}
                      >
                        {oneTimerCleanupLoading
                          ? '…'
                          : oneTimerCleanupEnabled
                          ? 'ON'
                          : 'OFF'}
                      </button>
                    </div>
                    {/* 2026-10-14 — Button to open the recently-removed
                        modal. Shows cumulative archive of users
                        swept by the one-timer cleanup (across all
                        sweeps, not just the most recent).
                        View-only; spare toggle lives in per-user menu. */}
                    <button
                      type="button"
                      data-testid="manage-users-open-recently-removed-btn"
                      onClick={handleOpenRecentlyRemoved}
                      className="w-full text-left bg-gray-900/60 hover:bg-gray-800 border border-gray-700 rounded px-3 py-2 mb-3 text-sm text-gray-200 transition-colors flex items-center justify-between"
                    >
                      <span>🗂️ Show recently removed/archived users</span>
                      <span className="text-gray-500 text-lg">›</span>
                    </button>
                    {/* ── Main user list — post-migration passkey users + ALL
                        admin regulars + ALL fallbacks (pre- and post-migration) */}
                    {(() => {
                      if (loadingUsers || displayedMainUsers.length === 0) return null;
                      const passkeyCount = displayedMainUsers.filter((x) => x.category === 'passkey').length;
                      const regularCount = displayedMainUsers.filter((x) => x.category === 'admin_regular').length;
                      const isFiltered = search.trim().length > 0;
                      const label = isFiltered ? 'Matches' : 'Users';
                      return (
                        <div
                          data-testid="manage-users-counter"
                          className="flex items-center justify-between text-xs mb-2 px-1"
                        >
                          <div className="text-gray-300">
                            <span className="font-semibold text-white">
                              {label} ({displayedMainUsers.length})
                            </span>
                            <span className="text-gray-500 ml-2">
                              {passkeyCount} Passkey · {regularCount} Admin
                            </span>
                          </div>
                          {isFiltered && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearch('');
                                Promise.all([fetchUsers(''), fetchLegacyUsers('')]);
                              }}
                              className="text-gray-400 hover:text-white text-xs underline"
                            >
                              Clear search
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    {(() => {
                      if (loadingUsers) {
                        return <div className="text-gray-400 text-sm">Loading users…</div>;
                      }
                      if (displayedMainUsers.length === 0) {
                        const emptyMsg = search
                          ? 'No users match this search.'
                          : 'No users found.';
                        return (
                          <div className="text-gray-500 text-sm text-center py-8">
                            {emptyMsg}
                          </div>
                        );
                      }
                      return (
                        <div className="space-y-1" data-testid="manage-users-list">
                          {displayedMainUsers.map((u) => {
                            const bd = categoryBadge(u.category);
                            const primary = u.admin_name || u.name;
                            const secondary = u.admin_name ? u.name : null;
                            return (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`manage-users-row-${u.id}`}
                                onClick={() => handleRowClick(u)}
                                className="w-full text-left px-3 py-2 rounded hover:bg-gray-700 border border-gray-700 hover:border-gray-500 transition-all"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-white font-semibold truncate">{primary}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${bd.cls}`}>
                                        {bd.text}
                                      </span>
                                    </div>
                                    {secondary && (
                                      <div className="text-xs text-gray-400 mt-0.5 truncate">
                                        Display name: <span className="text-gray-300">{secondary}</span>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {u.lifetime_song_count} song{u.lifetime_song_count === 1 ? '' : 's'}
                                      {u.alias_count > 0 && (
                                        <> · {u.alias_count} alias{u.alias_count === 1 ? '' : 'es'}</>
                                      )}
                                      {u.last_sang_at && (
                                        <> · last sang {new Date(u.last_sang_at).toLocaleDateString()}</>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-gray-500 text-lg">›</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* ── Legacy Users (expandable section — passkey-only) 2026-10-04 ──
                        Mirrors the Deleted Entries panel on the admin queue:
                        full-width toggle button with chevron, panel below.
                        Admin users and fallback users are hoisted into the main
                        list above, so this section only shows pre-migration
                        passkey users (who may need to be merged). */}
                    <div className="mt-4 border-t border-gray-700 pt-4">
                      <button
                        type="button"
                        data-testid="legacy-users-toggle"
                        onClick={() => setLegacyExpanded(v => !v)}
                        className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                      >
                        <span>
                          <span className="inline-block px-1.5 py-0.5 rounded text-xs mr-2 bg-purple-800/70 text-purple-100">
                            Legacy
                          </span>
                          Legacy Users{displayedLegacyUsers.length > 0 ? ` (${displayedLegacyUsers.length})` : ''}
                        </span>
                        <span className="text-gray-300">{legacyExpanded ? '▲' : '▼'}</span>
                      </button>

                      {legacyExpanded && (
                        <div className="mt-3 space-y-1" data-testid="legacy-users-panel">
                          {loadingLegacy && (
                            <div className="text-gray-400 text-sm">Loading legacy users…</div>
                          )}
                          {!loadingLegacy && displayedLegacyUsers.length === 0 && (
                            <div className="text-gray-500 text-sm text-center py-4">
                              {search
                                ? 'No legacy users match this search.'
                                : 'No legacy users remaining.'}
                            </div>
                          )}
                          {!loadingLegacy && displayedLegacyUsers.map((u) => {
                            const bd = legacyBadge();
                            const primary = u.admin_name || u.name;
                            const secondary = u.admin_name ? u.name : null;
                            return (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`manage-users-row-${u.id}`}
                                onClick={() => handleRowClick(u)}
                                className="w-full text-left px-3 py-2 rounded hover:bg-gray-700 border border-gray-700 hover:border-gray-500 transition-all"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-white font-semibold truncate">{primary}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${bd.cls}`}>
                                        {bd.text}
                                      </span>
                                    </div>
                                    {secondary && (
                                      <div className="text-xs text-gray-400 mt-0.5 truncate">
                                        Display name: <span className="text-gray-300">{secondary}</span>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {u.lifetime_song_count} song{u.lifetime_song_count === 1 ? '' : 's'}
                                      {u.alias_count > 0 && (
                                        <> · {u.alias_count} alias{u.alias_count === 1 ? '' : 'es'}</>
                                      )}
                                      {u.last_sang_at && (
                                        <> · last sang {new Date(u.last_sang_at).toLocaleDateString()}</>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-gray-500 text-lg">›</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                  );
                })()}

                {view === 'detail' && (
                  <>
                    {loadingDetail && (
                      <div className="text-gray-400 text-sm">Loading detail…</div>
                    )}
                    {!loadingDetail && selectedUser && (
                      <div className="space-y-5" data-testid="manage-users-detail">
                        {/* Header — primary identity */}
                        <div className="border-b border-gray-700 pb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-bold text-xl">
                              {selectedUser.admin_name || selectedUser.name}
                            </span>
                            {(() => {
                              const bd = categoryBadge(selectedUser.category);
                              return (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${bd.cls}`}>
                                  {bd.text}
                                </span>
                              );
                            })()}
                          </div>
                          {selectedUser.admin_name && (
                            <div className="text-sm text-gray-400 mt-1">
                              Public display name: <span className="text-gray-200">{selectedUser.name}</span>
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedUser.total_turns} total turn{selectedUser.total_turns === 1 ? '' : 's'} ·
                            joined {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '—'}
                          </div>
                        </div>

                        {/* Merge Identity (Railway migration) */}
                        <button
                          type="button"
                          data-testid="manage-users-merge-btn"
                          onClick={handleOpenMergePicker}
                          className="w-full flex items-center justify-between bg-indigo-700 hover:bg-indigo-600 border border-indigo-500 rounded-lg px-4 py-3 text-left transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">🔗</span>
                            <div>
                              <div className="text-white font-semibold">Merge with User</div>
                              <div className="text-xs text-indigo-200">
                                Import song history, aliases, and turn count from another user record.
                              </div>
                            </div>
                          </div>
                          <span className="text-indigo-200 text-lg">›</span>
                        </button>

                        {/* Admin name editor */}
                        <div>
                          <label className="block text-sm font-semibold text-white mb-1">
                            Admin Name <span className="text-gray-500 font-normal">(admin-only real-name label)</span>
                          </label>
                          <div className="text-xs text-gray-500 mb-2">
                            Shown to admin everywhere except the live queue rows (which stay as the user's chosen display name).
                          </div>
                          <div className="flex gap-2">
                            <input
                              data-testid="manage-users-admin-name-input"
                              type="text"
                              value={adminNameDraft}
                              onChange={(e) => setAdminNameDraft(e.target.value)}
                              placeholder="e.g. Sarah (red hair)"
                              maxLength={80}
                              className="flex-1 px-3 py-2 rounded bg-gray-900 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              data-testid="manage-users-admin-name-save-btn"
                              type="button"
                              disabled={!nameDraftDirty || saving}
                              onClick={handleSaveAdminName}
                              className="bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {saving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          {adminNameDraft && (
                            <button
                              type="button"
                              onClick={() => setAdminNameDraft('')}
                              className="text-xs text-gray-500 hover:text-gray-300 mt-1"
                            >
                              Clear
                            </button>
                          )}
                        </div>

                        {/* Aliases */}
                        <div>
                          <div className="text-sm font-semibold text-white mb-1">
                            Previously used names ({selectedUser.name_history?.length || 0})
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            Long-press an alias to delete it (e.g. when a singer used someone else's name to sign up a friend).
                          </div>
                          {(!selectedUser.name_history || selectedUser.name_history.length === 0) ? (
                            <div className="text-xs text-gray-500">
                              No alias history yet. New name changes will be tracked automatically going forward.
                            </div>
                          ) : (
                            <div className="space-y-1" data-testid="manage-users-alias-list">
                              {selectedUser.name_history.map((a, i) => (
                                <AliasRow
                                  key={`${a.name}-${i}`}
                                  alias={a}
                                  index={i}
                                  onLongPress={handleAliasLongPress}
                                  className="flex items-center justify-between text-sm bg-gray-900/60 rounded px-3 py-1.5 select-none cursor-pointer hover:bg-gray-900/80 transition-colors"
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Song history */}
                        <div>
                          <div className="text-sm font-semibold text-white mb-1">
                            Song history ({selectedUser.song_history?.length || 0})
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            Tap to view · Long-press to delete from history (queue is not affected).
                          </div>
                          {(!selectedUser.song_history || selectedUser.song_history.length === 0) ? (
                            <div className="text-xs text-gray-500">No songs sung yet.</div>
                          ) : (
                            <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="manage-users-history-list">
                              {selectedUser.song_history.map((item, i) => (
                                <HistoryDropdownRow
                                  key={`${item.song_id ?? item.song_title}-${i}`}
                                  item={item}
                                  index={i}
                                  testIdPrefix="manage-users-history-row"
                                  onSelect={() => {}}
                                  onLongPress={handleHistLongPress}
                                  className="bg-gray-900/60 hover:bg-gray-700 rounded px-3 py-2 cursor-pointer transition-colors select-none"
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* 2026-10-14 — Spare from auto-cleanup
                            toggle. Writes auto_cleanup_exempt=true
                            on the user doc; sweep's base filter
                            excludes exempted users. Visible on all
                            user categories (admin regulars are
                            already spared via is_admin_managed_regular,
                            but the toggle is harmless on them). */}
                        <div
                          data-testid="manage-users-cleanup-exempt-row"
                          className="flex items-start justify-between gap-3 px-3 py-2 rounded border border-gray-700 bg-gray-900/60"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-semibold">
                              Spare from auto-cleanup
                            </div>
                            <div className="text-gray-400 text-xs mt-0.5 leading-snug">
                              When toggled on, this user is exempt from the daily 6AM inactivity sweep.
                            </div>
                          </div>
                          <button
                            type="button"
                            data-testid="manage-users-cleanup-exempt-toggle-btn"
                            onClick={handleToggleCleanupExempt}
                            disabled={cleanupExemptLoading}
                            aria-pressed={!!selectedUser.auto_cleanup_exempt}
                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition-colors disabled:opacity-50 ${
                              selectedUser.auto_cleanup_exempt
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-gray-600 hover:bg-gray-500 text-gray-100'
                            }`}
                          >
                            {cleanupExemptLoading
                              ? '…'
                              : selectedUser.auto_cleanup_exempt
                              ? 'ON'
                              : 'OFF'}
                          </button>
                        </div>

                        {/* Danger Zone — permanent delete 2026-09-25 */}
                        <div className="border-t border-red-900/40 pt-4 mt-2" data-testid="manage-users-danger-zone">
                          <div className="text-xs uppercase tracking-wide text-red-400 font-semibold mb-1">
                            Danger Zone
                          </div>
                          <div className="text-xs text-gray-500 mb-3">
                            Hold the button for 3 seconds, then confirm. Record is archived for 30-day undo.
                          </div>
                          <HoldToDeleteUserButton
                            userLabel={selectedUser.admin_name || selectedUser.name}
                            onDelete={handleDeleteUser}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* History delete confirm — top-most modal layer */}
          {histTarget && (
            <HistoryDeleteConfirmModal
              item={histTarget}
              subjectLabel={`${(selectedUser?.admin_name || selectedUser?.name || 'this user')}'s`}
              isSubmitting={histDeleting}
              onConfirm={handleHistConfirmDelete}
              onCancel={() => setHistTarget(null)}
            />
          )}

          {/* Alias delete confirm — top-most modal layer */}
          {aliasTarget && (
            <AliasDeleteConfirmModal
              alias={aliasTarget}
              subjectLabel={`${(selectedUser?.admin_name || selectedUser?.name || 'this user')}'s`}
              isSubmitting={aliasDeleting}
              onConfirm={handleAliasConfirmDelete}
              onCancel={() => setAliasTarget(null)}
            />
          )}

          {/* 2026-10-14 — Recently-removed users modal. Styled to match
              the catalog-removals modal pattern. View-only; shows
              everyone archived by the daily one-timer sweep. */}
          {recentlyRemovedModalOpen && (
            <>
              <div
                className="fixed inset-0 z-[60] bg-black bg-opacity-80"
                onClick={handleCloseRecentlyRemoved}
                data-testid="recently-removed-modal-backdrop"
              />
              <div
                className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none"
                data-testid="recently-removed-modal"
              >
                <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden pointer-events-auto flex flex-col">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                    <div>
                      <div className="text-white font-semibold">
                        🗂️ Recently Removed Users
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">
                        Archive includes auto-cleanup sweep + admin manual deletes. Restorable within 30 days.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseRecentlyRemoved}
                      data-testid="recently-removed-modal-close-btn"
                      className="text-gray-400 hover:text-white text-2xl leading-none px-2"
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="px-4 py-3 overflow-y-auto flex-1">
                    {recentlyRemovedLoading && (
                      <div className="text-gray-400 text-sm">Loading…</div>
                    )}
                    {recentlyRemovedError && !recentlyRemovedLoading && (
                      <div className="text-red-400 text-sm">{recentlyRemovedError}</div>
                    )}
                    {!recentlyRemovedLoading && !recentlyRemovedError && recentlyRemovedData && (() => {
                      // Client-side archive search: name + name_history (aliases) + user_id,
                      // case-insensitive substring. Zero network cost — the full list is
                      // already in state from the single modal-open fetch. Added 2026-11-03.
                      const q = recentlyRemovedSearch.trim().toLowerCase();
                      const allEntries = recentlyRemovedData.entries || [];
                      const filteredEntries = q === ''
                        ? allEntries
                        : allEntries.filter((row) => {
                            const name = (row.user_name || '').toLowerCase();
                            const uid = (row.user_id || '').toLowerCase();
                            if (name.includes(q)) return true;
                            if (uid.includes(q)) return true;
                            const aliases = Array.isArray(row.name_history) ? row.name_history : [];
                            for (const a of aliases) {
                              if ((a || '').toLowerCase().includes(q)) return true;
                            }
                            return false;
                          });
                      const isFiltering = q !== '';
                      return (
                      <>
                        <div className="text-gray-300 text-sm mb-3" data-testid="recently-removed-modal-total">
                          {isFiltering ? (
                            <>
                              Showing <span className="font-semibold text-white">{filteredEntries.length}</span>
                              {' '}of <span className="font-semibold text-white">{recentlyRemovedData.total}</span>
                            </>
                          ) : (
                            <>
                              Total archived: <span className="font-semibold text-white">{recentlyRemovedData.total}</span>
                            </>
                          )}
                        </div>
                        {/* Archive Lifecycle Project Phase 1 (2026-10-16) — batch-merge control bar.
                            Dry-run preview via orphanPairsData loaded alongside the recently-removed
                            list; execute via POST /admin/archive/batch-merge. Preserves the 2-step
                            UX (scan → confirm) for safety. */}
                        <div
                          className="mb-3 px-3 py-2 bg-indigo-950/40 border border-indigo-700/40 rounded"
                          data-testid="recently-removed-modal-batch-merge-bar"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs text-indigo-200 flex-1">
                              {orphanPairsLoading && <span>Scanning for orphan pairs…</span>}
                              {!orphanPairsLoading && orphanPairsError && (
                                <span className="text-red-300">{orphanPairsError}</span>
                              )}
                              {!orphanPairsLoading && !orphanPairsError && orphanPairsData && (
                                orphanPairsData.count === 0 ? (
                                  <span>✓ No orphan pairs — archive is clean.</span>
                                ) : (
                                  <span>
                                    <span className="text-indigo-100 font-semibold">
                                      {orphanPairsData.count}
                                    </span>{' '}
                                    orphan pair{orphanPairsData.count === 1 ? '' : 's'} detected 🔗 —
                                    restoring any flagged row auto-merges with the live user; or batch-merge all now:
                                  </span>
                                )
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                data-testid="recently-removed-modal-rescan-btn"
                                onClick={handleScanOrphanPairs}
                                disabled={orphanPairsLoading || batchMergeRunning}
                                title="Re-scan the archive for orphan pairs"
                                className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                                  (orphanPairsLoading || batchMergeRunning)
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                    : 'bg-slate-700 hover:bg-slate-600 text-white'
                                }`}
                              >
                                {orphanPairsLoading ? '…' : '↻ Scan'}
                              </button>
                              {!orphanPairsLoading && orphanPairsData && orphanPairsData.count > 0 && (
                                <button
                                  type="button"
                                  data-testid="recently-removed-modal-batch-merge-btn"
                                  onClick={handleConfirmBatchMerge}
                                  disabled={batchMergeRunning}
                                  title="Auto-merge all orphan pairs now"
                                  className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                                    batchMergeRunning
                                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                      : 'bg-indigo-700 hover:bg-indigo-600 text-white'
                                  }`}
                                >
                                  {batchMergeRunning ? '…' : `🔗 Merge ${orphanPairsData.count}`}
                                </button>
                              )}
                            </div>
                          </div>
                          {batchMergeResult && (
                            <div
                              data-testid="recently-removed-modal-batch-merge-result"
                              className={`mt-2 px-2 py-1 rounded text-xs ${
                                batchMergeResult.error
                                  ? 'bg-red-900/40 border border-red-700/50 text-red-200'
                                  : 'bg-emerald-900/30 border border-emerald-700/40 text-emerald-200'
                              }`}
                            >
                              {batchMergeResult.error ? (
                                <>Batch merge error: {batchMergeResult.error}</>
                              ) : (
                                <>
                                  ✓ Merged <span className="font-semibold">{batchMergeResult.merged}</span>
                                  {batchMergeResult.failed > 0 && (
                                    <>, {batchMergeResult.failed} failed</>
                                  )}
                                  {' '}of {batchMergeResult.scanned} scanned.
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Search bar — client-side filter over already-loaded entries.
                            Matches display name, aliases (name_history), and user_id.
                            Added 2026-11-03. */}
                        {recentlyRemovedData.total > 0 && (
                          <div className="mb-3">
                            <div className="relative">
                              <input
                                type="text"
                                value={recentlyRemovedSearch}
                                onChange={(e) => setRecentlyRemovedSearch(e.target.value)}
                                placeholder="Search by name, alias, or user ID…"
                                data-testid="recently-removed-modal-search-input"
                                className="w-full bg-gray-900/70 border border-gray-700 focus:border-indigo-500 focus:outline-none rounded px-3 py-1.5 text-sm text-gray-100 placeholder-gray-500 pr-8"
                                autoComplete="off"
                                spellCheck="false"
                              />
                              {recentlyRemovedSearch && (
                                <button
                                  type="button"
                                  onClick={() => setRecentlyRemovedSearch('')}
                                  data-testid="recently-removed-modal-search-clear-btn"
                                  aria-label="Clear search"
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-lg leading-none px-1.5"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {recentlyRemovedData.total === 0 ? (
                          <div className="text-gray-400 italic text-sm">
                            No users in the archive right now.
                          </div>
                        ) : filteredEntries.length === 0 ? (
                          <div
                            className="text-gray-400 italic text-sm"
                            data-testid="recently-removed-modal-no-matches"
                          >
                            No archived users match &ldquo;{recentlyRemovedSearch}&rdquo;.
                          </div>
                        ) : (
                          <div
                            className="space-y-1 text-xs font-mono"
                            data-testid="recently-removed-modal-list"
                          >
                            {/* Column headers */}
                            <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-gray-900/80 rounded text-gray-400 uppercase tracking-wide">
                              <div className="col-span-3">Name</div>
                              <div className="col-span-2">Reason</div>
                              <div className="col-span-2">Deleted</div>
                              <div className="col-span-2">Last Sang</div>
                              <div className="col-span-2">User ID</div>
                              <div className="col-span-1 text-right">↩</div>
                            </div>
                            {filteredEntries.map((row, i) => {
                              const isRestoring = restoringUserIds.has(row.user_id);
                              const rowError = restoreRowError[row.user_id];
                              const isSweep = row.deleted_reason === 'auto_cleanup_inactive_one_timer';
                              const isSweep30d = row.deleted_reason === 'auto_cleanup_inactive_30d';
                              const isAdminDelete = row.deleted_reason === 'admin_delete';
                              const reasonLabel = isSweep
                                ? '🧹 Sweep (14d)'
                                : (isSweep30d
                                    ? '🧹 Sweep (30d)'
                                    : (isAdminDelete ? '👤 Admin' : (row.deleted_reason || '—')));
                              const reasonClass = (isSweep || isSweep30d)
                                ? 'text-gray-300'
                                : (isAdminDelete ? 'text-red-300' : 'text-gray-400');
                              // Archive Lifecycle Project Phase 1 (2026-10-16) —
                              // pending-review badge for sweep output + would-merge
                              // indicator when a fingerprint collision exists.
                              const hasReviewFlag = Boolean(row.archive_pending_review);
                              const willMerge = Boolean(
                                (orphanPairsData?.pairs || []).find((p) => p.archive_id === row.user_id)
                              );
                              return (
                                <div key={`${row.user_id}-${i}`} data-testid={`recently-removed-modal-row-${i}`}>
                                  <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-gray-900/40 hover:bg-gray-900/70 rounded text-gray-200 transition-colors items-center">
                                    <div className="col-span-3 truncate flex items-center gap-1" title={row.user_name}>
                                      <span className="truncate">{row.user_name}</span>
                                      {hasReviewFlag && (
                                        <span
                                          data-testid={`recently-removed-modal-review-badge-${i}`}
                                          title="Pending admin review (swept by inactivity cleanup)"
                                          className="text-amber-300 text-[0.7rem]"
                                        >
                                          🔖
                                        </span>
                                      )}
                                      {willMerge && (
                                        <span
                                          data-testid={`recently-removed-modal-merge-badge-${i}`}
                                          title="Restoring will auto-merge with an existing live user"
                                          className="text-indigo-300 text-[0.7rem]"
                                        >
                                          🔗
                                        </span>
                                      )}
                                    </div>
                                    <div className={`col-span-2 truncate ${reasonClass}`} title={row.deleted_reason}>
                                      {reasonLabel}
                                    </div>
                                    <div className="col-span-2 truncate text-gray-400" title={row.deleted_at}>
                                      {(row.deleted_at || '').slice(0, 10)}
                                    </div>
                                    <div className="col-span-2 truncate text-gray-400" title={row.last_sang_utc}>
                                      {(row.last_sang_utc || '').slice(0, 10) || '—'}
                                    </div>
                                    <div className="col-span-2 truncate text-gray-500" title={row.user_id}>
                                      {(row.user_id || '').slice(0, 12)}
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                      <button
                                        type="button"
                                        data-testid={`recently-removed-modal-restore-btn-${i}`}
                                        onClick={() => handleRestoreButtonClick(row)}
                                        disabled={isRestoring}
                                        title={isRestoring ? 'Restoring…' : (willMerge ? 'Restore & auto-merge' : 'Restore this user')}
                                        className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                                          isRestoring
                                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                            : (willMerge
                                                ? 'bg-indigo-700 hover:bg-indigo-600 text-white'
                                                : 'bg-emerald-700 hover:bg-emerald-600 text-white')
                                        }`}
                                      >
                                        {isRestoring ? '…' : (willMerge ? '🔗↩' : '↩')}
                                      </button>
                                    </div>
                                  </div>
                                  {rowError && (
                                    <div
                                      data-testid={`recently-removed-modal-row-error-${i}`}
                                      className={`px-2 py-1.5 mt-0.5 rounded text-xs leading-snug ${
                                        rowError.startsWith('✓')
                                          ? 'bg-emerald-900/40 border border-emerald-700/50 text-emerald-200'
                                          : 'bg-red-900/40 border border-red-700/50 text-red-200'
                                      }`}
                                    >
                                      {rowError}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Archive Lifecycle Project Phase 1 (2026-10-16) — Smart-restore
              confirmation modal. Shown when admin clicks Restore on an archive
              row whose fingerprint collides with a live user. Admin confirms
              before the auto-merge executes. Rendered as a sibling of the
              Recently Removed modal so it layers on top (z-[80]). */}
          {smartRestoreConfirm && (
            <>
              <div
                className="fixed inset-0 z-[80] bg-black bg-opacity-85"
                onClick={() => setSmartRestoreConfirm(null)}
                data-testid="smart-restore-confirm-backdrop"
              />
              <div
                className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
                data-testid="smart-restore-confirm-modal"
              >
                <div className="bg-gray-800 border border-indigo-600 rounded-lg max-w-md w-full p-5 pointer-events-auto shadow-2xl">
                  <div className="text-white font-semibold mb-2 flex items-center gap-2">
                    <span className="text-indigo-300">🔗</span>
                    Confirm Auto-Merge
                  </div>
                  <div className="text-gray-300 text-sm leading-relaxed mb-4">
                    Archived user{' '}
                    <span className="text-white font-semibold">
                      {smartRestoreConfirm.userName}
                    </span>{' '}
                    shares a device fingerprint with the current live user{' '}
                    <span className="text-white font-semibold">
                      {smartRestoreConfirm.liveUserName}
                    </span>.
                    <div className="mt-2 text-gray-400 text-xs">
                      Restoring will merge their song history and aliases into{' '}
                      <span className="text-white">{smartRestoreConfirm.liveUserName}</span>'s
                      live profile. Original join date and admin flags preserved.
                      Fresh session data (turn count, current name) kept from the live profile.
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      data-testid="smart-restore-confirm-cancel-btn"
                      onClick={() => setSmartRestoreConfirm(null)}
                      className="px-3 py-1.5 rounded text-sm font-semibold bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="smart-restore-confirm-ok-btn"
                      onClick={() => {
                        const { userId, userName } = smartRestoreConfirm;
                        setSmartRestoreConfirm(null);
                        handleRestoreArchivedUser(userId, userName);
                      }}
                      className="px-3 py-1.5 rounded text-sm font-semibold bg-indigo-700 hover:bg-indigo-600 text-white transition-colors"
                    >
                      🔗 Confirm Merge
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Merge picker — visual twin of the main Manage Users view
              (2026-10-07). Shows ALL users (post-migration + admin regulars
              + fallbacks) in the main section with a collapsible legacy
              dropdown at the bottom. Merge-mode indicators: indigo border
              on modal, prominent indigo banner at top, distinct title. */}
          {mergePickerOpen && (
            <>
              <div
                className="fixed inset-0 z-[60] bg-black bg-opacity-80"
                onClick={handleCloseMergePicker}
              />
              <div
                className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none"
                data-testid="merge-picker-modal"
              >
                <div className="bg-gray-800 border-2 border-indigo-500 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col pointer-events-auto">
                  <div className="flex justify-between items-center p-4 border-b border-indigo-700/60 bg-indigo-950/40 rounded-t-xl">
                    <h3 className="text-white font-bold text-lg">🔗 Merge Users</h3>
                    <button
                      data-testid="merge-picker-close-btn"
                      onClick={handleCloseMergePicker}
                      className="text-gray-400 hover:text-white text-xl leading-none"
                    >×</button>
                  </div>
                  {/* Merge-mode banner — persistent indicator so admin always
                      knows which identity they're merging INTO as they scan
                      the list. */}
                  <div
                    data-testid="merge-picker-mode-banner"
                    className="bg-indigo-900/60 border-b border-indigo-700/60 px-4 py-2.5 text-sm"
                  >
                    <span className="text-indigo-200 font-semibold">🔗 MERGE MODE</span>
                    <span className="text-gray-200 ml-2">— Tap any user to merge INTO:</span>
                    <span className="text-white font-semibold ml-1">
                      {selectedUser?.admin_name || selectedUser?.name || '—'}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    {(() => {
                      // Mirror of the main-view two-section layout. Backend's
                      // cohort=new query already includes ALL admin regulars
                      // and ALL fallbacks via an OR clause, so `users` has
                      // everything we want in the main section. Legacy
                      // section is passkey-only pre-migration users.
                      // Self-exclude the current survivor from both lists so
                      // admin can't accidentally pick themselves.
                      const displayedMainUsers = (users || []).filter(
                        (u) => !selectedUser || u.id !== selectedUser.id
                      );
                      const displayedLegacyUsers = (legacyUsers || []).filter(
                        (u) =>
                          u.category === 'passkey' &&
                          (!selectedUser || u.id !== selectedUser.id)
                      );
                      return (
                        <>
                          <input
                            data-testid="merge-picker-search-input"
                            type="text"
                            value={legacySearch}
                            onChange={handleLegacySearchChange}
                            placeholder="Search users to merge with…"
                            className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 mb-3"
                          />
                          {/* ── Main user list — mirror of main view ── */}
                          {(() => {
                            if (loadingUsers || displayedMainUsers.length === 0) return null;
                            const passkeyCount = displayedMainUsers.filter((x) => x.category === 'passkey').length;
                            const regularCount = displayedMainUsers.filter((x) => x.category === 'admin_regular').length;
                            const isFiltered = legacySearch.trim().length > 0;
                            const label = isFiltered ? 'Matches' : 'Users';
                            return (
                              <div
                                data-testid="merge-picker-counter"
                                className="flex items-center justify-between text-xs mb-2 px-1"
                              >
                                <div className="text-gray-300">
                                  <span className="font-semibold text-white">
                                    {label} ({displayedMainUsers.length})
                                  </span>
                                  <span className="text-gray-500 ml-2">
                                    {passkeyCount} Passkey · {regularCount} Admin
                                  </span>
                                </div>
                                {isFiltered && (
                                  <button
                                    type="button"
                                    data-testid="merge-picker-clear-search"
                                    onClick={() => {
                                      setLegacySearch('');
                                      Promise.all([fetchUsers(''), fetchLegacyUsers('')]);
                                    }}
                                    className="text-gray-400 hover:text-white text-xs underline"
                                  >
                                    Clear search
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                          {(() => {
                            if (loadingUsers) {
                              return <div className="text-gray-400 text-sm">Loading users…</div>;
                            }
                            if (displayedMainUsers.length === 0) {
                              const emptyMsg = legacySearch
                                ? 'No users match this search.'
                                : 'No users available to merge with.';
                              return (
                                <div className="text-gray-500 text-sm text-center py-8">
                                  {emptyMsg}
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-1" data-testid="merge-picker-list">
                                {displayedMainUsers.map((u) => {
                                  const bd = categoryBadge(u.category);
                                  const primary = u.admin_name || u.name;
                                  const secondary = u.admin_name ? u.name : null;
                                  return (
                                    <button
                                      key={u.id}
                                      type="button"
                                      data-testid={`merge-picker-row-${u.id}`}
                                      onClick={() => handleSelectLegacy(u)}
                                      className="w-full text-left px-3 py-2 rounded hover:bg-gray-700 border border-gray-700 hover:border-indigo-400 transition-all"
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-white font-semibold truncate">{primary}</span>
                                            <span
                                              data-testid={`merge-picker-row-badge-${u.id}`}
                                              className={`text-xs px-1.5 py-0.5 rounded ${bd.cls}`}
                                            >
                                              {bd.text}
                                            </span>
                                          </div>
                                          {secondary && (
                                            <div className="text-xs text-gray-400 mt-0.5 truncate">
                                              Display name: <span className="text-gray-300">{secondary}</span>
                                            </div>
                                          )}
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {u.lifetime_song_count} song{u.lifetime_song_count === 1 ? '' : 's'}
                                            {u.alias_count > 0 && (
                                              <> · {u.alias_count} alias{u.alias_count === 1 ? '' : 'es'}</>
                                            )}
                                            {u.last_sang_at && (
                                              <> · last sang {new Date(u.last_sang_at).toLocaleDateString()}</>
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-gray-500 text-lg">›</div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {/* ── Legacy Users (expandable, passkey-only) ──
                              Mirror of the main view's legacy dropdown.
                              Independent expand state from the main view
                              (`mergeLegacyExpanded`). */}
                          <div className="mt-4 border-t border-gray-700 pt-4">
                            <button
                              type="button"
                              data-testid="merge-picker-legacy-toggle"
                              onClick={() => setMergeLegacyExpanded(v => !v)}
                              className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                            >
                              <span>
                                <span className="inline-block px-1.5 py-0.5 rounded text-xs mr-2 bg-purple-800/70 text-purple-100">
                                  Legacy
                                </span>
                                Legacy Users{displayedLegacyUsers.length > 0 ? ` (${displayedLegacyUsers.length})` : ''}
                              </span>
                              <span className="text-gray-300">{mergeLegacyExpanded ? '▲' : '▼'}</span>
                            </button>

                            {mergeLegacyExpanded && (
                              <div className="mt-3 space-y-1" data-testid="merge-picker-legacy-panel">
                                {loadingLegacy && (
                                  <div className="text-gray-400 text-sm">Loading legacy users…</div>
                                )}
                                {!loadingLegacy && displayedLegacyUsers.length === 0 && (
                                  <div className="text-gray-500 text-sm text-center py-4">
                                    {legacySearch
                                      ? 'No legacy users match this search.'
                                      : 'No legacy users available to merge with.'}
                                  </div>
                                )}
                                {!loadingLegacy && displayedLegacyUsers.map((u) => {
                                  const bd = legacyBadge();
                                  const primary = u.admin_name || u.name;
                                  const secondary = u.admin_name ? u.name : null;
                                  return (
                                    <button
                                      key={u.id}
                                      type="button"
                                      data-testid={`merge-picker-row-${u.id}`}
                                      onClick={() => handleSelectLegacy(u)}
                                      className="w-full text-left px-3 py-2 rounded hover:bg-gray-700 border border-gray-700 hover:border-indigo-400 transition-all"
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-white font-semibold truncate">{primary}</span>
                                            <span
                                              data-testid={`merge-picker-row-badge-${u.id}`}
                                              className={`text-xs px-1.5 py-0.5 rounded ${bd.cls}`}
                                            >
                                              {bd.text}
                                            </span>
                                          </div>
                                          {secondary && (
                                            <div className="text-xs text-gray-400 mt-0.5 truncate">
                                              Display name: <span className="text-gray-300">{secondary}</span>
                                            </div>
                                          )}
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {u.lifetime_song_count} song{u.lifetime_song_count === 1 ? '' : 's'}
                                            {u.alias_count > 0 && (
                                              <> · {u.alias_count} alias{u.alias_count === 1 ? '' : 'es'}</>
                                            )}
                                            {u.last_sang_at && (
                                              <> · last sang {new Date(u.last_sang_at).toLocaleDateString()}</>
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-gray-500 text-lg">›</div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Merge confirmation preview modal */}
          {mergeCandidate && selectedUser && (
            <>
              <div
                className="fixed inset-0 z-[80] bg-black bg-opacity-90"
                onClick={() => setMergeCandidate(null)}
              />
              <div
                className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
                data-testid="merge-confirm-modal"
              >
                <div className="bg-gray-800 border-2 border-indigo-500 rounded-xl shadow-2xl w-full max-w-lg pointer-events-auto">
                  <div className="p-5 border-b border-gray-700">
                    <h3 className="text-white font-bold text-lg">Confirm Merge</h3>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="bg-gray-900/70 rounded p-3 text-sm">
                      <div className="text-xs text-indigo-300 uppercase tracking-wide font-semibold mb-1">Merging into (new)</div>
                      <div className="text-white font-semibold">{selectedUser.admin_name || selectedUser.name}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Keeps: passkey credential, current session position, ID
                      </div>
                    </div>
                    <div className="bg-gray-900/70 rounded p-3 text-sm">
                      <div className="text-xs text-amber-300 uppercase tracking-wide font-semibold mb-1">Importing from (legacy)</div>
                      <div className="text-white font-semibold">{mergeCandidate.admin_name || mergeCandidate.name}</div>
                      <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                        <div>• {(mergeCandidate.song_history || []).length} songs in history</div>
                        <div>• {(mergeCandidate.name_history || []).length} previous aliases</div>
                        <div>• {mergeCandidate.total_turns || 0} total turns</div>
                        {mergeCandidate.admin_name && (
                          <div>• admin_name: <span className="text-gray-200">"{mergeCandidate.admin_name}"</span></div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 border-t border-gray-700 pt-3">
                      Any active queue entry belonging to the legacy user will be reassigned to the new user — <span className="text-white font-semibold">position preserved</span>.
                      Legacy record archived for 30 days.
                    </div>

                    {/* Risky-direction warnings (2026-09-21 Manage Users widening) —
                        the merge endpoint is category-agnostic and directional:
                        whatever's picked as "legacy" is archived+deleted. If the
                        consumed side has assets the survivor lacks, they're lost.
                        Warn admin so they can flip direction if needed. */}
                    {mergeCandidate.category === 'passkey' && (
                      <div data-testid="merge-warning-passkey-loss" className="bg-red-900/40 border-2 border-red-500 rounded p-3 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="text-lg leading-none">⚠️</span>
                          <div>
                            <div className="text-red-100 font-semibold mb-1">Passkey credential will be discarded</div>
                            <div className="text-red-200 text-xs">
                              The legacy user is a passkey user. Their passkey credential is <span className="font-semibold">not inherited</span> — it will be archived and deleted, and that iCloud/platform passkey will become orphaned. Only proceed if you're sure this is the correct direction.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {mergeCandidate.category === 'admin_regular' && selectedUser.category !== 'admin_regular' && (
                      <div data-testid="merge-warning-admin-regular-loss" className="bg-amber-900/40 border-2 border-amber-500 rounded p-3 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="text-lg leading-none">⚠️</span>
                          <div>
                            <div className="text-amber-100 font-semibold mb-1">Admin Regular flag will be lost</div>
                            <div className="text-amber-200 text-xs">
                              The legacy user is an Admin Regular. Their <code className="bg-amber-950/50 px-1 rounded">is_admin_managed_regular</code> status is <span className="font-semibold">not inherited</span>. The merged identity will keep the survivor's category ({selectedUser.category || 'other'}). Consider flipping the direction if you want the Admin Regular record to survive.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="text-red-400 text-sm">{error}</div>
                    )}
                  </div>
                  <div className="p-4 border-t border-gray-700 flex gap-3">
                    <button
                      type="button"
                      data-testid="merge-confirm-cancel-btn"
                      onClick={() => setMergeCandidate(null)}
                      disabled={merging}
                      className="flex-1 px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="merge-confirm-submit-btn"
                      onClick={handleConfirmMerge}
                      disabled={merging}
                      className="flex-1 px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-semibold disabled:opacity-50"
                    >
                      {merging ? 'Merging…' : 'Confirm Merge'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>,
        document.body
      )}
    </>
  );
};

// Admin Auth Reset Button - Requires long press to reset all user authentication data
// Toggle to show/hide the Auth Reset button in admin panel
const SHOW_AUTH_RESET_BUTTON = false; // Set to true to enable the button

// One-time Recovery button for the 2026-09-20 Compass prefix bug.
// Flip to `true` when you need to run recovery, then flip back after.
const SHOW_DB_RECOVER_PREFIXED = false;

const AdminAuthResetButton = () => {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const holdTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const HOLD_DURATION = 5000; // 5 seconds

  const handleStart = () => {
    setIsHolding(true);
    setProgress(0);
    
    // Progress animation
    const startTime = Date.now();
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setProgress(newProgress);
    }, 50);
    
    // Action after hold duration
    holdTimerRef.current = setTimeout(() => {
      // Clear all auth data for all users
      passkeyUtils.clearAllAuthData();
      alert('Authentication data cleared. All users will need to re-authenticate.');
      window.location.reload();
    }, HOLD_DURATION);
  };

  const handleEnd = () => {
    setIsHolding(false);
    setProgress(0);
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  };

  return (
    <div className="mt-8 pt-4 border-t border-gray-700">
      <p className="text-xs text-gray-500 mb-2 text-center">
        Force all users to re-authenticate (hold for 5 seconds)
      </p>
      <button
        onMouseDown={handleStart}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchEnd={handleEnd}
        className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-400 rounded text-sm font-medium transition-colors relative overflow-hidden"
      >
        {isHolding && (
          <div 
            className="absolute inset-0 bg-red-600 opacity-30"
            style={{ width: `${progress}%`, transition: 'width 50ms linear' }}
          />
        )}
        <span className="relative z-10">
          {isHolding ? `Hold... ${Math.ceil((100 - progress) / 20)}s` : '🔐 Reset All User Authentication'}
        </span>
      </button>
    </div>
  );
};

const AddToQueueForm = ({ onAddToQueue, error, setError, success }) => {
  const [userName, setUserName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const searchTimeoutRef = useRef(null);
  // ── Admin Regulars feature (2026-09-17) ─────────────────────────
  // Two additions to the user-name flow:
  //   1) Save as Regular checkbox — persists this user across sessions.
  //   2) Search-mode toggle — flips the name field into autocomplete mode
  //      against `GET /api/admin/search-users`, showing both passkey
  //      users AND persistent admin regulars.
  const [saveAsRegular, setSaveAsRegular] = useState(false);
  const [nameSearchMode, setNameSearchMode] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const userSearchTimeoutRef = useRef(null);
  // ── Admin add: song-history dropdown for selected existing user (2026-09-24) ──
  // When admin picks a passkey user or admin regular from the search picker,
  // surface that user's song history so the admin can pick a repeat song
  // in one click. Mirrors the user-side and edit-modal dropdowns.
  const [addHistoryResults, setAddHistoryResults] = useState([]);
  const [showAddHistoryDropdown, setShowAddHistoryDropdown] = useState(false);
  const [isFetchingAddHistory, setIsFetchingAddHistory] = useState(false);

  const searchUsers = async (query) => {
    setIsSearchingUsers(true);
    try {
      // limit=500 returns every persistent user (well over our current
      // ~180 users incl. legacy cohort). Alphabetical sort is applied
      // server-side to match the Manage Users list ordering.
      const response = await axios.get(`${API}/admin/search-users?q=${encodeURIComponent(query || '')}&limit=500`);
      setUserSearchResults(response.data?.results || []);
      setShowUserDropdown(true);
    } catch (err) {
      console.error('User search error:', err);
      setUserSearchResults([]);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleUserNameChange = (e) => {
    const value = e.target.value;
    setUserName(value);
    setSelectedExistingUser(null);
    if (nameSearchMode) {
      if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
      userSearchTimeoutRef.current = setTimeout(() => searchUsers(value), 250);
    }
  };

  const handleSelectExistingUser = (u) => {
    setSelectedExistingUser(u);
    setUserName(u.name);
    setShowUserDropdown(false);
    // Reset any prior history dropdown state; fresh fetch happens on demand.
    setShowAddHistoryDropdown(false);
    setAddHistoryResults([]);
  };

  // Toggle the song-history dropdown for the currently-selected existing user.
  // First open triggers a fetch against `/api/song-history?device_fingerprint=…`
  // (fingerprint is already surfaced in the search-users response).
  const toggleAddHistoryDropdown = async () => {
    if (showAddHistoryDropdown) {
      setShowAddHistoryDropdown(false);
      return;
    }
    const fp = selectedExistingUser && selectedExistingUser.device_fingerprint;
    if (!fp) return;
    setIsFetchingAddHistory(true);
    try {
      const response = await axios.get(
        `${API}/song-history?device_fingerprint=${encodeURIComponent(fp)}&limit=200`
      );
      setAddHistoryResults(Array.isArray(response.data) ? response.data : []);
      setShowAddHistoryDropdown(true);
    } catch (err) {
      console.error('Admin add-form history fetch error:', err);
      setAddHistoryResults([]);
      setShowAddHistoryDropdown(true);
    } finally {
      setIsFetchingAddHistory(false);
    }
  };

  const handleAddSelectHistorySong = (h) => {
    handleSelectSong({
      id: h.song_id ? String(h.song_id) : undefined,
      title: h.song_title,
      artist: h.song_artist,
    });
    setShowAddHistoryDropdown(false);
  };

  // ── Admin add-form history delete (long-press → confirm → DELETE) ───
  // Selected user is a real user (passkey or admin regular) — we have
  // their device_fingerprint directly from the search-users response,
  // so use the user-scoped endpoint.
  const [addHistoryPendingDelete, setAddHistoryPendingDelete] = useState(null);
  const [isDeletingAddHistory, setIsDeletingAddHistory] = useState(false);

  const handleAddHistoryLongPress = (row) => {
    setAddHistoryPendingDelete(row);
  };

  const handleConfirmDeleteAddHistory = async () => {
    const fp = selectedExistingUser && selectedExistingUser.device_fingerprint;
    if (!addHistoryPendingDelete || !fp) return;
    setIsDeletingAddHistory(true);
    try {
      await axios.delete(
        `${API}/song-history/entry?device_fingerprint=${encodeURIComponent(fp)}`,
        {
          data: {
            song_id: addHistoryPendingDelete.song_id ?? null,
            song_title: addHistoryPendingDelete.song_title,
            song_artist: addHistoryPendingDelete.song_artist,
          },
        }
      );
      setAddHistoryResults((prev) => prev.filter((r) => (
        (r.song_id ?? null) !== (addHistoryPendingDelete.song_id ?? null) ||
        r.song_title !== addHistoryPendingDelete.song_title ||
        r.song_artist !== addHistoryPendingDelete.song_artist
      )));
    } catch (err) {
      console.error('Admin add-form history delete error:', err);
    } finally {
      setIsDeletingAddHistory(false);
      setAddHistoryPendingDelete(null);
    }
  };

  const toggleNameSearchMode = () => {
    const next = !nameSearchMode;
    setNameSearchMode(next);
    setSelectedExistingUser(null);
    setShowAddHistoryDropdown(false);
    setAddHistoryResults([]);
    if (next) {
      // Entering search mode: kick off an empty search to show recent regulars.
      searchUsers(userName);
    } else {
      setShowUserDropdown(false);
      setUserSearchResults([]);
    }
  };

  // Format "last sang X ago" text for search results.
  const fmtLastSang = (iso) => {
    if (!iso) return 'never sung';
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (days === 0) return 'today';
      if (days === 1) return 'yesterday';
      if (days < 7) return `${days}d ago`;
      if (days < 30) return `${Math.floor(days / 7)}w ago`;
      if (days < 365) return `${Math.floor(days / 30)}mo ago`;
      return `${Math.floor(days / 365)}y ago`;
    } catch (_e) {
      return '';
    }
  };

  // Search songs with debouncing
  const searchSongs = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/songs/search?q=${encodeURIComponent(query)}&limit=50`);
      setSearchResults(response.data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedSong(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchSongs(query);
    }, 300);
  };

  const handleSelectSong = (song) => {
    setSelectedSong(song);
    setSearchQuery(`${song.title} - ${song.artist}`);
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userName.trim() || !selectedSong) return;

    setIsAdding(true);
    try {
      // Admin Regulars feature (2026-09-17): three fingerprint paths.
      //   1) An existing user was selected from the search picker →
      //      pass existing_user_id, backend reuses their fingerprint.
      //   2) "Save as Regular" is checked → backend generates a UUID +
      //      creates a persistent user with is_admin_managed_regular=True.
      //   3) Default → backend generates an ephemeral admin_system_*
      //      fingerprint (current behavior).
      const payload = {
        user_name: userName.trim(),
        song_title: selectedSong.title,
        song_artist: selectedSong.artist,
      };
      // 2026-10-14 — forward KaraFun catalog song_id when available
      // (always set after a catalog pick or history-row select).
      if (selectedSong.id !== undefined && selectedSong.id !== null && selectedSong.id !== '') {
        payload.song_id = selectedSong.id;
      }
      if (selectedExistingUser) {
        payload.existing_user_id = selectedExistingUser.id;
      } else if (saveAsRegular) {
        payload.save_as_regular = true;
      }

      await onAddToQueue(payload);

      // Reset form
      setUserName('');
      setSearchQuery('');
      setSelectedSong(null);
      setSearchResults([]);
      setSaveAsRegular(false);
      setSelectedExistingUser(null);
      setNameSearchMode(false);
      setUserSearchResults([]);
      setShowUserDropdown(false);
      setShowAddHistoryDropdown(false);
      setAddHistoryResults([]);
    } finally {
      setIsAdding(false);
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
    };
  }, []);

  return (
    <div className="bg-blue-900/20 border border-blue-400 rounded-lg p-4 mb-4 relative">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="mb-2 relative">
          <label className="block text-base font-medium text-white mb-2">
            {nameSearchMode ? 'Search Saved Users' : 'User Name'}
          </label>
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={userName}
              onChange={handleUserNameChange}
              onFocus={() => nameSearchMode && searchUsers(userName)}
              placeholder={nameSearchMode ? 'Search admin users and passkey users...' : 'Enter User Name'}
              className="form-input flex-1 min-w-0 p-2 text-sm rounded"
              required
              data-testid="admin-add-user-name-input"
            />
            <button
              type="button"
              onClick={toggleNameSearchMode}
              className={`px-3 py-1 rounded text-sm font-semibold transition-all flex-shrink-0 ${
                nameSearchMode
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              }`}
              title={nameSearchMode ? 'Exit search mode' : 'Search saved users (admin users, and passkey users)'}
              data-testid="admin-add-user-search-toggle"
            >
              {nameSearchMode ? '✕' : '🔍'}
            </button>
          </div>

          {/* User search dropdown — shown in search mode when results exist */}
          {nameSearchMode && showUserDropdown && (
            <div
              className="absolute z-30 mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-[28rem] overflow-y-auto"
              data-testid="admin-add-user-search-dropdown"
            >
              {isSearchingUsers && (
                <div className="p-3 text-center text-gray-400 text-sm">Searching…</div>
              )}
              {!isSearchingUsers && userSearchResults.length === 0 && (
                <div className="p-3 text-center text-gray-400 text-sm">
                  No matching users. Toggle search off to add a new person.
                </div>
              )}
              {!isSearchingUsers && userSearchResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => handleSelectExistingUser(u)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-700 border-b border-gray-700 last:border-b-0"
                  data-testid={`admin-add-user-search-result-${u.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">{u.admin_name || u.name}</span>
                    {u.is_admin_managed_regular && (
                      <span className="text-xs bg-amber-700 text-amber-100 px-1.5 py-0.5 rounded">
                        Admin
                      </span>
                    )}
                  </div>
                  {u.admin_name && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      Display name: <span className="text-gray-300">{u.name}</span>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-0.5">
                    {u.lifetime_song_count > 0
                      ? `${u.lifetime_song_count} song${u.lifetime_song_count === 1 ? '' : 's'}${u.last_sang_at ? ` · last sang ${fmtLastSang(u.last_sang_at)}` : ''}`
                      : 'No songs yet'}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Save as Regular checkbox — visible only in default (non-search) mode */}
          {!nameSearchMode && !selectedExistingUser && (
            <label className="flex items-center gap-2 mt-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={saveAsRegular}
                onChange={(e) => setSaveAsRegular(e.target.checked)}
                className="w-4 h-4"
                data-testid="admin-add-save-as-regular-checkbox"
              />
              <span>
                Save as Regular
                <span className="text-gray-500 ml-1">
                  — persists across sessions with song history
                </span>
              </span>
            </label>
          )}

          {/* When an existing user is selected, show a small confirmation chip */}
          {selectedExistingUser && (
            <div className="mt-2 text-xs text-gray-300 flex items-center gap-2">
              <span className="bg-green-900/50 border border-green-500/50 text-green-100 px-2 py-1 rounded">
                ✓ Using saved user
                {selectedExistingUser.is_admin_managed_regular ? ' (Regular)' : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedExistingUser(null);
                  setUserName('');
                  setShowAddHistoryDropdown(false);
                  setAddHistoryResults([]);
                }}
                className="text-gray-400 hover:text-gray-200 underline"
              >
                clear
              </button>
            </div>
          )}

          {/* Song history dropdown for selected existing user (2026-09-24).
              Only renders when an existing user is selected (search-mode pick).
              Mirrors the user-side and admin-edit dropdowns. */}
          {selectedExistingUser && selectedExistingUser.device_fingerprint && (
            <div className="relative mt-2">
              <button
                type="button"
                data-testid="admin-add-open-history-dropdown"
                onClick={toggleAddHistoryDropdown}
                disabled={isFetchingAddHistory}
                className={`w-full text-left px-3 py-2 bg-purple-700/60 border border-purple-500 rounded-lg text-sm text-white hover:bg-purple-600 transition-colors flex items-center justify-between ${showAddHistoryDropdown ? 'relative z-30' : ''}`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">🎤</span>
                  {isFetchingAddHistory
                    ? 'Loading history…'
                    : `Select a song from ${selectedExistingUser.name}'s history`}
                </span>
                <span aria-hidden="true" className="text-purple-200">▾</span>
              </button>

              {showAddHistoryDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowAddHistoryDropdown(false);
                    }}
                  />
                  <div
                    data-testid="admin-add-history-dropdown"
                    className="absolute z-20 w-full mt-1 bg-gray-800 border-2 border-gray-600 rounded-lg shadow-2xl max-h-[28rem] overflow-y-auto"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {addHistoryResults.length === 0 ? (
                      <div className="p-3 text-center text-gray-400 text-sm">
                        No history for this user yet.
                      </div>
                    ) : (
                      addHistoryResults.map((h, index) => (
                        <HistoryDropdownRow
                          key={`${h.song_id ?? h.song_title}-${index}`}
                          item={h}
                          index={index}
                          testIdPrefix="admin-add-history-item"
                          onSelect={handleAddSelectHistorySong}
                          onLongPress={handleAddHistoryLongPress}
                          className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                        />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <HistoryDeleteConfirmModal
            item={addHistoryPendingDelete}
            subjectLabel={`${(selectedExistingUser && selectedExistingUser.name) || 'this user'}'s`}
            isSubmitting={isDeletingAddHistory}
            onConfirm={handleConfirmDeleteAddHistory}
            onCancel={() => setAddHistoryPendingDelete(null)}
          />
        </div>
        
        <div className="mb-2 relative">
          <label className="block text-base font-medium text-white mb-2">Search for a Song</label>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            placeholder="Enter Song Title or Artist Name"
            className="form-input w-full p-2 text-sm rounded"
            autoComplete="off"
          />
          
          {isSearching && (
            <div className="absolute right-2 top-8 text-xs text-gray-400">
              Searching...
            </div>
          )}

          {/* Dropdown Results */}
          {showDropdown && searchResults.length > 0 && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                className="fixed inset-0 z-10"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowDropdown(false);
                }}
              />
              
              {/* Results dropdown */}
              <div 
                className="absolute z-20 w-full mt-1 bg-gray-800 border-2 border-gray-600 rounded-lg shadow-2xl max-h-[28rem] overflow-y-auto"
                onMouseDown={(e) => e.stopPropagation()}
              >
                {searchResults.map((song, index) => (
                  <div
                    key={`${song.id}-${index}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectSong(song);
                    }}
                    className="p-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                  >
                    <div className="font-semibold text-white text-sm">{song.title}</div>
                    <div className="text-xs text-gray-400">{song.artist}</div>
                    {song.year && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {song.year} {song.styles && `• ${song.styles}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {selectedSong && (
            <div className="mt-2 p-2 bg-green-900/30 border border-green-500 rounded text-xs">
              <div className="text-green-500">✓ Selected:</div>
              <div className="font-semibold text-white">{selectedSong.title}</div>
              <div className="text-gray-300">{selectedSong.artist}</div>
            </div>
          )}
        </div>
        
        <button
          type="submit"
          disabled={!userName.trim() || !selectedSong || isAdding}
          className="btn btn-primary w-full py-2 rounded text-sm disabled:bg-gray-600"
        >
          {isAdding ? 'Adding...' : 'Add to Queue'}
        </button>
      </form>
      
      {/* Error Message - Overlay on Form */}
      {error && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 rounded-lg">
          <div className="message-error message-enter p-4 rounded-lg border max-w-lg w-full">
            <div className="flex justify-between items-start">
              <span>{error}</span>
              <button 
                onClick={() => setError('')}
                className="ml-4 px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white font-semibold flex-shrink-0"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Success Message - Overlay on Form */}
      {success && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 rounded-lg">
          <div className="message-success message-enter p-4 rounded-lg border max-w-lg w-full">
            {success}
          </div>
        </div>
      )}
    </div>
  );
};

const QueueEntryWithMenu = ({ entry, index, queueLength, inGroup = false, onMoveToPosition, onMarkPlayed, onRemove, onEdit, onConvertToRegular, onSetInsertPoint, insertPointEntryId, movingEntryId, onStartMove, onSelectMoveTarget, swappingEntryId, onStartSwap, onSelectSwapTarget, linkingEntryId, linkingAnchorGroupId, onStartLink, onSelectLinkTarget, selectedLinkTargets, onUnlink, groupsEnabled = false, onSendToKarafun, karafunSyncActive, batchSelectMode = false, batchSelectedIds, batchAnchorGroupId = null, onBatchToggle, onBatchLongPress, onLinkLongPressConfirm }) => {
  const [showMenu, setShowMenu] = useState(false);
  // Admin-name inline editor 2026-09-25 — expands inside this popover
  // so the admin can edit the real-name label without jumping into the
  // full Edit Entry modal. Mirrors the same UX contract as the modal's
  // inline editor (badge → click → input + Save/Cancel appear, Save
  // patches PATCH /admin/users/{user_id}). Uses local `localAdminName`
  // so the badge/pill re-renders instantly on save, without waiting
  // for the parent queue refresh.
  const [showAdminNameField, setShowAdminNameField] = useState(false);
  const [editAdminName, setEditAdminName] = useState('');
  const [savingAdminName, setSavingAdminName] = useState(false);
  const [adminNameError, setAdminNameError] = useState('');
  const [localAdminName, setLocalAdminName] = useState(entry.admin_name || null);

  // If the parent queue refresh brings a new admin_name (e.g. from a
  // different admin device editing it), sync our local view.
  useEffect(() => {
    setLocalAdminName(entry.admin_name || null);
  }, [entry.admin_name]);

  const handleSaveAdminName = async () => {
    if (!entry.user_id) return;
    setSavingAdminName(true);
    setAdminNameError('');
    try {
      const trimmed = (editAdminName || '').trim();
      const res = await axios.patch(
        `${API}/admin/users/${entry.user_id}`,
        { admin_name: trimmed }
      );
      const next = (res.data?.admin_name || '').trim() || null;
      setLocalAdminName(next);
      setEditAdminName(next || '');
      setShowAdminNameField(false);
    } catch (err) {
      setAdminNameError(err.response?.data?.detail || err.message || 'Failed to save admin name');
    } finally {
      setSavingAdminName(false);
    }
  };
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const holdTimeoutRef = useRef(null);
  const entryRef = useRef(null);
  const touchStartRef = useRef(null);
  const hasMoved = useRef(false);
  
  const isInsertPoint = insertPointEntryId === entry.id;
  // Honor feature flag — when off, treat entries as ungrouped everywhere.
  const effectiveGroupId = groupsEnabled ? entry.group_id : null;
  const isMoveModeActive = !!movingEntryId;
  const isBeingMoved = movingEntryId === entry.id;
  const isSelectableMoveTarget = isMoveModeActive && !isBeingMoved;

  const isSwapModeActive = !!swappingEntryId;
  const isBeingSwapped = swappingEntryId === entry.id;
  // Disallow swapping into/out of a group — grouped entries are NOT valid swap targets
  const isSelectableSwapTarget = isSwapModeActive && !isBeingSwapped && !effectiveGroupId;
  const isUnavailableSwapTarget = isSwapModeActive && !isBeingSwapped && !!effectiveGroupId;

  const isLinkModeActive = groupsEnabled && !!linkingEntryId;
  const isBeingLinked = linkingEntryId === entry.id;
  // When anchor is in a group, OTHER entries that are in a DIFFERENT group are
  // not selectable (cross-group is rejected). Same-group members are also not
  // useful tap targets, treat as unavailable for visual clarity.
  const isUnavailableLinkTarget = isLinkModeActive && !isBeingLinked && !!linkingAnchorGroupId
    && !!effectiveGroupId; // anchor grouped AND this entry grouped
  const isSelectableLinkTarget = isLinkModeActive && !isBeingLinked && !isUnavailableLinkTarget;
  const isSelectedLinkTarget = isLinkModeActive && selectedLinkTargets && selectedLinkTargets.has(entry.id);

  const isSelectionModeActive = isMoveModeActive || isSwapModeActive || isLinkModeActive;

  // Batch-select mode derived flags
  const isBatchSelectActive = !!batchSelectMode;
  const isBatchSelected = isBatchSelectActive && batchSelectedIds && batchSelectedIds.has(entry.id);
  // Unavailable: anchor group is locked AND this entry is in a different group.
  const isBatchUnavailable = (
    isBatchSelectActive
    && !!batchAnchorGroupId
    && !!effectiveGroupId
    && effectiveGroupId !== batchAnchorGroupId
  );

  const isInGroup = !!effectiveGroupId;

  // True only when admin has staged ≥1 target in per-entry Link Mode — at
  // that point, long-pressing ANY entry should open the confirm popover so
  // the admin doesn't have to scroll back up to the link banner.
  const canLinkLongPressConfirm = (
    isLinkModeActive
    && selectedLinkTargets
    && selectedLinkTargets.size > 0
  );

  const startHold = (e) => {
    // Move and Swap modes use the entry purely as a tap-target; no long-press.
    if (isMoveModeActive || isSwapModeActive) return;
    // Link mode with NO staged targets: still tap-only (long-press would have
    // nothing to confirm). Once ≥1 target is staged, long-press opens the
    // confirm popover below.
    if (isLinkModeActive && !canLinkLongPressConfirm) return;

    // Reset movement tracking
    hasMoved.current = false;
    
    // Record initial touch/mouse position
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    touchStartRef.current = { x: startX, y: startY };
    
    // Get position for menu - center it on screen
    const rect = entryRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Center the menu on screen
    setMenuPosition({ 
      x: Math.max(20, Math.min(viewportWidth / 2 - 200, rect.left)),
      y: Math.max(20, Math.min(viewportHeight / 2 - 150, rect.bottom + 5))
    });
    
    // Show menu after 800ms hold (increased from 500ms for better distinction)
    holdTimeoutRef.current = setTimeout(() => {
      // Only show menu if user hasn't moved significantly (not scrolling)
      if (!hasMoved.current) {
        if (isBatchSelectActive) {
          // In batch-select mode, long-press opens the GLOBAL action menu
          // instead of the per-entry menu. Position computed above is reused.
          if (onBatchLongPress) {
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;
            onBatchLongPress({
              x: Math.max(20, Math.min(viewportW / 2 - 160, rect.left)),
              y: Math.max(20, Math.min(viewportH / 2 - 120, rect.bottom + 5)),
            });
          }
        } else if (canLinkLongPressConfirm) {
          // In per-entry Link Mode with staged targets, long-press opens the
          // global confirm popover anywhere — admin doesn't have to scroll
          // back to the link banner at the top of the queue.
          if (onLinkLongPressConfirm) {
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;
            onLinkLongPressConfirm({
              x: Math.max(20, Math.min(viewportW / 2 - 160, rect.left)),
              y: Math.max(20, Math.min(viewportH / 2 - 120, rect.bottom + 5)),
            });
          }
        } else {
          setShowMenu(true);
        }
      }
    }, 800);
  };

  const handleMove = (e) => {
    // If no touch start recorded, ignore
    if (!touchStartRef.current) return;
    
    // Get current position
    const currentX = e.touches ? e.touches[0].clientX : e.clientX;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // Calculate distance moved
    const deltaX = Math.abs(currentX - touchStartRef.current.x);
    const deltaY = Math.abs(currentY - touchStartRef.current.y);
    
    // If moved more than 10px in any direction, consider it scrolling
    if (deltaX > 10 || deltaY > 10) {
      hasMoved.current = true;
      cancelHold();
    }
  };

  const cancelHold = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    touchStartRef.current = null;
  };

  const handleRemove = () => {
    setShowMenu(false);
    onRemove(entry.id);
  };

  const handleEdit = () => {
    setShowMenu(false);
    onEdit(entry);
  };

  const handleSetInsertPoint = () => {
    setShowMenu(false);
    onSetInsertPoint(entry.id);
  };

  const handleStartMove = () => {
    setShowMenu(false);
    if (isInGroup) {
      const ok = window.confirm("This entry is part of a group. Moving it will unlink it from the group. Continue?");
      if (!ok) return;
    }
    if (onStartMove) onStartMove(entry.id);
  };

  const handleStartSwap = () => {
    setShowMenu(false);
    if (onStartSwap) onStartSwap(entry.id);
  };

  const handleStartLink = () => {
    setShowMenu(false);
    if (onStartLink) onStartLink(entry.id);
  };

  const handleUnlink = () => {
    setShowMenu(false);
    if (onUnlink) onUnlink(entry.id);
  };

  const handleSendToKarafun = () => {
    setShowMenu(false);
    if (onSendToKarafun) onSendToKarafun(entry);
  };

  const handleEntryClick = () => {
    // Batch-select mode: tap toggles selection (or no-op if unavailable).
    if (isBatchSelectActive) {
      if (isBatchUnavailable) return;
      if (onBatchToggle) onBatchToggle(entry);
      return;
    }
    // In move mode: select as move target
    if (isSelectableMoveTarget && onSelectMoveTarget) {
      onSelectMoveTarget(entry);
      return;
    }
    // In swap mode: select as swap target (only if not grouped)
    if (isSelectableSwapTarget && onSelectSwapTarget) {
      onSelectSwapTarget(entry);
      return;
    }
    // In link mode: select as link target
    if (isSelectableLinkTarget && onSelectLinkTarget) {
      onSelectLinkTarget(entry);
    }
  };

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={entryRef}
        onClick={handleEntryClick}
        onMouseDown={startHold}
        onMouseMove={handleMove}
        onMouseUp={cancelHold}
        onMouseLeave={cancelHold}
        onTouchStart={startHold}
        onTouchMove={handleMove}
        onTouchEnd={cancelHold}
        onTouchCancel={cancelHold}
        data-testid={
          isBatchSelectActive
            ? (isBatchSelected
                ? `batch-selected-${entry.id}`
                : isBatchUnavailable
                ? `batch-unavailable-${entry.id}`
                : `batch-selectable-${entry.id}`)
            : isSelectableMoveTarget
            ? `move-target-${entry.id}`
            : isSelectableSwapTarget
            ? `swap-target-${entry.id}`
            : isSelectableLinkTarget
            ? `link-target-${entry.id}`
            : isUnavailableLinkTarget
            ? `link-target-unavailable-${entry.id}`
            : `queue-entry-${entry.id}`
        }
        className={`queue-entry p-4 rounded-lg border-l-4 ${inGroup ? 'mb-2 last:mb-0' : 'mb-3'} transition-all ${
          isBatchUnavailable || isUnavailableSwapTarget || isUnavailableLinkTarget ? 'cursor-not-allowed' : 'cursor-pointer'
        } ${
          isBatchSelected
            ? '!border-cyan-400 !bg-cyan-700/60 ring-2 ring-cyan-300'
            : isBatchUnavailable
            ? '!border-gray-600 !bg-gray-800/30 opacity-40'
            : isBatchSelectActive
            ? (entry.is_current
                ? 'border-green-500 bg-green-900/30'
                : entry.has_played
                ? '!bg-[#1a1a1a] [&_.font-semibold]:!text-gray-400 [&_.text-gray-300]:!text-gray-500 [&_.position-badge]:!bg-none [&_.position-badge]:!bg-gray-700 [&_.position-badge]:!text-gray-300'
                : entry.priority_score < 1000
                ? 'border-blue-400 bg-blue-900/30'
                : 'border-yellow-400 bg-yellow-900/30')
            : isBeingMoved
            ? '!border-orange-500 ring-2 ring-orange-400'
            : isBeingSwapped
            ? '!border-orange-500 ring-2 ring-orange-400'
            : isBeingLinked
            ? '!border-cyan-400 !bg-cyan-700/60 ring-2 ring-cyan-300'
            : isSelectedLinkTarget
            ? '!border-cyan-400 !bg-cyan-700/60 ring-2 ring-cyan-300'
            : isUnavailableSwapTarget
            ? 'border-gray-600 bg-gray-800/30 opacity-40'
            : isUnavailableLinkTarget
            ? 'border-gray-600 bg-gray-800/30 opacity-40'
            : isSelectableLinkTarget
            ? 'border-cyan-400 bg-gray-800/60 hover:bg-cyan-900/30 hover:ring-2 hover:ring-cyan-400'
            : entry.is_current
            ? 'border-green-500 bg-green-900/30'
            : entry.has_played
            ? '!bg-[#1a1a1a] [&_.font-semibold]:!text-gray-400 [&_.text-gray-300]:!text-gray-500 [&_.position-badge]:!bg-none [&_.position-badge]:!bg-gray-700 [&_.position-badge]:!text-gray-300'
            : entry.priority_score < 1000
            ? 'border-blue-400 bg-blue-900/30'
            : 'border-yellow-400 bg-yellow-900/30'
        } ${isInsertPoint && !isSelectionModeActive && !isBatchSelectActive ? 'ring-2 ring-emerald-500' : ''}`}
        title={
          isBatchUnavailable
            ? "This entry is in a different group — can't batch with the current selection."
            : isUnavailableSwapTarget
            ? "Grouped entries can't be swap targets"
            : undefined
        }
      >
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0 max-w-[55%]">
            <div className="font-semibold text-white flex items-center gap-2 flex-wrap">
              <span className="position-badge">{index + 1}</span>
              <span className="truncate">{entry.user_name}</span>
              {entry.using_fallback && <span className="text-orange-400 flex-shrink-0" title="User is in compatibility mode (no device lock or incompatible device)">⚠️</span>}
              {entry.is_admin_added && (
                <span
                  data-testid={`admin-added-badge-${entry.id}`}
                  className="inline-flex items-center text-slate-300 bg-slate-700/60 border border-slate-500/50 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wide flex-shrink-0"
                  title="Admin-added entry — won't auto-delete; update song manually"
                >
                  Admin
                </span>
              )}
              {entry.is_admin_managed_regular && (
                <span
                  data-testid={`regular-badge-${entry.id}`}
                  className="inline-flex items-center text-amber-100 bg-amber-700/40 border border-amber-500/50 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wide flex-shrink-0"
                  title="Admin-managed user — persists across sessions with song history"
                >
                  Admin
                </span>
              )}
              {entry.sent_to_karafun && (
                <span
                  data-testid={`sent-to-karafun-badge-${entry.id}`}
                  className="inline-flex items-center gap-1 text-purple-300 bg-purple-900/40 border border-purple-600/50 px-2 py-0.5 rounded-full text-xs flex-shrink-0"
                  title="This song has already been sent to KaraFun"
                >
                  <span aria-hidden="true">✓</span>
                  <span>In KaraFun</span>
                </span>
              )}
              {entry.add_failed && (
                <span
                  data-testid={`add-failed-badge-${entry.id}`}
                  className="inline-flex items-start gap-1.5 text-red-100 bg-red-900/60 border border-red-500/60 px-2.5 py-1 rounded-md text-xs leading-snug flex-shrink-0 max-w-full whitespace-normal"
                  role="alert"
                >
                  <span aria-hidden="true" className="mt-0.5">⚠</span>
                  <span>This song is currently unavailable. Please select another song or see host for assistance.</span>
                </span>
              )}
            </div>
            <div className="text-gray-300 mt-1 break-words">
              {entry.song_title} - {entry.song_artist}
            </div>
          </div>
          <div className="text-right flex flex-col items-end gap-1 flex-shrink-0">
            {entry.is_current && (
              <span className="text-green-500 font-semibold inline-flex items-center gap-1"><GiMusicalNotes className="text-blue-500" size={20} /> Current <GiMusicalNotes className="text-blue-500" size={20} /></span>
            )}
            {entry.has_played && (
              <span className="inline-flex items-center !text-white font-bold text-xs tracking-widest px-1.5 py-0.5 rounded bg-white/10 border border-white/20">
                PLAYED
              </span>
            )}
            {!entry.has_played && !entry.is_current && (entry.total_turns || 0) === 0 && (
              <span
                data-testid={`first-turn-badge-${entry.id}`}
                className="inline-flex flex-col sm:flex-row gap-y-0.5 sm:gap-x-1 items-center leading-none text-emerald-100 bg-emerald-700/50 border border-emerald-500/60 px-2 py-1 rounded font-bold text-xs tracking-widest uppercase flex-shrink-0"
                title="This singer hasn't sung yet tonight"
              >
                <span>First</span>
                <span>Turn</span>
              </span>
            )}
            {entry.total_turns > 0 && (
              <div className="text-white text-base italic">
                {entry.total_turns === 1 ? '1 previous turn' : `${entry.total_turns} previous turns`}
              </div>
            )}
          </div>
        </div>
        {/* Insert Point Indicator */}
        {isInsertPoint && (
          <div className="mt-2 text-emerald-400 text-sm flex items-center gap-1">
            <span>📍</span>
            <span>New entries will be added after this position</span>
          </div>
        )}
      </div>

      {/* Position selection menu */}
      {showMenu && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={() => setShowMenu(false)}
          />
          
          {/* Menu */}
          <div
            className="fixed z-50 bg-gray-800 border-2 border-gray-600 rounded-xl shadow-2xl p-6 max-h-96 overflow-y-auto"
            style={{ 
              left: `${menuPosition.x}px`, 
              top: `${menuPosition.y}px`,
              minWidth: '400px',
              maxWidth: '500px'
            }}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
              <span className="text-white font-bold text-lg">
                {entry.user_name}
              </span>
              {entry.user_id && !showAdminNameField && (
                localAdminName ? (
                  <button
                    type="button"
                    data-testid={`admin-name-badge-popover-${entry.id}`}
                    onClick={() => {
                      setEditAdminName(localAdminName || '');
                      setAdminNameError('');
                      setShowAdminNameField(true);
                    }}
                    title="Click to edit admin name"
                    className="inline-flex items-center gap-2 leading-none bg-emerald-700/40 border border-emerald-500/60 hover:bg-emerald-700/60 hover:border-emerald-400 text-emerald-100 px-3 py-1 rounded-lg text-base font-semibold transition-colors"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
                      Admin Name
                    </span>
                    <span className="text-white text-lg font-bold">
                      {localAdminName}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`admin-name-add-popover-${entry.id}`}
                    onClick={() => {
                      setEditAdminName('');
                      setAdminNameError('');
                      setShowAdminNameField(true);
                    }}
                    title="Click to add admin name"
                    className="inline-flex items-center gap-1.5 leading-none bg-emerald-900/30 border border-emerald-700/50 hover:bg-emerald-800/40 hover:border-emerald-500 text-emerald-300 hover:text-emerald-200 px-2.5 py-1 rounded-lg text-sm font-semibold transition-colors"
                  >
                    + Add admin name
                  </button>
                )
              )}
              {localAdminName && !showAdminNameField && (
                <span className="text-xs text-gray-500 italic">
                  (click badge to edit admin name)
                </span>
              )}
            </div>
            {/* Inline admin-name editor for the popover 2026-09-25 —
                mirrors the same shape as the modal's inline editor. */}
            {showAdminNameField && (
              <div
                data-testid={`admin-name-inline-editor-popover-${entry.id}`}
                className="bg-emerald-950/30 border border-emerald-600/50 rounded-lg p-3 mb-3"
              >
                <label className="block text-xs uppercase tracking-widest font-bold text-emerald-300 mb-2">
                  Admin Name <span className="text-gray-400 normal-case tracking-normal font-normal">(real-name label — admin-only)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    data-testid={`admin-name-input-popover-${entry.id}`}
                    value={editAdminName}
                    onChange={(e) => setEditAdminName(e.target.value)}
                    placeholder="e.g. Sarah (red hair)"
                    maxLength={80}
                    className="input flex-1 px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleSaveAdminName(); }
                      if (e.key === 'Escape') { setShowAdminNameField(false); setAdminNameError(''); }
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    data-testid={`admin-name-save-btn-popover-${entry.id}`}
                    disabled={savingAdminName || (editAdminName.trim() === (localAdminName || ''))}
                    onClick={handleSaveAdminName}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingAdminName ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-name-cancel-btn-popover-${entry.id}`}
                    disabled={savingAdminName}
                    onClick={() => { setShowAdminNameField(false); setAdminNameError(''); }}
                    className="bg-gray-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
                {localAdminName && (
                  <button
                    type="button"
                    data-testid={`admin-name-clear-btn-popover-${entry.id}`}
                    disabled={savingAdminName}
                    onClick={() => setEditAdminName('')}
                    className="text-xs text-gray-400 hover:text-white mt-2 underline"
                  >
                    Clear (removes the admin name label)
                  </button>
                )}
                {adminNameError && (
                  <div className="text-red-400 text-sm mt-2" data-testid={`admin-name-error-popover-${entry.id}`}>
                    {adminNameError}
                  </div>
                )}
              </div>
            )}
            <div className="text-gray-400 text-sm mb-4">
              {entry.song_title} - {entry.song_artist}
            </div>
            
            <div className="space-y-2 mb-4">
              <button
                onClick={handleEdit}
                className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-indigo-700 transition-all"
              >
                ✏️ Edit Entry
              </button>
              <button
                data-testid={`move-entry-btn-${entry.id}`}
                onClick={handleStartMove}
                className="w-full bg-orange-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-orange-500 transition-all"
              >
                ↕ Move Entry
              </button>
              {!isInGroup && (
                <button
                  data-testid={`swap-entry-btn-${entry.id}`}
                  onClick={handleStartSwap}
                  className="w-full bg-orange-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-orange-500 transition-all"
                >
                  ⇄ Swap with…
                </button>
              )}
              {groupsEnabled && (isInGroup ? (
                <button
                  data-testid={`unlink-entry-btn-${entry.id}`}
                  onClick={handleUnlink}
                  className="w-full bg-cyan-800 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-cyan-700 transition-all"
                >
                  🔗 Unlink from group
                </button>
              ) : (
                <button
                  data-testid={`link-entry-btn-${entry.id}`}
                  onClick={handleStartLink}
                  className="w-full bg-cyan-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-cyan-700 transition-all"
                >
                  🔗 Link with…
                </button>
              ))}
              <button
                onClick={handleSetInsertPoint}
                className={`w-full px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                  isInsertPoint 
                    ? 'bg-emerald-900 text-white cursor-default' 
                    : 'bg-emerald-800 text-white hover:bg-emerald-700'
                }`}
                disabled={isInsertPoint}
              >
                {isInsertPoint ? '📍 Insert Point Set Here' : '📍 Set Insert Point Here'}
              </button>
              {/* Convert to Regular — Admin Regulars feature (2026-09-17).
                  Visible only for ephemeral admin adds NOT at pos-1.
                  Placed just above Remove so it sits with the destructive-adjacent group. */}
              {entry.is_admin_added && !entry.is_admin_managed_regular && index > 0 && onConvertToRegular && (
                <button
                  data-testid={`convert-to-regular-btn-${entry.id}`}
                  onClick={async () => {
                    if (window.confirm(
                      `Convert ${entry.user_name} to an Admin User?\n\nThis will persist their identity across sessions and start accumulating song history. This action cannot be undone from the UI.`
                    )) {
                      await onConvertToRegular(entry.id);
                      setShowMenu(false);
                    }
                  }}
                  className="w-full bg-amber-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-amber-500 transition-all"
                  title="Persist this admin-added identity so it survives session resets"
                >
                  ⭐ Convert to Admin User
                </button>
              )}
              {karafunSyncActive && (
                <button
                  data-testid={`send-to-karafun-btn-${entry.id}`}
                  onClick={handleSendToKarafun}
                  className={`w-full px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    entry.sent_to_karafun
                      ? 'bg-purple-800 text-white hover:bg-purple-700'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {entry.sent_to_karafun ? '🎵 Re-send to KaraFun' : '🎵 Send to KaraFun'}
                </button>
              )}
              {/* Merge button — hidden on ephemeral admin-added entries
                  2026-09-25. Merging FROM an ephemeral entry would make
                  the ephemeral the survivor and DELETE the real user
                  the admin picks, causing permanent identity loss for
                  that real user. Admin must first tap ⭐ Convert to
                  Regular (above), which persists the ephemeral user
                  and unlocks the Merge button. Future option B (auto-
                  swap merge direction) is tracked in DEFERRED_FIXES.md
                  Fix #36. */}
              {entry.user_id && !entry.is_admin_added && (
                <button
                  data-testid={`merge-entry-btn-${entry.id}`}
                  onClick={() => {
                    setShowMenu(false);
                    // Cross-component signal: ManageUsersButton listens for
                    // this event, opens the modal, jumps to the user's
                    // detail view, and immediately opens the merge picker.
                    // Custom event chosen over shared state to avoid
                    // prop-drilling for a one-shot imperative signal.
                    window.dispatchEvent(new CustomEvent('karaoke:open-merge-for-user', {
                      detail: { user_id: entry.user_id },
                    }));
                  }}
                  className="w-full bg-indigo-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-indigo-600 transition-all border border-indigo-500"
                  title="Open Manage Users → merge picker for this user"
                >
                  🔗 Merge…
                </button>
              )}
              <button
                onClick={handleRemove}
                className="w-full bg-red-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-red-700 transition-all"
              >
                🗑️ Remove from Queue
              </button>
              {entry.add_failed && (
                <>
                  <button
                    data-testid={`dismiss-add-failed-confirm-${entry.id}`}
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/admin/queue-entry/${entry.id}/dismiss-add-failed?action=confirm`
                        );
                        setShowMenu(false);
                      } catch (e) { console.error(e); }
                    }}
                    className="w-full bg-amber-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-amber-700 transition-all"
                  >
                    ⚠ Confirm KaraFun removed this song
                  </button>
                  <button
                    data-testid={`dismiss-add-failed-false-${entry.id}`}
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/admin/queue-entry/${entry.id}/dismiss-add-failed?action=false_alarm`
                        );
                        setShowMenu(false);
                      } catch (e) { console.error(e); }
                    }}
                    className="w-full bg-slate-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-slate-700 transition-all"
                  >
                    ✓ False alarm — song is fine
                  </button>
                </>
              )}
              <button
                onClick={() => setShowMenu(false)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const AdminPanel = ({ queue, onMoveToPosition, onRemove, onMarkCurrent, onMarkPlayed, onAdvanceQueue, onAddToQueue, onResetSession, onEditEntry, onConvertToRegular, onSetInsertPoint, insertPointEntryId, groupsEnabled = false, entriesPaused = false, setEntriesPaused, error, setError, success, setSuccess }) => {
  const karafunSync = KaraFunSyncControls();
  const isSyncing = karafunSync.isSyncing;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginComplete, setLoginComplete] = useState(false);
  // 2026-09-12: Admin Controls banner is now a collapse toggle for the
  // buttons row (Update Library / Entries / Reset Session / Diagnostics).
  // Previously the banner click exited admin mode; admin now exits via
  // long-press on the 🎵 DisguisedAdminButton at the top of the page.
  // Default collapsed (host preference 2026-09-12) — admin taps the
  // banner to reveal the controls when needed.
  const [controlsExpanded, setControlsExpanded] = useState(false);
  
  // Catalog upload state
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [expandedUploadId, setExpandedUploadId] = useState(null);
  // Fix 2026-10: when a row auto-expands (post-upload) OR is manually
  // clicked, scroll it into view inside the scrollable list. Needed
  // because the list is now chronological (oldest-first); the newest
  // row sits at the bottom and would otherwise be off-screen after
  // a fresh upload.
  useEffect(() => {
    if (!expandedUploadId) return;
    const el = document.querySelector(`[data-testid="upload-history-row-${expandedUploadId}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expandedUploadId]);
  const [catalogInfo, setCatalogInfo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  // Admin-side preview of the singer-facing removals pop-up. Local
  // to `AdminPanel` (the App-level state at 8507 is not in scope here).
  // The modal supports three variants: badge-status drill-down
  // ("confirmed" / "suspected" / "restored"), and the full grouped view.
  const [catalogRemovalsModal, setCatalogRemovalsModal] = useState({
    open: false,
    variant: 'admin-all',
    statusFilter: null,
  });
  const openCatalogRemovalsModal = (variant, statusFilter = null) =>
    setCatalogRemovalsModal({ open: true, variant, statusFilter });
  const closeCatalogRemovalsModal = () =>
    setCatalogRemovalsModal((s) => ({ ...s, open: false }));
  const fileInputRef = useRef(null);
  
  // WebSocket handler state
  const [wsConnected, setWsConnected] = useState(false);

  // Move-mode state: when set, admin is picking a target entry to move this one near.
  const [movingEntryId, setMovingEntryId] = useState(null);
  const movingEntry = movingEntryId ? queue.find(e => e.id === movingEntryId) : null;

  // Swap-mode state: when set, admin is picking a target entry to swap with.
  const [swappingEntryId, setSwappingEntryId] = useState(null);
  const swappingEntry = swappingEntryId ? queue.find(e => e.id === swappingEntryId) : null;

  // Link-mode state: when set, admin is picking target entries to link with.
  const [linkingEntryId, setLinkingEntryId] = useState(null);
  const linkingEntry = linkingEntryId ? queue.find(e => e.id === linkingEntryId) : null;
  // Multi-select set of entry IDs being staged for batch linking (unlinked only)
  const [selectedLinkTargets, setSelectedLinkTargets] = useState(new Set());
  // Long-press confirm popover position — set by long-pressing any entry
  // while Link Mode is active AND ≥1 target is staged. Opens a global
  // "Confirm Link / Cancel" popover so admin doesn't have to scroll back
  // to the link banner at the top of the queue.
  const [linkConfirmPopover, setLinkConfirmPopover] = useState(null);  // { x, y }

  const handleLinkLongPressConfirm = (position) => {
    if (!linkingEntryId || selectedLinkTargets.size === 0) return;
    setLinkConfirmPopover(position);
  };

  // ── Batch-Select Mode ────────────────────────────────────────────────
  // Independent of the per-entry "Link with…" flow. Admin enables this via
  // the "Select" button at the top of the queue, then taps multiple entries
  // to select them. Long-press any entry to open the GLOBAL action menu
  // (Link Selected / Delete Selected). The per-entry hold-menu is
  // suppressed while batch-select mode is active.
  //
  // Anchor-group rule: the FIRST grouped entry the admin selects "locks"
  // its group_id as the anchor. After that, entries from OTHER groups are
  // unavailable (greyed out). If the admin tries to add a grouped entry
  // AFTER having selected one or more solos (i.e., no anchor group set
  // yet), an immediate confirmation modal appears so they can't
  // accidentally span two groups.
  const [batchSelectMode, setBatchSelectMode] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState(new Set());
  const [batchActionMenu, setBatchActionMenu] = useState(null);  // { x, y } when open
  // When non-null, a confirmation modal asking whether to absorb the
  // currently-selected solo entries into this grouped entry's group.
  // Shape: { groupedEntry, currentSelectionIds: Set }
  const [pendingGroupAbsorb, setPendingGroupAbsorb] = useState(null);
  const [showBatchLinkConfirm, setShowBatchLinkConfirm] = useState(false);
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);

  // The "anchor group" for the current batch selection: the group_id of the
  // FIRST grouped entry that was added. null when no grouped entry is in
  // the selection. Used to block cross-group selections.
  const batchAnchorGroupId = (() => {
    if (!batchSelectMode || batchSelectedIds.size === 0) return null;
    for (const id of batchSelectedIds) {
      const e = queue.find(x => x.id === id);
      if (e && e.group_id) return e.group_id;
    }
    return null;
  })();

  const exitBatchSelectMode = () => {
    setBatchSelectMode(false);
    setBatchSelectedIds(new Set());
    setBatchActionMenu(null);
    setPendingGroupAbsorb(null);
    setShowBatchLinkConfirm(false);
    setShowBatchDeleteConfirm(false);
  };

  const enterBatchSelectMode = () => {
    // Cancel any other selection-mode flows first.
    setMovingEntryId(null);
    setSwappingEntryId(null);
    setLinkingEntryId(null);
    setSelectedLinkTargets(new Set());
    setBatchSelectMode(true);
    setBatchSelectedIds(new Set());
  };

  // Toggle an entry's membership in the batch selection. Returns immediately
  // and triggers the immediate-confirmation modal when the admin tries to
  // add a grouped entry to a selection that already has solos.
  const handleBatchToggle = (entry) => {
    if (!groupsEnabled && entry.group_id) {
      // Should never happen — group_id is null when groups disabled.
    }
    const alreadySelected = batchSelectedIds.has(entry.id);
    // Deselecting is always allowed.
    if (alreadySelected) {
      setBatchSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
      return;
    }
    // Adding: enforce anchor-group rules.
    const isGrouped = !!entry.group_id;
    if (isGrouped) {
      if (batchAnchorGroupId) {
        if (entry.group_id === batchAnchorGroupId) {
          // Same group — allow toggle on
          setBatchSelectedIds(prev => {
            const next = new Set(prev);
            next.add(entry.id);
            return next;
          });
        } else {
          // Different group — block. Visual greying already signals this; toast for safety.
          setError("That entry is in a different group. Linking can only target one group at a time.");
        }
        return;
      }
      // No anchor group yet. If there ARE existing solo selections, force
      // immediate confirmation: admin is about to absorb everything into
      // this grouped entry's group. (If selection is empty, the grouped
      // entry simply becomes the anchor.)
      if (batchSelectedIds.size > 0) {
        setPendingGroupAbsorb({
          groupedEntry: entry,
          currentSelectionIds: new Set(batchSelectedIds),
        });
        return;
      }
      // Empty selection + grouped entry → just add (locks anchor group).
      setBatchSelectedIds(prev => {
        const next = new Set(prev);
        next.add(entry.id);
        return next;
      });
      return;
    }
    // Solo entry → always allowed (no cross-group risk).
    setBatchSelectedIds(prev => {
      const next = new Set(prev);
      next.add(entry.id);
      return next;
    });
  };

  // Called by QueueEntryWithMenu's long-press hook when batch-select mode
  // is active. Opens the global batch-action menu at the entry's location.
  const handleBatchLongPress = (position) => {
    if (batchSelectedIds.size === 0) {
      setError("No entries selected. Tap entries first, then long-press to open the action menu.");
      return;
    }
    setBatchActionMenu(position);
  };

  // Resolve the absorb-into-group confirmation (admin tapped a grouped
  // entry while solos were already selected).
  const handleAbsorbConfirm = async () => {
    if (!pendingGroupAbsorb) return;
    const { groupedEntry, currentSelectionIds } = pendingGroupAbsorb;
    setPendingGroupAbsorb(null);
    try {
      // Use the grouped entry as the anchor so its existing group_id is reused.
      const entry_ids = [...currentSelectionIds];
      const response = await axios.post(`${API}/admin/queue/link-batch`, {
        anchor_id: groupedEntry.id,
        entry_ids,
      });
      setSuccess(response.data.message);
      exitBatchSelectMode();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to link entries');
    }
  };

  const handleAbsorbCancel = () => {
    setPendingGroupAbsorb(null);
    // Selection state unchanged — admin can keep editing the batch.
  };

  // Submit a "Link Selected" batch link. Backend `link-batch` accepts an
  // anchor + entry_ids; we compute the anchor as the lowest-position
  // selected entry (when no anchor group is set) or the lowest-position
  // member of the anchor group (when one is set).
  const handleBatchLinkConfirm = async () => {
    setShowBatchLinkConfirm(false);
    if (batchSelectedIds.size < 2) {
      setError("Need at least 2 entries to link.");
      return;
    }
    const selectedEntries = queue.filter(e => batchSelectedIds.has(e.id));
    selectedEntries.sort((a, b) => (a.position || 0) - (b.position || 0));
    // If anchor group is locked, prefer a member of that group as anchor
    // (server reuses the group_id of the anchor for the whole batch).
    let anchor = null;
    if (batchAnchorGroupId) {
      anchor = selectedEntries.find(e => e.group_id === batchAnchorGroupId) || selectedEntries[0];
    } else {
      anchor = selectedEntries[0];
    }
    // Filter entry_ids: server's link-batch rejects already-grouped ids in
    // entry_ids. When admin selects multiple same-group members alongside
    // solos, the same-group members don't need re-linking (they're already
    // there). Drop them silently.
    const entry_ids = selectedEntries
      .filter(e => e.id !== anchor.id && !e.group_id)
      .map(e => e.id);
    if (entry_ids.length === 0) {
      // Nothing to link — all selected entries are already grouped together.
      setError("All selected entries are already in the same group. Nothing to link.");
      exitBatchSelectMode();
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/queue/link-batch`, {
        anchor_id: anchor.id,
        entry_ids,
      });
      setSuccess(response.data.message);
      exitBatchSelectMode();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to link entries');
    }
  };

  // Submit a "Delete Selected" batch. Iterates DELETE calls — backend has
  // no batch endpoint and the operations are independent. Soft- vs hard-
  // delete rules are still enforced server-side per entry.
  const handleBatchDeleteConfirm = async () => {
    setShowBatchDeleteConfirm(false);
    const ids = [...batchSelectedIds];
    let succeeded = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await axios.delete(`${API}/admin/queue/remove/${id}`);
        succeeded++;
      } catch (err) {
        failed++;
      }
    }
    if (failed === 0) {
      setSuccess(`Removed ${succeeded} entr${succeeded === 1 ? 'y' : 'ies'}.`);
    } else {
      setError(`Removed ${succeeded}, but ${failed} failed.`);
    }
    exitBatchSelectMode();
  };

  const handleStartMove = (entryId) => {
    setSwappingEntryId(null);
    setLinkingEntryId(null);
    setMovingEntryId(entryId);
  };

  const handleCancelMove = () => {
    setMovingEntryId(null);
  };

  const handleSelectMoveTarget = (targetEntry) => {
    if (!movingEntryId || targetEntry.id === movingEntryId) {
      setMovingEntryId(null);
      return;
    }
    // If the move will change the currently-playing song (either the moved
    // entry sits at pos-1, or the target slot is pos 1), require explicit
    // confirmation — same UX as Restart. We check position (not is_current)
    // because is_current is only set while KaraFun is actively playing; the
    // pos-1 entry between songs would otherwise skip the warning.
    const movingEntry = queue.find(e => e.id === movingEntryId);
    const willInterruptPlayback = (
      (movingEntry && (movingEntry.position === 1 || movingEntry.is_current)) ||
      targetEntry.position === 1
    );
    if (willInterruptPlayback) {
      const ok = window.confirm(
        'This move will stop the currently-playing song and start a new one. Continue?'
      );
      if (!ok) {
        setMovingEntryId(null);
        return;
      }
    }
    // Reuse existing backend semantics: passing the target's current position
    // performs a "pop-and-insert" — moved entry lands on the target's slot,
    // pushing the target up (move up) or down (move down) by one.
    onMoveToPosition(movingEntryId, targetEntry.position);
    setMovingEntryId(null);
  };

  const handleStartSwap = (entryId) => {
    setMovingEntryId(null);
    setLinkingEntryId(null);
    setSwappingEntryId(entryId);
  };

  const handleCancelSwap = () => {
    setSwappingEntryId(null);
  };

  const handleSelectSwapTarget = async (targetEntry) => {
    if (!swappingEntryId || targetEntry.id === swappingEntryId) {
      setSwappingEntryId(null);
      return;
    }
    // If the swap will change the currently-playing song (either endpoint
    // sits at pos-1), require explicit confirmation — same UX as Restart
    // and Move. We check position (not is_current) because is_current is
    // only set while KaraFun is actively playing; the pos-1 entry between
    // songs would otherwise skip the warning.
    const anchorEntry = queue.find(e => e.id === swappingEntryId);
    const willInterruptPlayback = (
      (anchorEntry && (anchorEntry.position === 1 || anchorEntry.is_current)) ||
      targetEntry.position === 1 ||
      targetEntry.is_current
    );
    if (willInterruptPlayback) {
      const ok = window.confirm(
        'This swap will stop the currently-playing song and start a new one. Continue?'
      );
      if (!ok) {
        setSwappingEntryId(null);
        return;
      }
    }
    try {
      const response = await axios.put(`${API}/admin/queue/swap-positions`, {
        entry_id_a: swappingEntryId,
        entry_id_b: targetEntry.id
      });
      setSuccess(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to swap entries');
    } finally {
      setSwappingEntryId(null);
    }
  };

  const handleStartLink = (entryId) => {
    setMovingEntryId(null);
    setSwappingEntryId(null);
    setSelectedLinkTargets(new Set());
    setLinkingEntryId(entryId);
  };

  const handleCancelLink = () => {
    setLinkingEntryId(null);
    setSelectedLinkTargets(new Set());
    setLinkConfirmPopover(null);
  };

  // Hybrid Link Mode (Option 2):
  //   • Tap an UNLINKED target → toggle it in the multi-select.
  //   • Tap a GROUPED target (and anchor is unlinked):
  //       - If selection is empty → INSTANT link anchor into that group, exit Link Mode.
  //       - If selection has items → batch-link (anchor + all selected) into that group.
  //   • Tap a GROUPED target (anchor is also grouped, different group) → friendly
  //     error toast. (Frontend also greys these out to telegraph non-selectability.)
  const handleSelectLinkTarget = async (targetEntry) => {
    if (!linkingEntryId || targetEntry.id === linkingEntryId) {
      handleCancelLink();
      return;
    }
    const anchor = queue.find(e => e.id === linkingEntryId);
    const anchorGrouped = !!(anchor && anchor.group_id);
    const targetGrouped = !!targetEntry.group_id;

    // Cross-group attempt (both grouped, different groups) → reject
    if (targetGrouped && anchorGrouped && anchor.group_id !== targetEntry.group_id) {
      setError('That entry already belongs to another group. Unlink it first.');
      return;
    }

    // Same group (no-op): silently ignore
    if (targetGrouped && anchorGrouped && anchor.group_id === targetEntry.group_id) {
      return;
    }

    // Target is grouped + anchor is unlinked → absorb anchor (and any staged selection)
    // into the target's group.
    if (targetGrouped && !anchorGrouped) {
      try {
        if (selectedLinkTargets.size === 0) {
          // No selection staged → simple instant link of anchor into the group
          const response = await axios.post(`${API}/admin/queue/link`, {
            entry_id_a: targetEntry.id,   // group side stays the anchor block
            entry_id_b: linkingEntryId,
          });
          setSuccess(response.data.message);
        } else {
          // Selection staged → confirm before absorbing into existing group
          // (guards against accidental tap on a grouped entry while staging).
          const count = selectedLinkTargets.size + 1; // anchor + selections
          const ok = window.confirm(
            `Link ${count} entries (${anchor.user_name} + ${selectedLinkTargets.size} selected) into ${targetEntry.user_name}'s group?`
          );
          if (!ok) return; // keep Link Mode + selection intact for further editing
          // Use the grouped target as the batch anchor so its group_id is reused.
          const entry_ids = [linkingEntryId, ...Array.from(selectedLinkTargets)];
          const response = await axios.post(`${API}/admin/queue/link-batch`, {
            anchor_id: targetEntry.id,
            entry_ids,
          });
          setSuccess(response.data.message);
        }
      } catch (error) {
        setError(error.response?.data?.detail || 'Failed to link entries');
      } finally {
        handleCancelLink();
      }
      return;
    }

    // MULTI-SELECT case: target is unlinked → toggle in selection
    setSelectedLinkTargets(prev => {
      const next = new Set(prev);
      if (next.has(targetEntry.id)) {
        next.delete(targetEntry.id);
      } else {
        next.add(targetEntry.id);
      }
      return next;
    });
  };

  // Confirm button — sends the staged multi-select batch to the backend.
  const handleConfirmLinkBatch = async () => {
    if (!linkingEntryId || selectedLinkTargets.size === 0) return;
    try {
      const response = await axios.post(`${API}/admin/queue/link-batch`, {
        anchor_id: linkingEntryId,
        entry_ids: Array.from(selectedLinkTargets),
      });
      setSuccess(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to link entries');
    } finally {
      handleCancelLink();
    }
  };

  const handleUnlinkEntry = async (entryId) => {
    try {
      const response = await axios.delete(`${API}/admin/queue/link/${entryId}`);
      setSuccess(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to unlink entry');
    }
  };

  const handleSendToKarafun = async (targetEntry) => {
    try {
      const response = await axios.post(`${API}/karafun/send-entry/${targetEntry.id}`);
      setSuccess(response.data.message || `Sent ${targetEntry.user_name}'s song to KaraFun`);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send song to KaraFun');
    }
  };

  const handleWebSocketStatus = (connected, error) => {
    setWsConnected(connected);
    if (connected) {
      console.log('✅ WebSocket connected to KaraFun');
      setLoginComplete(true);
      setShowLoginModal(false);
    } else if (error) {
      console.error('❌ WebSocket error:', error);
    }
  };
  
  // Check if login is needed when sync starts
  useEffect(() => {
    if (isSyncing && !loginComplete) {
      // Check if login is complete
      const checkLogin = async () => {
        try {
          const response = await fetch(`${API}/karafun/login/check`);
          const data = await response.json();
          if (data.login_complete) {
            setLoginComplete(true);
            setShowLoginModal(false);
          } else {
            setShowLoginModal(true);
          }
        } catch (err) {
          console.error('Error checking login:', err);
        }
      };
      
      checkLogin();
    }
  }, [isSyncing, loginComplete]);
  
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingEntryIsRestore, setEditingEntryIsRestore] = useState(false);
  const [editName, setEditName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  // History dropdown state for admin edit modal (Fix #23 follow-up, 2026-09-15)
  const [adminHistoryResults, setAdminHistoryResults] = useState([]);
  const [showAdminHistoryDropdown, setShowAdminHistoryDropdown] = useState(false);
  const [isFetchingAdminHistory, setIsFetchingAdminHistory] = useState(false);

  // Admin-name inline editor 2026-09-25 — a toggle button above the User
  // Name field opens a second input for editing the admin_name label
  // without leaving the Edit Entry flow. Patches via
  // `PATCH /admin/users/{user_id}` (same endpoint Manage Users uses).
  const [showAdminNameField, setShowAdminNameField] = useState(false);
  const [editAdminName, setEditAdminName] = useState('');
  const [savingAdminName, setSavingAdminName] = useState(false);
  const [adminNameError, setAdminNameError] = useState('');

  // Admin "Change Pitch" sub-modal — opened from within the Edit Entry modal
  const [adminPitchEntry, setAdminPitchEntry] = useState(null);
  const [adminPitchDraft, setAdminPitchDraft] = useState(0);
  const [adminPitchLoading, setAdminPitchLoading] = useState(false);

  const openAdminPitchModal = async (entry) => {
    if (!entry) return;
    setAdminPitchEntry(entry);
    setAdminPitchDraft(0);
    setAdminPitchLoading(true);
    try {
      // Fetch the actual current value (admin endpoint bypasses privacy strip).
      const r = await axios.get(`${API}/admin/queue/${entry.id}/pitch`);
      setAdminPitchDraft(Number(r.data?.pitch_offset || 0));
    } catch (err) {
      setAdminPitchDraft(0);
    } finally {
      setAdminPitchLoading(false);
    }
  };
  const closeAdminPitchModal = () => setAdminPitchEntry(null);
  const handleAdminPitchSave = async () => {
    if (!adminPitchEntry) return;
    setAdminPitchLoading(true);
    try {
      await axios.put(`${API}/admin/queue/${adminPitchEntry.id}/pitch`, {
        pitch_offset: Number(adminPitchDraft),
      });
      if (setSuccess) {
        setSuccess(
          Number(adminPitchDraft) === 0
            ? `Cleared pitch for ${adminPitchEntry.user_name}.`
            : `Set pitch ${adminPitchDraft > 0 ? '+' : ''}${adminPitchDraft} for ${adminPitchEntry.user_name}.`
        );
      }
      setAdminPitchEntry(null);
    } catch (err) {
      if (setError) {
        setError(err.response?.data?.detail || 'Failed to update pitch');
      }
    } finally {
      setAdminPitchLoading(false);
    }
  };

  // Deleted-entries panel
  const [deletedExpanded, setDeletedExpanded] = useState(false);
  const [deletedEntries, setDeletedEntries] = useState([]);
  const [deletedRowMenuId, setDeletedRowMenuId] = useState(null);
  // Fix #44 (shipped 2026-09-28): Multi-select for the Deleted Entries panel.
  // Mirrors the main queue's batch-select pattern, scoped to deleted
  // entries. Permanent-delete only (no bulk restore).
  const [deletedBatchSelectMode, setDeletedBatchSelectMode] = useState(false);
  const [deletedBatchSelectedIds, setDeletedBatchSelectedIds] = useState(new Set());
  const [showDeletedBatchDeleteConfirm, setShowDeletedBatchDeleteConfirm] = useState(false);

  // ── KaraFun catalog-removals section (Fix #23-followup, 2026-09-16;
  // public URL removed 2026-09-24 in favor of the in-app modal) ──
  // Bottom-of-panel expandable list of songs KaraFun has removed from
  // its catalog. Admin can:
  //   • Download detailed .txt (song IDs / statuses / dates).
  //   • Preview the singer-facing pop-up (opens `CatalogRemovalsModal`,
  //     the same modal users see via `/api/public/catalog-removals`).
  const [catalogRemovalsExpanded, setCatalogRemovalsExpanded] = useState(false);
  const [catalogRemovals, setCatalogRemovals] = useState([]);
  const [catalogRemovalsCounts, setCatalogRemovalsCounts] = useState({});

  const fetchCatalogRemovals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/catalog-removals`);
      setCatalogRemovals(response.data?.rows || []);
      setCatalogRemovalsCounts(response.data?.counts_by_status || {});
    } catch (err) {
      console.error('Failed to fetch catalog removals:', err);
    }
  }, []);

  const fetchDeletedEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/queue/deleted`);
      setDeletedEntries(response.data || []);
    } catch (err) {
      console.error('Failed to fetch deleted entries:', err);
    }
  }, []);

  useEffect(() => {
    // Refresh deleted-entries list whenever the panel is open or the active
    // queue changes (so deletions show up immediately).
    if (deletedExpanded) fetchDeletedEntries();
  }, [deletedExpanded, queue, fetchDeletedEntries]);

  useEffect(() => {
    if (catalogRemovalsExpanded) fetchCatalogRemovals();
  }, [catalogRemovalsExpanded, fetchCatalogRemovals]);

  // Fetch removal count on mount so the collapsed section shows "(N)"
  // without requiring the admin to open it first.
  useEffect(() => {
    fetchCatalogRemovals();
  }, [fetchCatalogRemovals]);

  const downloadCatalogRemovalsTxt = () => {
    // Admin-detail .txt — grouped by status, includes song IDs and dates.
    // Format is intentionally richer than the public URL (which is a
    // simple title—artist list for user sharing).
    const groups = {
      confirmed: [],
      suspected: [],
      restored: [],
      false_positive: [],
    };
    for (const r of catalogRemovals) {
      const bucket = groups[r.status] || groups.suspected;
      bucket.push(r);
    }
    const idWidth = Math.max(
      ...catalogRemovals.map(r => String(r.song_id ?? '').length),
      1,
    );
    const padId = (id) => String(id ?? '').padStart(idWidth, ' ');
    const fmtDate = (iso) => {
      if (!iso) return '(unknown date)';
      try {
        return new Date(iso).toISOString().slice(0, 10);
      } catch (_e) {
        return '(unknown date)';
      }
    };
    const groupSection = (label, rows) => {
      const header = [`${label} (${rows.length})`, '-'.repeat(label.length + 5)];
      if (rows.length === 0) return [...header, '(none)', ''];
      // Sort within group: newest-detected first.
      const sorted = [...rows].sort((a, b) => {
        const at = a.last_detected_at || a.first_detected_at || '';
        const bt = b.last_detected_at || b.first_detected_at || '';
        return bt.localeCompare(at);
      });
      const lines = sorted.map(r => {
        const seen = r.occurrence_count ? `seen ${r.occurrence_count}×` : 'seen 1×';
        return `[${padId(r.song_id)}] ${r.title || '(no title)'} — ${r.artist || '(no artist)'}    (detected ${fmtDate(r.last_detected_at || r.first_detected_at)}, ${seen})`;
      });
      return [...header, ...lines, ''];
    };
    const now = new Date();
    const nowStr = now.toLocaleString();
    const nowStamp = now.toISOString().slice(0, 10);
    const total = catalogRemovals.length;
    const c = catalogRemovalsCounts;
    const lines = [
      'KaraFun Removed Songs — Full Admin Export',
      '==========================================',
      `Generated: ${nowStr}`,
      `Total: ${total} (Confirmed: ${c.confirmed || 0}, Suspected: ${c.suspected || 0}, Restored: ${c.restored || 0})`,
      '',
      ...groupSection('CONFIRMED', groups.confirmed),
      ...groupSection('SUSPECTED', groups.suspected),
      ...groupSection('RESTORED', groups.restored),
      // FALSE POSITIVE section intentionally omitted — false positives are
      // songs confirmed as still in the catalog, so they don't belong in
      // an export of "removed songs". Retained as `groups.false_positive`
      // in memory for the debug-log signal only. (2026-09-20, host request.)
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `karafun-removed-songs-${nowStamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEdit = (entry, options = {}) => {
    setEditingEntry(entry);
    setEditingEntryIsRestore(false);
    setEditName(entry.user_name);
    setSearchQuery('');  // Clear song search field for fresh search
    setSelectedSong({ title: entry.song_title, artist: entry.song_artist });  // Keep selected song for reference
    setSearchResults([]);
    setShowDropdown(false);
    // Reset admin-name sub-editor state; pre-fill draft with current
    // value so the field is ready if the host clicks the toggle.
    setShowAdminNameField(!!options.focusAdminName);
    setEditAdminName(entry.admin_name || '');
    setAdminNameError('');
  };

  const handleOpenRestoreModal = (deletedEntry) => {
    // Reuse the edit modal as the "Restore" modal.
    // Builds a synthetic queue-entry-shaped object so the modal renders.
    // deleted_kind determines which endpoint to hit on submit:
    //   • passkey_placeholder → POST /admin/queue/placeholder/{id}/restore
    //   • admin_ephemeral | admin_regular → POST /admin/queue/restore/{id}
    setDeletedRowMenuId(null);
    setEditingEntry({
      id: deletedEntry.id,
      user_name: deletedEntry.user_name,
      song_title: deletedEntry.song_title,
      song_artist: deletedEntry.song_artist,
      deleted_kind: deletedEntry.deleted_kind,
      is_passkey_placeholder: !!deletedEntry.is_passkey_placeholder,
    });
    setEditingEntryIsRestore(true);
    setEditName(deletedEntry.user_name);
    setSearchQuery('');
    setSelectedSong({ title: deletedEntry.song_title, artist: deletedEntry.song_artist });
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handlePermanentlyDelete = async (deletedEntry) => {
    const ok = window.confirm(
      `Permanently delete ${deletedEntry.user_name}'s entry from the database?\n\nThis cannot be undone.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/queue/permanently-delete/${deletedEntry.id}`);
      setSuccess(`Permanently deleted ${deletedEntry.user_name}'s entry`);
      setDeletedRowMenuId(null);
      fetchDeletedEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to permanently delete entry');
    }
  };

  // Fix #44 (shipped 2026-09-28): Deleted-entries batch-select handlers.
  // Uses the same iterate-per-item pattern as the main queue's
  // `handleBatchDeleteConfirm` (line ~4489). Reuses the existing
  // `/admin/queue/permanently-delete/{id}` endpoint — no new backend.
  const enterDeletedBatchMode = () => {
    setDeletedRowMenuId(null); // close any open per-row menu
    setDeletedBatchSelectedIds(new Set());
    setDeletedBatchSelectMode(true);
  };

  const exitDeletedBatchMode = () => {
    setDeletedBatchSelectMode(false);
    setDeletedBatchSelectedIds(new Set());
    setShowDeletedBatchDeleteConfirm(false);
  };

  const toggleDeletedBatchSelect = (entryId) => {
    setDeletedBatchSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const handleDeletedBatchDeleteConfirm = async () => {
    setShowDeletedBatchDeleteConfirm(false);
    const ids = [...deletedBatchSelectedIds];
    let succeeded = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await axios.delete(`${API}/admin/queue/permanently-delete/${id}`);
        succeeded++;
      } catch (err) {
        failed++;
      }
    }
    if (failed === 0) {
      setSuccess(`Permanently deleted ${succeeded} entr${succeeded === 1 ? 'y' : 'ies'}.`);
    } else {
      setError(`Deleted ${succeeded}, but ${failed} failed.`);
    }
    exitDeletedBatchMode();
    fetchDeletedEntries();
  };

  const searchSongs = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/songs/search?q=${encodeURIComponent(query)}&limit=50`);
      setSearchResults(response.data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedSong(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchSongs(query);
    }, 300);
  };

  const handleSelectSong = (song) => {
    setSelectedSong(song);
    setSearchQuery(`${song.title} - ${song.artist}`);
    setShowDropdown(false);
  };

  // Toggle target user's song history dropdown for admin edit modal.
  // Uses admin-only endpoint that looks up device_fingerprint server-side
  // (fingerprint is not exposed in QueueEntryResponse for privacy).
  // Fix (2026-09-24): button now TOGGLES — if the dropdown is already open,
  // just close it (no re-fetch). Previously the button only opened, leaving
  // the dropdown stuck open until an item was selected.
  const openAdminHistoryDropdown = async () => {
    if (showAdminHistoryDropdown) {
      setShowAdminHistoryDropdown(false);
      return;
    }
    const targetId = editingEntry && editingEntry.id;
    if (!targetId) return;
    setIsFetchingAdminHistory(true);
    try {
      const response = await axios.get(
        `${API}/admin/queue-entry/${targetId}/song-history?limit=200`
      );
      setAdminHistoryResults(Array.isArray(response.data) ? response.data : []);
      setShowAdminHistoryDropdown(true);
      setShowDropdown(false);
    } catch (err) {
      console.error('Admin history fetch error:', err);
      setAdminHistoryResults([]);
      setShowAdminHistoryDropdown(true);
    } finally {
      setIsFetchingAdminHistory(false);
    }
  };

  const handleAdminSelectHistorySong = (h) => {
    handleSelectSong({
      id: h.song_id ? String(h.song_id) : undefined,
      title: h.song_title,
      artist: h.song_artist,
    });
    setShowAdminHistoryDropdown(false);
  };

  // ── Admin history delete (long-press → confirm → DELETE) ─────────
  const [adminHistoryPendingDelete, setAdminHistoryPendingDelete] = useState(null);
  const [isDeletingAdminHistory, setIsDeletingAdminHistory] = useState(false);

  const handleAdminHistoryLongPress = (row) => {
    setAdminHistoryPendingDelete(row);
  };

  const handleConfirmDeleteAdminHistory = async () => {
    const targetId = editingEntry && editingEntry.id;
    if (!adminHistoryPendingDelete || !targetId) return;
    setIsDeletingAdminHistory(true);
    try {
      await axios.delete(
        `${API}/admin/queue-entry/${targetId}/song-history/entry`,
        {
          data: {
            song_id: adminHistoryPendingDelete.song_id ?? null,
            song_title: adminHistoryPendingDelete.song_title,
            song_artist: adminHistoryPendingDelete.song_artist,
          },
        }
      );
      setAdminHistoryResults((prev) => prev.filter((r) => (
        (r.song_id ?? null) !== (adminHistoryPendingDelete.song_id ?? null) ||
        r.song_title !== adminHistoryPendingDelete.song_title ||
        r.song_artist !== adminHistoryPendingDelete.song_artist
      )));
    } catch (err) {
      console.error('Admin history delete error:', err);
    } finally {
      setIsDeletingAdminHistory(false);
      setAdminHistoryPendingDelete(null);
    }
  };

  const handleSubmitEdit = async () => {
    if (!editName.trim()) {
      alert('Please enter a name');
      return;
    }
    if (!selectedSong) {
      alert('Please select a song');
      return;
    }

    // If editing the pos-1 entry AND the song is actually changing, this
    // will interrupt the currently-playing song in KaraFun (the backend
    // performs an Add→Move→Skip "replace current" sequence). Require
    // explicit confirmation — same UX as Swap/Move on pos-1. Name-only
    // edits don't touch KaraFun, so no prompt. Restore flow is exempt
    // (restored entries land at the bottom, not pos-1).
    if (editingEntry && !editingEntryIsRestore) {
      const isPos1 = (editingEntry.position === 1 || editingEntry.is_current);
      const songChanged = (
        selectedSong.title !== editingEntry.song_title ||
        selectedSong.artist !== editingEntry.song_artist
      );
      if (isPos1 && songChanged) {
        const ok = window.confirm(
          'This edit will stop the currently-playing song and start a new one. Continue?'
        );
        if (!ok) return;
      }
    }

    if (editingEntryIsRestore) {
      const isPlaceholder = !!editingEntry.is_passkey_placeholder;
      const restoreUrl = isPlaceholder
        ? `${API}/admin/queue/placeholder/${editingEntry.id}/restore`
        : `${API}/admin/queue/restore/${editingEntry.id}`;
      try {
        const response = await axios.post(
          restoreUrl,
          { user_name: editName, song_title: selectedSong.title, song_artist: selectedSong.artist }
        );
        setSuccess(response.data.message || 'Entry restored');
        fetchDeletedEntries();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to restore entry');
      }
    } else {
      await onEditEntry(editingEntry.id, editName, selectedSong.title, selectedSong.artist, selectedSong.id);
    }

    // Reset form
    setEditingEntry(null);
    setEditingEntryIsRestore(false);
    setEditName('');
    setSearchQuery('');
    setSelectedSong(null);
    setSearchResults([]);
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditingEntryIsRestore(false);
    setEditName('');
    setSearchQuery('');
    setSelectedSong(null);
    setSearchResults([]);
    // Reset admin-name sub-editor state on any modal close.
    setShowAdminNameField(false);
    setEditAdminName('');
    setAdminNameError('');
    setSavingAdminName(false);
  };

  // Save the admin-name draft against `entry.user_id`. Reuses the same
  // `PATCH /admin/users/{user_id}` endpoint Manage Users uses, so the
  // two entry points stay in sync. On success, patches the local
  // editingEntry so the header badge re-renders immediately without
  // re-fetching the queue.
  const handleSaveAdminName = async () => {
    if (!editingEntry || !editingEntry.user_id) return;
    setSavingAdminName(true);
    setAdminNameError('');
    try {
      const trimmed = (editAdminName || '').trim();
      const res = await axios.patch(
        `${API}/admin/users/${editingEntry.user_id}`,
        { admin_name: trimmed }  // "" → backend $unsets; any other → sets
      );
      const nextAdmin = (res.data?.admin_name || '').trim() || null;
      setEditingEntry((prev) => prev ? { ...prev, admin_name: nextAdmin } : prev);
      setEditAdminName(nextAdmin || '');
      setShowAdminNameField(false);
    } catch (err) {
      setAdminNameError(err.response?.data?.detail || err.message || 'Failed to save admin name');
    } finally {
      setSavingAdminName(false);
    }
  };

  // Fetch catalog info
  const fetchCatalogInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/catalog-info`);
      setCatalogInfo(response.data);
    } catch (err) {
      console.error('Error fetching catalog info:', err);
    }
  }, []);

  // Handle catalog file upload
  const handleCatalogUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to replace the song library?\n\nFile: ${file.name}\nSize: ${(file.size / 1024 / 1024).toFixed(2)} MB\n\nThis will replace all existing songs in the database.`
    );
    
    if (!confirmed) {
      event.target.value = '';
      return;
    }
    
    setIsUploading(true);
    setUploadProgress('Uploading file...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/upload-catalog`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(`Uploading: ${percentCompleted}%`);
        },
      });
      
      setUploadProgress('Processing...');
      
      if (response.data.success) {
        setSuccess(`✅ Song library updated! ${response.data.songs_loaded.toLocaleString()} songs loaded.`);
        await fetchCatalogInfo();
        // Refresh upload history so the new entry appears at the top with its diff (2026-09-11)
        try {
          const historyRes = await axios.get(`${API}/admin/catalog-uploads?limit=20`);
          setUploadHistory(historyRes.data?.uploads || []);
          // Auto-expand the just-uploaded entry so admin sees the diff immediately
          if (response.data.upload_id) setExpandedUploadId(response.data.upload_id);
        } catch (_) {
          // Non-blocking — history refresh failure doesn't undo the upload
        }
      } else {
        setError(response.data.message || 'Upload failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload catalog');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      event.target.value = '';
    }
  };

  // Fetch catalog info when modal opens
  useEffect(() => {
    if (showCatalogModal) {
      fetchCatalogInfo();
      // Fetch the last 20 uploads for the history section (shipped 2026-09-11)
      axios.get(`${API}/admin/catalog-uploads?limit=20`)
        .then(res => setUploadHistory(res.data?.uploads || []))
        .catch(() => setUploadHistory([]));
    } else {
      setExpandedUploadId(null);
    }
  }, [showCatalogModal, fetchCatalogInfo]);

  // Generate a plain-text diff file for a catalog upload and trigger a client-side download.
  // Format is device-agnostic — .txt opens natively on PC/Mac/iOS/Android.
  const downloadDiffTxt = (upload) => {
    const added = upload?.diff?.added_song_details || [];
    const removed = upload?.diff?.removed_song_details || [];
    const dt = upload?.uploaded_at ? new Date(upload.uploaded_at).toLocaleString() : '(unknown)';
    const idWidth = Math.max(
      ...added.map(s => String(s.id).length),
      ...removed.map(s => String(s.id).length),
      1
    );
    const padId = (id) => String(id).padStart(idWidth, ' ');
    const lines = [
      'Karaoke Library Diff',
      '====================',
      `Uploaded: ${dt}`,
      `File: ${upload?.filename || '(unknown)'}`,
      `Total songs after upload: ${upload?.song_count?.toLocaleString?.() || upload?.song_count || '?'}`,
      '',
      'Summary',
      '-------',
      `Added:     ${added.length}`,
      `Removed:   ${removed.length}`,
      `Unchanged: ${(upload?.diff?.unchanged_count ?? 0).toLocaleString?.() || upload?.diff?.unchanged_count || 0}`,
      '',
      `ADDED SONGS (${added.length})`,
      '----------------',
      ...added.map(s => `[${padId(s.id)}] ${s.title} — ${s.artist}`),
      '',
      `REMOVED SONGS (${removed.length})`,
      '-----------------',
      ...removed.map(s => `[${padId(s.id)}] ${s.title} — ${s.artist}`),
      ''
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = upload?.uploaded_at ? new Date(upload.uploaded_at).toISOString().slice(0, 19).replace(/[:T]/g, '-') : 'diff';
    a.download = `karaoke-library-diff-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="card rounded-lg shadow-lg p-6">
        {/* Admin Controls heading — now toggles collapse of the buttons row below.
            Admin exits admin mode via long-press on the 🎵 icon at the top of the page. */}
        <h3
          data-testid="admin-controls-toggle"
          className="text-xl font-bold text-white cursor-pointer hover:text-gray-300 transition-colors mb-3 flex items-center justify-between select-none"
          onClick={() => setControlsExpanded(v => !v)}
          title={controlsExpanded ? 'Click to collapse admin controls' : 'Click to expand admin controls'}
        >
          <span>🛠️ Admin Controls</span>
          <span className="text-gray-400 text-base" aria-hidden>{controlsExpanded ? '▲' : '▼'}</span>
        </h3>
        {/* Buttons row: Update Library | Pause Toggle | Reset Session | Diagnostics
            Kept mounted (not conditionally rendered) so ManageUsersButton's
            `karaoke:open-merge-for-user` event listener stays live even
            when Admin Controls are collapsed — otherwise tapping Merge in
            an entry popover is a silent no-op. Hidden via `display: none`
            when collapsed. */}
        <div
          className={`mb-4 flex justify-end gap-2 flex-wrap [&>button]:w-[calc(50%-0.25rem)] [&>button]:whitespace-nowrap sm:[&>button]:w-auto ${controlsExpanded ? '' : 'hidden'}`}
          data-testid="admin-controls-buttons-row"
        >
          <ManageUsersButton />
          <DBToolsButton />
          {SHOW_DB_RECOVER_PREFIXED && <DBRecoverPrefixedButton />}
          <button
            onClick={() => setShowCatalogModal(true)}
            className="bg-indigo-600 text-white px-3 py-2 sm:py-1 rounded hover:bg-indigo-700 text-sm font-semibold"
            title="Update Song Catalog"
          >
            📚 Update Catalog
          </button>
          <button
            data-testid="entries-paused-toggle"
            onClick={async () => {
              const next = !entriesPaused;
              try {
                await axios.post(`${API}/admin/queue/pause`, { paused: next });
                if (setEntriesPaused) setEntriesPaused(next);
                setSuccess(next ? 'Incoming entries blocked' : 'Incoming entries allowed');
              } catch (err) {
                setError(err.response?.data?.detail || 'Failed to toggle pause');
              }
            }}
            className={`px-3 py-2 sm:py-1 rounded text-sm font-semibold text-white transition-all ${
              entriesPaused
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            title={entriesPaused
              ? 'Currently BLOCKING new signups — click to allow'
              : 'Currently ALLOWING new signups — click to block'}
          >
            {entriesPaused ? '⏸ Entries Blocked' : '▶ Entries Allowed'}
          </button>
          <HoldToResetButton onResetSession={onResetSession} />
          <DiagnosticsButton />
        </div>

        {/* ── Admin Controls cluster — order per host 2026-10-04:
              1. Stop KaraFun Sync + KaraFun Login (top, only when syncing)
              2. Auto-Advance (only when syncing)
              3. Auto-Insert (bottom, always; counter renders directly below) */}
        {controlsExpanded && (
          <div className="mb-4 space-y-2" data-testid="admin-auto-controls-cluster">
            {isSyncing && karafunSync.renderSyncButtons(setShowLoginModal)}
            {isSyncing && <AutoAdvanceButton onAdvanceQueue={onAdvanceQueue} />}
            <AutoInsertButton />
          </div>
        )}

        <AddToQueueForm onAddToQueue={onAddToQueue} error={error} setError={setError} success={success} />
        
        {/* Start Sync button — visible at this original location whenever
            sync is NOT active. Same button as the Stop version above;
            the button itself flips its label + color based on isSyncing. */}
        {!isSyncing && karafunSync.renderSyncButtons(setShowLoginModal)}
        
        {/* KaraFun sync status pill (only rendered when sync is active) */}
        {karafunSync.renderSyncStatus()}
        
        {/* Show original Advance button when sync is NOT active */}
        {!isSyncing && (
          <div className="mb-4">
            <button
              onClick={onAdvanceQueue}
              className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800 font-semibold w-full flex items-center justify-center gap-2"
            >
              Advance to Next Turn <IoArrowForwardCircle size={24} />
            </button>
          </div>
        )}
        
        {/* KaraFun Playback Controls (Play/Pause/Restart/Skip only — the
            Auto-Insert and Auto-Advance toggles were extracted to the
            Admin Controls cluster above in 2026-09-27). Only rendered when
            syncing. */}
        {isSyncing && <KaraFunPlaybackControls onAdvanceQueue={onAdvanceQueue} />}
        
        <div>
          {/* ── Batch-Select toggle + banner ─────────────────────────── */}
          {!batchSelectMode ? (
            <div className="mb-3 flex justify-end">
              <button
                data-testid="batch-select-enter-btn"
                onClick={enterBatchSelectMode}
                className="bg-purple-700 hover:bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                title="Select multiple entries at once for batch link or delete"
              >
                <span>✓</span>
                <span>Select</span>
              </button>
            </div>
          ) : (
            <div
              data-testid="batch-select-banner"
              className="mb-3 p-3 rounded-lg border border-purple-500 bg-purple-900/40 flex items-center justify-between gap-3"
            >
              <div className="text-purple-100 text-sm sm:text-base flex-1 min-w-0">
                <span className="font-semibold">Select Mode </span>
                <span className="font-bold text-white">({batchSelectedIds.size} selected)</span>
                <span className="text-purple-200/80 block sm:inline sm:ml-2 text-xs sm:text-sm">
                  Tap entries to select. Long-press any entry to open the batch menu.
                </span>
              </div>
              <button
                data-testid="batch-select-cancel-btn"
                onClick={exitBatchSelectMode}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0"
              >
                Cancel
              </button>
            </div>
          )}
          {movingEntry && (
            <div
              data-testid="move-mode-banner"
              className="mb-3 p-3 rounded-lg border border-orange-500 flex items-center justify-between gap-3"
            >
              <div className="text-orange-100 text-sm sm:text-base">
                <span className="font-semibold">Select where to move </span>
                <span className="font-bold text-white">{movingEntry.user_name}</span>
                <span className="text-orange-200/80 block sm:inline sm:ml-2 text-xs sm:text-sm">
                  Tap an entry to place above (if higher) or below (if lower).
                </span>
              </div>
              <button
                data-testid="move-mode-cancel-btn"
                onClick={handleCancelMove}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0"
              >
                Cancel
              </button>
            </div>
          )}
          {swappingEntry && (
            <div
              data-testid="swap-mode-banner"
              className="mb-3 p-3 rounded-lg border border-orange-500 flex items-center justify-between gap-3"
            >
              <div className="text-orange-100 text-sm sm:text-base">
                <span className="font-semibold">Select an entry to swap with </span>
                <span className="font-bold text-white">{swappingEntry.user_name}</span>
                <span className="text-orange-200/80 block sm:inline sm:ml-2 text-xs sm:text-sm">
                  Grouped entries can't be swapped.
                </span>
              </div>
              <button
                data-testid="swap-mode-cancel-btn"
                onClick={handleCancelSwap}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0"
              >
                Cancel
              </button>
            </div>
          )}
          {groupsEnabled && linkingEntry && (
            <div
              data-testid="link-mode-banner"
              className="mb-3 p-3 rounded-lg border border-cyan-500 bg-cyan-900/40 flex items-center justify-between gap-3"
            >
              <div className="text-cyan-100 text-sm sm:text-base flex-1 min-w-0">
                {selectedLinkTargets.size > 0 ? (
                  <>
                    <span className="font-semibold">Linking </span>
                    <span className="font-bold text-white">{linkingEntry.user_name}</span>
                    <span className="font-semibold"> with </span>
                    <span className="font-bold text-white">{selectedLinkTargets.size}</span>
                    <span className="font-semibold">{selectedLinkTargets.size === 1 ? ' other' : ' others'}</span>
                    <span className="text-cyan-200/80 block sm:inline sm:ml-2 text-xs sm:text-sm">
                      Tap more to add, or Confirm.
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Linking with </span>
                    <span className="font-bold text-white">{linkingEntry.user_name}</span>
                    <span className="text-cyan-200/80 block sm:inline sm:ml-2 text-xs sm:text-sm">
                      Tap unlinked entries to build a group, or tap a group member to join that group.
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {selectedLinkTargets.size > 0 && (
                  <button
                    data-testid="link-mode-confirm-btn"
                    onClick={handleConfirmLinkBatch}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all"
                  >
                    Link Selected
                  </button>
                )}
                <button
                  data-testid="link-mode-cancel-btn"
                  onClick={handleCancelLink}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {queue.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No entries to manage</p>
          ) : (
            (() => {
              const renderEntry = (entry, index, inGroup = false) => (
                <QueueEntryWithMenu
                  key={entry.id}
                  entry={entry}
                  index={index}
                  queueLength={queue.length}
                  inGroup={inGroup}
                  onMoveToPosition={onMoveToPosition}
                  onMarkPlayed={onMarkPlayed}
                  onRemove={onRemove}
                  onEdit={handleEdit}
                  onConvertToRegular={onConvertToRegular}
                  onSetInsertPoint={onSetInsertPoint}
                  insertPointEntryId={insertPointEntryId}
                  movingEntryId={movingEntryId}
                  onStartMove={handleStartMove}
                  onSelectMoveTarget={handleSelectMoveTarget}
                  swappingEntryId={swappingEntryId}
                  onStartSwap={handleStartSwap}
                  onSelectSwapTarget={handleSelectSwapTarget}
                  linkingEntryId={linkingEntryId}
                  linkingAnchorGroupId={linkingEntry ? linkingEntry.group_id : null}
                  onStartLink={handleStartLink}
                  onSelectLinkTarget={handleSelectLinkTarget}
                  selectedLinkTargets={selectedLinkTargets}
                  onUnlink={handleUnlinkEntry}
                  groupsEnabled={groupsEnabled}
                  onSendToKarafun={handleSendToKarafun}
                  karafunSyncActive={isSyncing}
                  batchSelectMode={batchSelectMode}
                  batchSelectedIds={batchSelectedIds}
                  batchAnchorGroupId={batchAnchorGroupId}
                  onBatchToggle={handleBatchToggle}
                  onBatchLongPress={handleBatchLongPress}
                  onLinkLongPressConfirm={handleLinkLongPressConfirm}
                />
              );

              // Walk the queue, clustering consecutive entries that share a
              // group_id inside a single purple-bordered wrapper. Groups are
              // always contiguous in the DATA layer (enforced server-side on
              // link), but during natural rotation a group can split visually
              // (one member rotates to the bottom while another stays up top).
              // We render each contiguous run as its own cluster — keys MUST
              // include the first member's id so two clusters with the same
              // group_id don't collide in React's reconciliation (which
              // previously caused stale-DOM visual hangs after advance).
              const out = [];
              let i = 0;
              let prevWasGroup = false;
              while (i < queue.length) {
                const entry = queue[i];
                const gid = groupsEnabled ? entry.group_id : null;
                if (gid) {
                  const members = [];
                  while (i < queue.length && (groupsEnabled ? queue[i].group_id : null) === gid) {
                    members.push({ entry: queue[i], index: i });
                    i++;
                  }
                  out.push(
                    <div
                      key={`grp-${gid}-${members[0].entry.id}`}
                      data-testid={`group-cluster-${gid}`}
                      className={`border-2 border-purple-500 rounded-lg p-2 mb-3 bg-purple-950/10 ${
                        prevWasGroup ? 'mt-3' : ''
                      }`}
                    >
                      {members.map(m => renderEntry(m.entry, m.index, true))}
                    </div>
                  );
                  prevWasGroup = true;
                } else {
                  out.push(renderEntry(entry, i, false));
                  i++;
                  prevWasGroup = false;
                }
              }
              return out;
            })()
          )}
        </div>

        {/* ── Deleted Entries (expandable section) ───────────────────── */}
        <div className="mt-4 border-t border-gray-700 pt-4">
          <button
            data-testid="deleted-entries-toggle"
            onClick={() => setDeletedExpanded(v => !v)}
            className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
          >
            <span>🗑️ Deleted Entries {deletedEntries.length > 0 ? `(${deletedEntries.length})` : ''}</span>
            <span className="text-gray-300">{deletedExpanded ? '▲' : '▼'}</span>
          </button>

          {deletedExpanded && (
            <div className="mt-3 space-y-2" data-testid="deleted-entries-panel">
              {/* ── Fix #44: Batch-select toggle / banner ──────────────── */}
              {deletedEntries.length >= 2 && (
                !deletedBatchSelectMode ? (
                  <div className="flex justify-end">
                    <button
                      data-testid="deleted-batch-select-enter-btn"
                      onClick={enterDeletedBatchMode}
                      className="bg-purple-700 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2"
                      title="Select multiple deleted entries to permanently delete at once"
                    >
                      <span>✓</span>
                      <span>Select</span>
                    </button>
                  </div>
                ) : (
                  <div
                    data-testid="deleted-batch-select-banner"
                    className="p-2 rounded-lg border border-purple-500 bg-purple-900/40 flex items-center justify-between gap-3"
                  >
                    <div className="text-purple-100 text-xs sm:text-sm flex-1 min-w-0">
                      <span className="font-semibold">Select Mode </span>
                      <span className="font-bold text-white">({deletedBatchSelectedIds.size} selected)</span>
                      <span className="text-purple-200/80 block sm:inline sm:ml-2 text-xs">
                        Tap entries to select.
                      </span>
                    </div>
                    <button
                      data-testid="deleted-batch-select-cancel-btn"
                      onClick={exitDeletedBatchMode}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0"
                    >
                      Cancel
                    </button>
                  </div>
                )
              )}

              {deletedEntries.length === 0 ? (
                <p className="text-gray-400 text-center py-3 text-sm">
                  No deleted entries in this session.
                </p>
              ) : (
                (() => {
                  // Render deleted entries. Removed the inline '🔗 was grouped'
                  // badge and the per-row deleted_reason text per host request
                  // 2026-10-27.
                  //
                  // Group clustering (host request 2026-10-27, second pass):
                  // bucket entries by `had_group_id` using first-seen position
                  // so split-apart group members (deleted at different times)
                  // visually rejoin inside a single purple-bordered cluster.
                  // Frontend-only analogue of the backend's live-queue
                  // `_compact_split_groups` — no DB mutation, pure render.
                  const renderDeletedRow = (de) => {
                    const isSelected = deletedBatchSelectMode && deletedBatchSelectedIds.has(de.id);
                    const rowBorder = isSelected
                      ? 'border-purple-500 ring-2 ring-purple-500/40'
                      : deletedBatchSelectMode
                        ? 'border-gray-600 hover:border-purple-400/60 cursor-pointer'
                        : 'border-gray-700';
                    return (
                    <div
                      key={de.id}
                      data-testid={`deleted-entry-${de.id}`}
                      data-selected={isSelected ? 'true' : 'false'}
                      onClick={deletedBatchSelectMode ? () => toggleDeletedBatchSelect(de.id) : undefined}
                      className={`bg-gray-900/60 border rounded-lg p-3 flex items-center justify-between gap-3 transition-all ${rowBorder}`}
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        {deletedBatchSelectMode && (
                          <div
                            className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? 'bg-purple-600 border-purple-500' : 'border-gray-500'
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-white truncate flex items-center gap-2 flex-wrap">
                            <span>{de.user_name}</span>
                            {de.is_admin_managed_regular ? (
                              <span
                                className="inline-flex items-center text-amber-100 bg-amber-700/40 border border-amber-500/50 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wide flex-shrink-0"
                                title="Admin-managed user — persists across sessions with song history"
                              >
                                Admin
                              </span>
                            ) : de.is_admin_added && (
                              <span className="inline-flex items-center text-slate-300 bg-slate-700/60 border border-slate-500/50 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wide flex-shrink-0">
                                Admin
                              </span>
                            )}
                          </div>
                          <div className="text-gray-300 text-sm truncate">
                            {de.song_title} — {de.song_artist}
                          </div>
                        </div>
                      </div>
                      {/* Per-row ⋯ menu hidden while in batch-select mode */}
                      {!deletedBatchSelectMode && (
                        <div className="relative flex-shrink-0">
                          <button
                            data-testid={`deleted-entry-menu-btn-${de.id}`}
                            onClick={(e) => { e.stopPropagation(); setDeletedRowMenuId(deletedRowMenuId === de.id ? null : de.id); }}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all"
                          >
                            ⋯
                          </button>
                          {deletedRowMenuId === de.id && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setDeletedRowMenuId(null)}
                              />
                              <div className="absolute right-0 mt-1 w-48 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 overflow-hidden">
                                <button
                                  data-testid={`restore-entry-btn-${de.id}`}
                                  onClick={() => handleOpenRestoreModal(de)}
                                  className="w-full text-left px-4 py-3 text-white text-sm font-semibold hover:bg-green-700 transition-all"
                                >
                                  ♻️ Restore to Queue
                                </button>
                                <button
                                  data-testid={`permanently-delete-btn-${de.id}`}
                                  onClick={() => handlePermanentlyDelete(de)}
                                  className="w-full text-left px-4 py-3 text-white text-sm font-semibold hover:bg-red-700 transition-all border-t border-gray-700"
                                >
                                  🗑️ Delete Permanently
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  };

                  const out = [];
                  // Visual-compact: bucket entries by `had_group_id`, keeping
                  // insertion order for both the buckets and the non-grouped
                  // entries. First occurrence of a group fixes its slot in the
                  // list; subsequent members (even if deleted at different
                  // times, so returned non-contiguously) fold into the same
                  // purple cluster. Frontend-only analogue to the backend's
                  // `_compact_split_groups` for the live queue. Host request
                  // 2026-10-27.
                  const buckets = [];
                  const groupBucket = new Map();
                  for (const de of deletedEntries) {
                    const gid = de.had_group_id;
                    if (!gid) {
                      buckets.push({ kind: 'single', entry: de });
                      continue;
                    }
                    let b = groupBucket.get(gid);
                    if (!b) {
                      b = { kind: 'group', gid, members: [de] };
                      groupBucket.set(gid, b);
                      buckets.push(b);
                    } else {
                      b.members.push(de);
                    }
                  }
                  for (const b of buckets) {
                    if (b.kind === 'group') {
                      out.push(
                        <div
                          key={`del-grp-${b.gid}-${b.members[0].id}`}
                          data-testid={`deleted-group-cluster-${b.gid}`}
                          className="border-2 border-purple-500 rounded-lg p-2 bg-purple-950/10 space-y-2"
                        >
                          {b.members.map(m => renderDeletedRow(m))}
                        </div>
                      );
                    } else {
                      out.push(renderDeletedRow(b.entry));
                    }
                  }
                  return out;
                })()
              )}

              {/* ── Fix #44: Bottom action bar — only visible in select
                    mode when ≥1 entry is selected ─────────────────────── */}
              {deletedBatchSelectMode && deletedBatchSelectedIds.size > 0 && (
                <div className="pt-2">
                  <button
                    data-testid="deleted-batch-delete-btn"
                    onClick={() => setShowDeletedBatchDeleteConfirm(true)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg text-sm sm:text-base font-semibold transition-all"
                  >
                    🗑️ Delete Permanently ({deletedBatchSelectedIds.size})
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Fix #44: Deleted-entries batch delete confirmation modal ── */}
        {showDeletedBatchDeleteConfirm && (() => {
          const selected = deletedEntries.filter(e => deletedBatchSelectedIds.has(e.id));
          return (
            <>
              <div
                className="fixed inset-0 z-40 bg-black bg-opacity-70"
                onClick={() => setShowDeletedBatchDeleteConfirm(false)}
              />
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div
                  data-testid="deleted-batch-delete-confirm-modal"
                  className="bg-gray-800 border-2 border-red-500 rounded-xl shadow-2xl p-6 max-w-md w-full pointer-events-auto max-h-[80vh] overflow-y-auto"
                >
                  <div className="text-white font-bold mb-3 text-lg">
                    🗑️ Permanently delete {selected.length} entr{selected.length === 1 ? 'y' : 'ies'}?
                  </div>
                  <div className="text-gray-300 text-sm mb-4">
                    This cannot be undone.
                  </div>
                  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 mb-4 max-h-60 overflow-y-auto space-y-1">
                    {selected.map(e => (
                      <div key={e.id} className="text-gray-200 text-sm truncate">
                        <span className="font-semibold">{e.user_name}</span>
                        <span className="text-gray-400"> — {e.song_title}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <button
                      data-testid="deleted-batch-delete-confirm-btn"
                      onClick={handleDeletedBatchDeleteConfirm}
                      className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg text-base font-semibold transition-all"
                    >
                      🗑️ Delete Permanently ({selected.length})
                    </button>
                    <button
                      data-testid="deleted-batch-delete-cancel-btn"
                      onClick={() => setShowDeletedBatchDeleteConfirm(false)}
                      className="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg text-base font-semibold transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* KaraFun Removed Songs section moved to the Update Library popup
            (below Upload History, above Close) — 2026-09-20 per host request
            to consolidate library management in one place. */}
      </div>

      {/* ── Link-Confirm Popover (per-entry Link Mode, opens on long-press
            when ≥1 target is staged). Lets admin confirm anywhere in the
            queue without scrolling back to the link banner. ─────────── */}
      {linkConfirmPopover && linkingEntry && selectedLinkTargets.size > 0 && (() => {
        // Build the full list of entries that'll be linked: anchor + all
        // staged targets, sorted by position. Matches the batch-select
        // confirm modal format so admins see a consistent UI.
        const allLinked = [
          linkingEntry,
          ...queue.filter(e => selectedLinkTargets.has(e.id)),
        ].sort((a, b) => (a.position || 0) - (b.position || 0));
        return (
          <>
            <div
              className="fixed inset-0 z-40 bg-black bg-opacity-50"
              onClick={() => setLinkConfirmPopover(null)}
              data-testid="link-confirm-popover-backdrop"
            />
            <div
              data-testid="link-confirm-popover"
              className="fixed z-50 bg-gray-800 border-2 border-cyan-500 rounded-xl shadow-2xl p-4"
              style={{
                left: `${linkConfirmPopover.x}px`,
                top: `${linkConfirmPopover.y}px`,
                minWidth: '320px',
                maxWidth: '420px',
              }}
            >
              <div className="text-white font-bold mb-1 text-lg">
                🔗 Confirm Link
              </div>
              <div className="text-gray-300 text-sm mb-2">
                Linking <span className="font-bold text-white">{allLinked.length}</span> entries:
              </div>
              <div className="max-h-40 overflow-y-auto bg-gray-900/60 border border-gray-700 rounded-lg p-2 mb-4 text-sm">
                {allLinked.map(e => (
                  <div key={e.id} className="text-gray-200 flex items-center gap-2">
                    <span className="text-gray-500">#{e.position}</span>
                    <span>{e.user_name}</span>
                    {e.group_id && (
                      <span className="text-purple-300 text-xs">🔗 grouped</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <button
                  data-testid="link-confirm-popover-confirm-btn"
                  onClick={() => {
                    setLinkConfirmPopover(null);
                    handleConfirmLinkBatch();
                  }}
                  className="w-full bg-cyan-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-cyan-700 transition-all"
                >
                  🔗 Confirm Link
                </button>
                <button
                  data-testid="link-confirm-popover-cancel-btn"
                  onClick={() => setLinkConfirmPopover(null)}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Batch-Select Action Menu (global, opens on long-press) ── */}
      {batchActionMenu && (        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={() => setBatchActionMenu(null)}
            data-testid="batch-action-menu-backdrop"
          />
          <div
            data-testid="batch-action-menu"
            className="fixed z-50 bg-gray-800 border-2 border-purple-500 rounded-xl shadow-2xl p-4"
            style={{
              left: `${batchActionMenu.x}px`,
              top: `${batchActionMenu.y}px`,
              minWidth: '320px',
              maxWidth: '420px',
            }}
          >
            <div className="text-white font-bold mb-3 text-lg">
              Batch Actions ({batchSelectedIds.size} selected)
            </div>
            <div className="space-y-2">
              <button
                data-testid="batch-link-btn"
                onClick={() => {
                  setBatchActionMenu(null);
                  if (batchSelectedIds.size < 2) {
                    setError("Select at least 2 entries to link.");
                    return;
                  }
                  setShowBatchLinkConfirm(true);
                }}
                disabled={!groupsEnabled || batchSelectedIds.size < 2}
                className={`w-full px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                  !groupsEnabled || batchSelectedIds.size < 2
                    ? 'bg-cyan-900/40 text-cyan-300/60 cursor-not-allowed'
                    : 'bg-cyan-600 text-white hover:bg-cyan-700'
                }`}
              >
                🔗 Link Selected ({batchSelectedIds.size})
              </button>
              <button
                data-testid="batch-delete-btn"
                onClick={() => {
                  setBatchActionMenu(null);
                  setShowBatchDeleteConfirm(true);
                }}
                className="w-full bg-red-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-red-700 transition-all"
              >
                🗑️ Delete Selected ({batchSelectedIds.size})
              </button>
              <button
                onClick={() => setBatchActionMenu(null)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Absorb-into-group confirmation (admin tapped a grouped entry
            while solos were already staged) ─────────────────────────── */}
      {pendingGroupAbsorb && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-70"
            onClick={handleAbsorbCancel}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div
              data-testid="batch-absorb-confirm-modal"
              className="bg-gray-800 border-2 border-purple-500 rounded-xl shadow-2xl p-6 max-w-md w-full pointer-events-auto"
            >
              <div className="text-white font-bold mb-3 text-lg">
                🔗 Link into existing group?
              </div>
              <div className="text-gray-300 text-sm mb-4">
                <span className="font-bold text-white">{pendingGroupAbsorb.groupedEntry.user_name}</span>{' '}
                is in a group. Linking will add all <span className="font-bold text-white">{pendingGroupAbsorb.currentSelectionIds.size}</span> currently-selected{' '}
                {pendingGroupAbsorb.currentSelectionIds.size === 1 ? 'entry' : 'entries'} into that group.
                <div className="mt-2 text-gray-400 text-xs">
                  This will happen immediately — selection mode will exit.
                </div>
              </div>
              <div className="space-y-2">
                <button
                  data-testid="batch-absorb-confirm-btn"
                  onClick={handleAbsorbConfirm}
                  className="w-full bg-cyan-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-cyan-700 transition-all"
                >
                  🔗 Link Into Group
                </button>
                <button
                  data-testid="batch-absorb-cancel-btn"
                  onClick={handleAbsorbCancel}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Batch Link Confirmation Modal ────────────────────────────── */}
      {showBatchLinkConfirm && (() => {
        const selectedEntries = queue.filter(e => batchSelectedIds.has(e.id))
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        const targetIntoExistingGroup = !!batchAnchorGroupId;
        return (
          <>
            <div
              className="fixed inset-0 z-40 bg-black bg-opacity-70"
              onClick={() => setShowBatchLinkConfirm(false)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <div
                data-testid="batch-link-confirm-modal"
                className="bg-gray-800 border-2 border-cyan-500 rounded-xl shadow-2xl p-6 max-w-md w-full pointer-events-auto"
              >
                <div className="text-white font-bold mb-3 text-lg">
                  🔗 Link {selectedEntries.length} entries?
                </div>
                <div className="text-gray-300 text-sm mb-2">
                  {targetIntoExistingGroup
                    ? <>Adding all selected entries into the existing group.</>
                    : <>Creating a new group from these entries.</>
                  }
                </div>
                <div className="max-h-40 overflow-y-auto bg-gray-900/60 border border-gray-700 rounded-lg p-2 mb-4 text-sm">
                  {selectedEntries.map(e => (
                    <div key={e.id} className="text-gray-200 flex items-center gap-2">
                      <span className="text-gray-500">#{e.position}</span>
                      <span>{e.user_name}</span>
                      {e.group_id && (
                        <span className="text-purple-300 text-xs">🔗 grouped</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <button
                    data-testid="batch-link-confirm-btn"
                    onClick={handleBatchLinkConfirm}
                    className="w-full bg-cyan-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-cyan-700 transition-all"
                  >
                    🔗 Confirm Link
                  </button>
                  <button
                    onClick={() => setShowBatchLinkConfirm(false)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Batch Delete Confirmation Modal ──────────────────────────── */}
      {showBatchDeleteConfirm && (() => {
        const selectedEntries = queue.filter(e => batchSelectedIds.has(e.id))
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        return (
          <>
            <div
              className="fixed inset-0 z-40 bg-black bg-opacity-70"
              onClick={() => setShowBatchDeleteConfirm(false)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <div
                data-testid="batch-delete-confirm-modal"
                className="bg-gray-800 border-2 border-red-500 rounded-xl shadow-2xl p-6 max-w-md w-full pointer-events-auto"
              >
                <div className="text-white font-bold mb-3 text-lg">
                  🗑️ Delete {selectedEntries.length} entries?
                </div>
                <div className="text-gray-300 text-sm mb-2">
                  This will remove all listed entries from the queue. Admin-added
                  entries can be restored from the Deleted panel; user entries are
                  permanently removed.
                </div>
                <div className="max-h-40 overflow-y-auto bg-gray-900/60 border border-gray-700 rounded-lg p-2 mb-4 text-sm">
                  {selectedEntries.map(e => (
                    <div key={e.id} className="text-gray-200 flex items-center gap-2">
                      <span className="text-gray-500">#{e.position}</span>
                      <span>{e.user_name}</span>
                      <span className="text-gray-500 text-xs truncate">— {e.song_title}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <button
                    data-testid="batch-delete-confirm-btn"
                    onClick={handleBatchDeleteConfirm}
                    className="w-full bg-red-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-red-700 transition-all"
                  >
                    🗑️ Delete All
                  </button>
                  <button
                    onClick={() => setShowBatchDeleteConfirm(false)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-base font-semibold hover:bg-gray-600 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Edit Modal */}
      {editingEntry && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-70"
            onClick={handleCancelEdit}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-gray-800 rounded-lg shadow-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto pointer-events-auto">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center flex-wrap gap-x-3 gap-y-2">
                <span>{editingEntryIsRestore ? '♻️ Restore Entry' : '✏️ Edit Entry'}</span>
                {!editingEntryIsRestore && editingEntry && editingEntry.user_id && (
                  editingEntry.admin_name ? (
                    <button
                      type="button"
                      data-testid="edit-entry-admin-name"
                      onClick={() => {
                        setEditAdminName(editingEntry.admin_name || '');
                        setAdminNameError('');
                        setShowAdminNameField(true);
                      }}
                      title="Click to edit admin name"
                      className="inline-flex items-center gap-2 leading-none bg-emerald-700/40 border border-emerald-500/60 hover:bg-emerald-700/60 hover:border-emerald-400 text-emerald-100 px-3 py-1 rounded-lg text-base font-semibold transition-colors"
                    >
                      <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
                        Admin Name
                      </span>
                      <span className="text-white text-lg font-bold">
                        {editingEntry.admin_name}
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="edit-entry-admin-name-add"
                      onClick={() => {
                        setEditAdminName('');
                        setAdminNameError('');
                        setShowAdminNameField(true);
                      }}
                      title="Click to add admin name"
                      className="inline-flex items-center gap-1.5 leading-none bg-emerald-900/30 border border-emerald-700/50 hover:bg-emerald-800/40 hover:border-emerald-500 text-emerald-300 hover:text-emerald-200 px-2.5 py-1 rounded-lg text-sm font-semibold transition-colors"
                    >
                      + Add admin name
                    </button>
                  )
                )}
                {!editingEntryIsRestore && editingEntry && editingEntry.admin_name && (
                  <span className="text-xs text-gray-400 italic font-normal normal-case">
                    (click badge to edit admin name)
                  </span>
                )}
              </h3>
              
              <div className="space-y-4">
                {/* Admin-name inline editor 2026-09-25 — opened by clicking
                    the admin-name badge in the header. Hidden on Restore
                    flow (no user_id). */}
                {!editingEntryIsRestore && editingEntry && editingEntry.user_id && showAdminNameField && (
                  <div data-testid="admin-name-inline-editor" className="bg-emerald-950/30 border border-emerald-600/50 rounded-lg p-3">
                    <label className="block text-xs uppercase tracking-widest font-bold text-emerald-300 mb-2">
                      Admin Name <span className="text-gray-400 normal-case tracking-normal font-normal">(real-name label — admin-only)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        data-testid="admin-name-input"
                        value={editAdminName}
                        onChange={(e) => setEditAdminName(e.target.value)}
                        placeholder="e.g. Sarah (red hair)"
                        maxLength={80}
                        className="input flex-1 px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSaveAdminName(); }
                          if (e.key === 'Escape') { setShowAdminNameField(false); setAdminNameError(''); }
                        }}
                        autoFocus
                      />
                      <button
                        type="button"
                        data-testid="admin-name-save-btn"
                        disabled={savingAdminName || (editAdminName.trim() === (editingEntry.admin_name || ''))}
                        onClick={handleSaveAdminName}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingAdminName ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        data-testid="admin-name-cancel-btn"
                        disabled={savingAdminName}
                        onClick={() => { setShowAdminNameField(false); setAdminNameError(''); }}
                        className="bg-gray-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-gray-500 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {editingEntry.admin_name && (
                      <button
                        type="button"
                        data-testid="admin-name-clear-btn"
                        disabled={savingAdminName}
                        onClick={() => setEditAdminName('')}
                        className="text-xs text-gray-400 hover:text-white mt-2 underline"
                      >
                        Clear (removes the admin name label)
                      </button>
                    )}
                    {adminNameError && (
                      <div className="text-red-400 text-sm mt-2" data-testid="admin-name-error">
                        {adminNameError}
                      </div>
                    )}
                  </div>
                )}

                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    User Name
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="input w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter name"
                  />
                </div>

                {/* Song Search */}
                <div className="relative">
                  <label className="block text-sm font-medium text-white mb-2">
                    Search for Song
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="input w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="Search by song title or artist..."
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-11 text-gray-400">
                      Searching...
                    </div>
                  )}

                  {/* Search Results Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div className="absolute z-[60] w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((song, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectSong(song)}
                          className="px-4 py-3 hover:bg-gray-600 cursor-pointer border-b border-gray-600 last:border-b-0"
                        >
                          <div className="font-semibold text-white">{song.title}</div>
                          <div className="text-sm text-gray-300">{song.artist}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                    <div className="absolute z-[60] w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg px-4 py-3 text-gray-400">
                      No songs found
                    </div>
                  )}
                </div>

                {/* Select from user history (Fix #23 follow-up) */}
                {editingEntry && editingEntry.id && (
                  <div className="relative">
                    <button
                      type="button"
                      data-testid="open-history-dropdown-admin"
                      onClick={openAdminHistoryDropdown}
                      disabled={isFetchingAdminHistory}
                      className="w-full text-left px-4 py-2 bg-purple-700/60 border border-purple-500 rounded-lg text-sm text-white hover:bg-purple-600 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <span aria-hidden="true">🎤</span>
                        {isFetchingAdminHistory ? 'Loading history…' : 'Select a song from user history'}
                      </span>
                      <span aria-hidden="true" className="text-purple-200">▾</span>
                    </button>
                    {showAdminHistoryDropdown && (
                      <>
                        <div
                          className="fixed inset-0 z-[55]"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setShowAdminHistoryDropdown(false);
                          }}
                        />
                        <div
                          data-testid="history-dropdown-admin"
                          className="absolute z-[60] w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {adminHistoryResults.length === 0 ? (
                            <div className="px-4 py-3 text-gray-400 text-sm">
                              No history for this user yet.
                            </div>
                          ) : (
                            adminHistoryResults.map((h, index) => (
                              <HistoryDropdownRow
                                key={`${h.song_id ?? h.song_title}-${index}`}
                                item={h}
                                index={index}
                                testIdPrefix="history-item-admin"
                                onSelect={handleAdminSelectHistorySong}
                                onLongPress={handleAdminHistoryLongPress}
                                className="px-4 py-3 hover:bg-gray-600 cursor-pointer border-b border-gray-600 last:border-b-0"
                              />
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <HistoryDeleteConfirmModal
                  item={adminHistoryPendingDelete}
                  subjectLabel={`${(editingEntry && editingEntry.user_name) || 'this user'}'s`}
                  isSubmitting={isDeletingAdminHistory}
                  onConfirm={handleConfirmDeleteAdminHistory}
                  onCancel={() => setAdminHistoryPendingDelete(null)}
                />

                {/* Selected Song Display */}
                {selectedSong && (
                  <div className="bg-blue-900/30 border border-blue-400 rounded-lg p-4">
                    <div className="text-sm text-blue-300 mb-1">Selected Song:</div>
                    <div className="font-semibold text-white">{selectedSong.title}</div>
                    <div className="text-gray-300">{selectedSong.artist}</div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSubmitEdit}
                    className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all"
                  >
                    {editingEntryIsRestore ? '♻️ Restore to Queue' : '💾 Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-gray-500 transition-all"
                  >
                    Cancel
                  </button>
                </div>

                {/* Change Pitch (admin-only override) — hidden on Restore
                    flow since the entry isn't live yet. */}
                {!editingEntryIsRestore && editingEntry?.id && (
                  <button
                    data-testid="admin-change-pitch-btn"
                    onClick={() => openAdminPitchModal(editingEntry)}
                    className="w-full mt-1 bg-purple-700/40 border border-purple-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-purple-700/60 transition-all"
                  >
                    🎚️ Change Pitch
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Admin Change-Pitch sub-modal */}
      {adminPitchEntry && (
        <div
          data-testid="admin-pitch-modal-backdrop"
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeAdminPitchModal(); }}
        >
          <div
            data-testid="admin-pitch-modal"
            className="bg-gray-800 rounded-lg shadow-2xl p-6 max-w-sm w-full border-2 border-purple-500"
          >
            <h3 className="text-xl font-bold text-white mb-1 text-center">Adjust Pitch</h3>
            <p className="text-gray-400 text-sm text-center mb-1">
              {adminPitchEntry.user_name}
            </p>
            <p className="text-gray-500 text-xs text-center mb-5 italic">
              Shift this song up or down in semitones (max ±6).
            </p>

            <div className="flex items-center justify-center gap-4 mb-3">
              <button
                data-testid="admin-pitch-step-down"
                onClick={() => setAdminPitchDraft((v) => Math.max(-6, Number(v) - 1))}
                disabled={adminPitchLoading || adminPitchDraft <= -6}
                className="w-14 h-14 rounded-full text-3xl font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Lower pitch by one semitone"
              >
                −
              </button>
              <div
                data-testid="admin-pitch-value"
                className="min-w-[5rem] text-center text-4xl font-bold text-purple-300 tabular-nums"
              >
                {adminPitchDraft > 0 ? `+${adminPitchDraft}` : adminPitchDraft}
              </div>
              <button
                data-testid="admin-pitch-step-up"
                onClick={() => setAdminPitchDraft((v) => Math.min(6, Number(v) + 1))}
                disabled={adminPitchLoading || adminPitchDraft >= 6}
                className="w-14 h-14 rounded-full text-3xl font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Raise pitch by one semitone"
              >
                +
              </button>
            </div>
            <p className="text-center text-xs text-gray-500 mb-5">
              {Number(adminPitchDraft) === 0
                ? 'Original key'
                : `${Math.abs(adminPitchDraft)} semitone${Math.abs(adminPitchDraft) === 1 ? '' : 's'} ${adminPitchDraft > 0 ? 'higher' : 'lower'}`}
            </p>

            <div className="flex gap-2">
              <button
                data-testid="admin-pitch-confirm-btn"
                onClick={handleAdminPitchSave}
                disabled={adminPitchLoading}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {adminPitchLoading ? 'Saving…' : 'Confirm'}
              </button>
              <button
                data-testid="admin-pitch-cancel-btn"
                onClick={closeAdminPitchModal}
                disabled={adminPitchLoading}
                className="flex-1 bg-gray-600 text-white py-2 rounded-lg font-semibold hover:bg-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KaraFun Login Modal */}
      {showLoginModal && (
        <>
          <div className="fixed inset-0 z-50 bg-black bg-opacity-70" onClick={() => setShowLoginModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-lg shadow-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto relative">
              <button 
                onClick={() => setShowLoginModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl font-bold"
                title="Close modal"
              >
                ×
              </button>
              <h3 className="text-2xl font-bold text-white mb-4">🔐 KaraFun Login Required</h3>
              <div className="mb-4 text-gray-300">
                Please log in to KaraFun to enable automatic song synchronization.
              </div>
              <KaraFunManualLogin onLoginComplete={async () => {
                setLoginComplete(true);
                setShowLoginModal(false);
                
                // Call backend to complete authentication and start sync
                try {
                  const response = await axios.post(`${API}/karafun/complete-login`, {}, {
                    timeout: 60000 // 60 second timeout to allow for authentication wait
                  });
                  setSuccess('KaraFun sync started successfully! WebSocket authenticated.');
                  // Fetch the updated sync status
                  await karafunSync.fetchSyncStatus();
                } catch (error) {
                  const errorMessage = error.response?.data?.detail || error.message;
                  if (errorMessage.includes('not connected') || errorMessage.includes('not authenticated')) {
                    setError('KaraFun sync failed: ' + errorMessage + '. Please try again.');
                    setLoginComplete(false);
                  } else {
                    setError('Failed to complete authentication: ' + errorMessage);
                  }
                }
              }} />
              
              {/* Stop Browser Service Button */}
              <div className="mt-6 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400 mb-3">
                  If the microservice is stuck or you need to start fresh:
                </p>
                <button
                  onClick={async () => {
                    if (window.confirm('Stop the browser service and log out of KaraFun? You will need to login again to restart sync.')) {
                      try {
                        await axios.post(`${API}/karafun/stop`);
                        setSuccess('Browser service stopped. KaraFun logged out.');
                        setShowLoginModal(false);
                        setLoginComplete(false);
                        await karafunSync.fetchSyncStatus();
                      } catch (error) {
                        setError('Failed to stop browser service: ' + (error.response?.data?.detail || error.message));
                      }
                    }
                  }}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold transition-colors"
                >
                  🛑 Stop Browser Service & Logout
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* WebSocket Handler Component */}
      <KaraFunWebSocketHandler 
        isActive={isSyncing}
        onStatusChange={handleWebSocketStatus}
      />
      
      {/* Catalog Upload Modal */}
      {showCatalogModal && (
        <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-70" onClick={() => !isUploading && setShowCatalogModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-lg shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-white mb-4">📚 Update Song Catalog</h3>
              
              {/* Current catalog info */}
              {catalogInfo && (
                <div className="mb-4 p-3 bg-gray-700 rounded-lg text-sm">
                  <div className="text-gray-300">
                    <div><strong>Current songs:</strong> {catalogInfo.in_memory_count?.toLocaleString() || 'Unknown'}</div>
                    <div><strong>Searchable:</strong> {catalogInfo.searchable_songs_count?.toLocaleString() || 'Unknown'}</div>
                    <div><strong>Source:</strong> {catalogInfo.source === 'mongodb' ? 'Database' : 'CSV File'}</div>
                    {catalogInfo.last_updated && (
                      <div><strong>Last updated:</strong> {new Date(catalogInfo.last_updated).toLocaleString()}</div>
                    )}
                    {catalogInfo.filename && (
                      <div><strong>File:</strong> {catalogInfo.filename}</div>
                    )}
                  </div>
                </div>
              )}
              
              <p className="text-gray-400 text-sm mb-4">
                Upload a new KaraFun catalog CSV file to replace the current song library. 
                The file should be semicolon-delimited with columns: Id, Title, Artist.
              </p>
              
              {isUploading ? (
                <div className="text-center py-4">
                  <div className="text-white mb-2">{uploadProgress}</div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleCatalogUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 font-semibold mb-3"
                    data-testid="update-library-select-file-btn"
                  >
                    📁 Select CSV File
                  </button>

                  {/* Upload History (last 20) — shipped 2026-09-11.
                      2026-10-21 host request: hide zero-diff rows (duplicate re-uploads
                      of the same CSV surface as added_count=0 AND removed_count=0 and
                      are noise). Filter at render time; backend keeps the raw history. */}
                  {(() => {
                    const visibleUploads = uploadHistory.filter(
                      u => !(u?.diff?.added_count === 0 && u?.diff?.removed_count === 0)
                    );
                    if (visibleUploads.length === 0) return null;
                    return (
                    <div className="mb-3 border-t border-gray-700 pt-3">
                      <div className="text-white font-semibold text-sm mb-2">
                        📜 Upload History ({visibleUploads.length})
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-2" data-testid="upload-history-list">
                        {/* Fix 2026-10: reverse at render time so oldest shows at top (chronological).
                            Backend keeps newest-first sort + limit=20 so we still see the most recent uploads. */}
                        {[...visibleUploads].reverse().map((u) => {
                          const isExpanded = expandedUploadId === u.id;
                          const added = u?.diff?.added_song_details || [];
                          const removed = u?.diff?.removed_song_details || [];
                          return (
                            <div key={u.id} className="bg-gray-700 rounded-lg p-2 text-xs" data-testid={`upload-history-row-${u.id}`}>
                              <div className="flex items-center justify-between">
                                <button
                                  onClick={() => setExpandedUploadId(isExpanded ? null : u.id)}
                                  className="text-left text-gray-200 flex-1 hover:text-white"
                                  data-testid={`upload-history-toggle-${u.id}`}
                                >
                                  <div className="font-mono">
                                    📅 {new Date(u.uploaded_at).toLocaleString()}
                                  </div>
                                  <div className="text-gray-400">
                                    <span className="text-green-400">+{u.diff?.added_count ?? 0}</span>
                                    {' · '}
                                    <span className="text-red-400">−{u.diff?.removed_count ?? 0}</span>
                                    {' · '}
                                    {u.song_count?.toLocaleString?.() || u.song_count} total
                                  </div>
                                </button>
                                <button
                                  onClick={() => downloadDiffTxt(u)}
                                  className="ml-2 bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs whitespace-nowrap"
                                  data-testid={`upload-history-download-${u.id}`}
                                  title="Download diff as .txt"
                                >
                                  📄 .txt
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="mt-2 border-t border-gray-600 pt-2 space-y-2">
                                  {added.length > 0 && (
                                    <div>
                                      <div className="text-green-400 font-semibold mb-1">
                                        Added ({added.length})
                                      </div>
                                      <div className="max-h-32 overflow-y-auto text-gray-300 font-mono">
                                        {added.map(s => (
                                          <div key={`a-${s.id}`}>{s.title} — {s.artist}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {removed.length > 0 && (
                                    <div>
                                      <div className="text-red-400 font-semibold mb-1">
                                        Removed ({removed.length})
                                      </div>
                                      <div className="max-h-32 overflow-y-auto text-gray-300 font-mono">
                                        {removed.map(s => (
                                          <div key={`r-${s.id}`}>{s.title} — {s.artist}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {added.length === 0 && removed.length === 0 && (
                                    <div className="text-gray-400 italic">No song-level changes recorded.</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}

                  {/* ── KaraFun Removed Songs (moved from queue-page bottom to
                      Update Library popup — 2026-09-20 per host request. Expandable
                      list of songs KaraFun has removed from its catalog. Admin
                      can download a detailed .txt or share the public URL —
                      same URL surfaced on the user view too. ─────────────── */}
                  <div className="mb-3 border-t border-gray-700 pt-3">
                    <button
                      data-testid="catalog-removals-toggle"
                      onClick={() => setCatalogRemovalsExpanded(v => !v)}
                      className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-all text-sm"
                    >
                      <span>📕 KaraFun Removed Songs</span>
                      <span className="text-gray-300">{catalogRemovalsExpanded ? '▲' : '▼'}</span>
                    </button>

                    {catalogRemovalsExpanded && (
                      <div className="mt-3 space-y-3" data-testid="catalog-removals-panel">
                        <p className="text-gray-400 text-xs leading-relaxed">
                          Songs KaraFun has removed from its live catalog (or that
                          silently failed to add). The public link below shows a
                          simple title/artist list you can share with singers —
                          it auto-updates when songs return to the catalog on the
                          next library upload.
                        </p>

                        {/* Status summary chips — click each to drill into that list.
                            False Positive intentionally omitted (2026-09-24, host request).
                            False positives are songs confirmed as still in-catalog, so an ambient
                            count of them is redundant. The signal is preserved
                            in debug_log events (admin_dismissed_catalog_removal)
                            with 7-day retention via debug_events TTL index. */}
                        <div className="flex gap-2 text-xs sm:text-sm">
                          <button
                            type="button"
                            data-testid="catalog-removals-badge-confirmed"
                            onClick={() => openCatalogRemovalsModal('admin-status', 'confirmed')}
                            disabled={(catalogRemovalsCounts.confirmed || 0) === 0}
                            className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 bg-red-900/40 border border-red-500/50 text-red-100 px-2 py-2 rounded hover:bg-red-900/70 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="View confirmed removals"
                          >
                            Confirmed: <strong>{catalogRemovalsCounts.confirmed || 0}</strong>
                          </button>
                          <button
                            type="button"
                            data-testid="catalog-removals-badge-suspected"
                            onClick={() => openCatalogRemovalsModal('admin-status', 'suspected')}
                            disabled={(catalogRemovalsCounts.suspected || 0) === 0}
                            className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 bg-amber-900/40 border border-amber-500/50 text-amber-100 px-2 py-2 rounded hover:bg-amber-900/70 hover:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="View suspected removals"
                          >
                            Suspected: <strong>{catalogRemovalsCounts.suspected || 0}</strong>
                          </button>
                          <button
                            type="button"
                            data-testid="catalog-removals-badge-restored"
                            onClick={() => openCatalogRemovalsModal('admin-status', 'restored')}
                            disabled={(catalogRemovalsCounts.restored || 0) === 0}
                            className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 bg-emerald-900/40 border border-emerald-500/50 text-emerald-100 px-2 py-2 rounded hover:bg-emerald-900/70 hover:border-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="View restored songs"
                          >
                            Restored: <strong>{catalogRemovalsCounts.restored || 0}</strong>
                          </button>
                        </div>

                        {/* View Full List — grouped Confirmed / Suspected / Restored (admin view) */}
                        <button
                          data-testid="admin-open-catalog-removals-modal-btn"
                          onClick={() => openCatalogRemovalsModal('admin-all', null)}
                          disabled={catalogRemovals.length === 0}
                          className="w-full bg-purple-700 hover:bg-purple-800 disabled:bg-gray-800 disabled:cursor-not-allowed disabled:text-gray-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                          title="View all removals grouped by status (same order as .txt download)"
                        >
                          View Full List
                        </button>

                        {/* Download detailed .txt */}
                        <button
                          data-testid="download-catalog-removals-btn"
                          onClick={downloadCatalogRemovalsTxt}
                          disabled={catalogRemovals.length === 0}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                          title="Download full detail (song IDs, statuses, dates) — admin only"
                        >
                          ⬇️ Download Full List (.txt)
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setShowCatalogModal(false)}
                    className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                    data-testid="update-library-close-btn"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Auth Reset Button - Long Press Required (Hidden by default, toggle SHOW_AUTH_RESET_BUTTON to enable) */}
      {SHOW_AUTH_RESET_BUTTON && <AdminAuthResetButton />}

      {/* Admin-side preview of the singer-facing removals pop-up. */}
      {catalogRemovalsModal.open && (
        <CatalogRemovalsModal
          onClose={closeCatalogRemovalsModal}
          variant={catalogRemovalsModal.variant}
          statusFilter={catalogRemovalsModal.statusFilter}
        />
      )}
    </div>
  );
}

const QueueDisplay = ({ queue, rotationInfo }) => {
  // The viewer's own group_id (if any). Found by locating their own entry —
  // the one where `can_edit` is true (server flags this for the device that
  // owns the entry). When the viewer isn't in a group, `myGroupId` is null
  // and we render the flat list exactly as before.
  const myEntry = queue.find(e => e.can_edit && e.group_id);
  const myGroupId = myEntry ? myEntry.group_id : null;

  // Modal state for the "Your Group" info popup. Local to this component
  // because nothing outside QueueDisplay needs to know about it.
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  const renderEntry = (entry, index) => (
    <div
      key={entry.id}
      data-testid={`queue-entry-${entry.id}`}
      className={`queue-entry p-4 rounded-lg priority-indicator ${
        entry.is_current
          ? 'priority-current bg-green-900/20'
          : entry.has_played
          ? 'priority-played !bg-[#1a1a1a] [&_.font-semibold]:!text-gray-400 [&_.text-gray-300]:!text-gray-500 [&_.position-badge]:!bg-none [&_.position-badge]:!bg-gray-700 [&_.position-badge]:!text-gray-300'
          : entry.priority_score === 0
          ? 'priority-new bg-blue-900/20'
          : 'priority-returning bg-yellow-900/20'
      }`}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0 max-w-[55%]">
          <div className="font-semibold text-white flex items-center gap-2 flex-wrap">
            <span className="position-badge">{index + 1}</span>
            <span className="truncate">{entry.user_name}</span>
            {entry.can_edit && (
              <span className="user-badge text-xs px-2 py-1 rounded whitespace-nowrap">
                YOU
              </span>
            )}
          </div>
          <div className="text-gray-300 mt-1 break-words">
            {entry.song_title} - {entry.song_artist}
          </div>
          {entry.add_failed && (
            <div
              data-testid={`add-failed-badge-user-${entry.id}`}
              className="mt-2 inline-flex items-start gap-1.5 text-red-100 bg-red-900/60 border border-red-500/60 px-2.5 py-1 rounded-md text-xs leading-snug max-w-full whitespace-normal"
              role="alert"
            >
              <span aria-hidden="true" className="mt-0.5">⚠</span>
              <span>This song is currently unavailable. Please select another song or see host for assistance.</span>
            </div>
          )}
        </div>
        <div className="text-right flex flex-col items-end gap-1 flex-shrink-0">
          {/* Position-based notifications - always at top of right column */}
          {entry.can_edit && !entry.has_played && index === 1 && (
            <span className="bg-orange-600 text-white text-xs px-2 py-1 rounded animate-pulse font-bold">
              🔔 You're Next!
            </span>
          )}
          {entry.can_edit && !entry.has_played && index === 2 && (
            <span className="bg-orange-600 text-white text-xs px-2 py-1 rounded">
              🔔 Coming Up!
            </span>
          )}
          {/* Fallback is_next logic for other cases */}
          {entry.is_next && !entry.has_played && index !== 1 && (
            <span className="bg-orange-600 text-white text-xs px-2 py-1 rounded animate-pulse font-bold">
              🔔 You're Next!
            </span>
          )}
          {entry.is_current && (
            <span className="text-green-500 font-semibold inline-flex items-center gap-1"><GiMusicalNotes className="text-blue-500" size={20} /> Current <GiMusicalNotes className="text-blue-500" size={20} /></span>
          )}
          {entry.has_played && (
            <span className="inline-flex items-center !text-white font-bold text-xs tracking-widest px-1.5 py-0.5 rounded bg-white/10 border border-white/20">
              PLAYED
            </span>
          )}
          {!entry.has_played && !entry.is_current && !entry.can_edit && (entry.total_turns || 0) === 0 && (
            <span
              data-testid={`first-turn-badge-user-${entry.id}`}
              className="inline-flex flex-col sm:flex-row gap-y-0.5 sm:gap-x-1 items-center leading-none text-emerald-100 bg-emerald-700/50 border border-emerald-500/60 px-2 py-1 rounded font-bold text-xs tracking-widest uppercase flex-shrink-0"
              title="Hasn't sung yet tonight"
            >
              <span>First</span>
              <span>Turn</span>
            </span>
          )}
          {entry.total_turns > 0 && (
            <div className="text-white text-base italic">
              {entry.total_turns === 1 ? '1 previous turn' : `${entry.total_turns} previous turns`}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // 2026-10-14 — Changed from "cluster only MY group" to "cluster
  // ALL groups" so users can visually see which patrons are grouped
  // together, matching the admin view's cluster behavior. The
  // "Your Group" info banner stays gated to the viewer's own group
  // only — other groups render without the banner (same purple
  // border + purple tint, no viewer-specific label).
  //
  // Mirrors the admin view's cluster pattern (line ~6227) line-for-line
  // on the border styling.
  //
  // 2026-10-14 (follow-up): wrap each group in a `py-2` container so
  // groups get extra visible breathing room above AND below from
  // adjacent entries. We can't rely on `my-X` on the box itself
  // because the parent's `space-y-3` applies margin-top via sibling
  // selector, and CSS vertical margins collapse — so adding `mb-3`
  // to the group just collapses with the next sibling's space-y
  // margin to the same 12px. Padding on a wrapper doesn't collapse,
  // so the group reliably gets +8px per side of visible separation.
  const renderClusters = () => {
    const out = [];
    let i = 0;
    while (i < queue.length) {
      const entry = queue[i];
      const gid = entry.group_id || null;
      if (gid) {
        const members = [];
        while (i < queue.length && queue[i].group_id === gid) {
          members.push({ entry: queue[i], index: i });
          i++;
        }
        const isMyGroup = !!myGroupId && gid === myGroupId;
        out.push(
          <div
            key={`grp-wrap-${gid}-${members[0].entry.id}`}
            className="py-2"
          >
            <div
              data-testid={isMyGroup ? `my-group-cluster-${gid}` : `group-cluster-${gid}`}
              className="border-2 border-purple-500 rounded-lg p-2 bg-purple-950/10 space-y-3"
            >
              {/* "Your Group" banner — ONLY on the viewer's own group.
                  Other groups get the border but no banner (correct —
                  the banner is a viewer-specific label, not a group
                  property). */}
              {isMyGroup && (
                <button
                  type="button"
                  onClick={() => setShowGroupInfo(true)}
                  data-testid="your-group-info-btn"
                  className="w-full text-left mb-2 px-3 py-2 rounded-md bg-purple-900/40 hover:bg-purple-900/60 transition-colors border border-purple-700/50"
                >
                  <div className="text-purple-200 font-semibold text-sm flex items-center gap-2 flex-wrap">
                    <span aria-hidden>🔗</span>
                    <span>Your Group</span>
                    <span className="text-purple-300/80 font-normal">
                      (Click For More Info)
                    </span>
                  </div>
                </button>
              )}
              {members.map(m => renderEntry(m.entry, m.index))}
            </div>
          </div>
        );
      } else {
        out.push(renderEntry(entry, i));
        i++;
      }
    }
    return out;
  };

  return (
    <div className="card rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-white">Current Queue</h2>
        <div className="text-lg font-semibold text-gray-300">
          {queue.length} {queue.length === 1 ? 'singer' : 'singers'}
        </div>
      </div>

      {queue.length === 0 ? (
        <p className="text-gray-400 text-center py-8">No one in queue yet. Be the first!</p>
      ) : (
        <div className="space-y-3">{renderClusters()}</div>
      )}

      {/* "Your Group" info popup — shown when the viewer taps the banner.
          Plain explainer text only; no destructive actions inside. */}
      {showGroupInfo && (
        <div
          data-testid="your-group-info-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowGroupInfo(false)}
        >
          <div
            className="bg-gray-900 border-2 border-purple-500 rounded-lg shadow-2xl p-8 max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-purple-200 mb-4 flex items-center gap-2">
              <span aria-hidden>🔗</span>
              Your Group
            </h3>
            <p className="text-gray-200 text-base leading-relaxed">
              You have been added to a group. This means that you and your
              friends won't be separated by new, incoming users and will
              stay together in the queue.
            </p>
            <p className="text-gray-300 text-base leading-relaxed mt-4">
              If you want to add more friends to your group, please let
              the host know.
            </p>
            <button
              type="button"
              data-testid="your-group-info-close"
              onClick={() => setShowGroupInfo(false)}
              className="mt-6 w-full bg-purple-600 hover:bg-purple-700 text-white text-base font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// NEW: Search-based song selection component
const SearchSongForm = ({ name, setName, onSubmit, userEntry, userQueuePosition, isLoading, onUpdatePitch, deviceFingerprint }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const searchTimeoutRef = useRef(null);
  // History dropdown state (Fix #23 follow-up, 2026-09-15)
  const [historyResults, setHistoryResults] = useState([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);

  // Pitch modal state
  const [isPitchModalOpen, setIsPitchModalOpen] = useState(false);
  const [pitchDraft, setPitchDraft] = useState(0);
  const [isSavingPitch, setIsSavingPitch] = useState(false);

  const isChangingSong = userEntry && userEntry.can_edit;
  const formTitle = isChangingSong ? "Change Your Song" : "Add Your Song";
  const buttonText = isChangingSong ? "Change Song" : "Add to Queue";
  const loadingText = isChangingSong ? "Updating..." : "Adding...";

  // Search songs with debouncing
  const searchSongs = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/songs/search?q=${encodeURIComponent(query)}&limit=50`);
      setSearchResults(response.data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedSong(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchSongs(query);
    }, 300);
  };

  const handleSelectSong = (song) => {
    setSelectedSong(song);
    setSearchQuery(`${song.title} - ${song.artist}`);
    setShowDropdown(false);
  };

  // Toggle user's song history dropdown.
  // Fix (2026-09-24): button now TOGGLES — if the dropdown is already open,
  // close it (no re-fetch). Previously the button click was consumed by the
  // backdrop first (which closed) and then re-fired on the button (which
  // reopened), so the button couldn't collapse the dropdown reliably.
  const openHistoryDropdown = async () => {
    if (showHistoryDropdown) {
      setShowHistoryDropdown(false);
      return;
    }
    if (!deviceFingerprint) return;
    setIsFetchingHistory(true);
    try {
      const response = await axios.get(
        `${API}/song-history?device_fingerprint=${encodeURIComponent(deviceFingerprint)}&limit=200`
      );
      setHistoryResults(Array.isArray(response.data) ? response.data : []);
      setShowHistoryDropdown(true);
      setShowDropdown(false);
    } catch (err) {
      console.error('History fetch error:', err);
      setHistoryResults([]);
      setShowHistoryDropdown(true);
    } finally {
      setIsFetchingHistory(false);
    }
  };

  // Adapt Model-3 history row → search-result shape, then reuse handleSelectSong.
  const handleSelectHistorySong = (h) => {
    handleSelectSong({
      id: h.song_id ? String(h.song_id) : undefined,
      title: h.song_title,
      artist: h.song_artist,
    });
    setShowHistoryDropdown(false);
  };

  // ── History delete (long-press → confirm → DELETE) ─────────────────
  const [historyPendingDelete, setHistoryPendingDelete] = useState(null);
  const [isDeletingHistory, setIsDeletingHistory] = useState(false);

  const handleHistoryLongPress = (row) => {
    setHistoryPendingDelete(row);
  };

  const handleConfirmDeleteHistory = async () => {
    if (!historyPendingDelete || !deviceFingerprint) return;
    setIsDeletingHistory(true);
    try {
      await axios.delete(
        `${API}/song-history/entry?device_fingerprint=${encodeURIComponent(deviceFingerprint)}`,
        {
          data: {
            song_id: historyPendingDelete.song_id ?? null,
            song_title: historyPendingDelete.song_title,
            song_artist: historyPendingDelete.song_artist,
          },
        }
      );
      // Optimistic: strip the row from local list.
      setHistoryResults((prev) => prev.filter((r) => (
        (r.song_id ?? null) !== (historyPendingDelete.song_id ?? null) ||
        r.song_title !== historyPendingDelete.song_title ||
        r.song_artist !== historyPendingDelete.song_artist
      )));
    } catch (err) {
      console.error('History delete error:', err);
    } finally {
      setIsDeletingHistory(false);
      setHistoryPendingDelete(null);
    }
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Use the input value in both cases so name edits go through even in
    // "Change Song" mode. Pre-population of `name` on edit open (line ~3845)
    // keeps this backward-compatible for name-untouched submissions.
    const submittedName = name;
    if (!submittedName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (!selectedSong) {
      alert('Please select a song from the search results');
      return;
    }
    // 2026-10-14 — carry the KaraFun catalog song_id end-to-end so the
    // backend doesn't have to re-resolve via title/artist string matching.
    // `selectedSong.id` is undefined when the user kept their previous
    // song (Change Song pre-fill at line ~7606 omits id intentionally);
    // in that case the backend falls back to find_karafun_song_id().
    onSubmit(e, selectedSong.title, selectedSong.artist, selectedSong.id);
    // Reset form
    setSearchQuery('');
    setSelectedSong(null);
    setSearchResults([]);
    setIsEditing(false);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  if (userEntry && !isEditing) {
    // POS-1 GUARD ONLY: users may change their own song at any position
    // EXCEPT position 1 (currently performing). Pos 2 is now allowed since
    // the backend syncs the change to KaraFun via remove + push.
    const canChangeSong = !userQueuePosition || userQueuePosition > 1;

    // Pitch UI — restored 2026-07-24 alongside the SetQueueItemOptions sync.
    // Singers can adjust pitch at any time before their song plays; backend
    // dispatches a live SetQueueItemOptionsRequest if the slot already
    // exists in KaraFun's queue, or bakes pitch into the slot at push time
    // otherwise.
    const pitchOffset = Number(userEntry.pitch_offset || 0);
    const canChangePitch = !!userEntry.can_edit;

    const handleOpenPitchModal = () => {
      setPitchDraft(pitchOffset);
      setIsPitchModalOpen(true);
    };
    const handleConfirmPitch = async () => {
      if (!onUpdatePitch) {
        setIsPitchModalOpen(false);
        return;
      }
      setIsSavingPitch(true);
      try {
        await onUpdatePitch(userEntry.id, pitchDraft);
        setIsPitchModalOpen(false);
      } finally {
        setIsSavingPitch(false);
      }
    };
    const handleCancelPitch = () => {
      setIsPitchModalOpen(false);
      setPitchDraft(pitchOffset);
    };
    const stepDown = () => setPitchDraft((v) => Math.max(-6, v - 1));
    const stepUp = () => setPitchDraft((v) => Math.min(6, v + 1));
    const formatPitch = (n) => (n > 0 ? `+${n}` : `${n}`);

    return (
      <div className="card rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Your Entry</h2>
        <div
          className="border border-green-500 rounded-lg p-4"
          style={{ backgroundColor: '#2a2a2a' }}
        >
          <h3 className="font-semibold text-green-500 text-xl">You're In The Queue!</h3>
          <p className="flex"><span className="text-white flex-shrink-0">Name:</span> <span className="text-gray-300 ml-2">{userEntry.user_name}</span></p>
          <p className="flex"><span className="text-white flex-shrink-0">Song:</span> <span className="text-gray-300 ml-2">{userEntry.song_title} - {userEntry.song_artist}</span></p>
          {pitchOffset !== 0 && (
            <p
              className="flex mt-1"
              data-testid="user-pitch-badge"
            >
              <span className="text-white flex-shrink-0">Pitch:</span>
              <span className="ml-2 inline-flex items-center gap-1 text-purple-300 font-semibold">
                {formatPitch(pitchOffset)} {pitchOffset === 1 || pitchOffset === -1 ? 'semitone' : 'semitones'}
              </span>
            </p>
          )}
          {userEntry.is_next && !userEntry.has_played && (
            <div className="mt-2 p-2 bg-orange-600 rounded" data-testid="user-next-badge">
              <span className="text-white font-semibold">🔔 You're up next! Get ready!</span>
            </div>
          )}
        </div>

        {/* Change Song Button - Only show if not at position 1 (currently performing) */}
        {canChangeSong && (
          <button
            data-testid="user-change-song-btn"
            onClick={() => {
              setName(userEntry.user_name); // Pre-fill name when editing
              // Pre-populate the song too, so name-only edits are possible
              // without re-searching (2026-09-15 fix — hosts had to change
              // names manually via admin edit because the form required a
              // song re-selection on every submit).
              setSelectedSong({
                id: undefined,
                title: userEntry.song_title,
                artist: userEntry.song_artist,
              });
              setSearchQuery(`${userEntry.song_title} - ${userEntry.song_artist}`);
              setIsEditing(true);
            }}
            className="btn btn-secondary w-full py-3 rounded-lg font-semibold mt-4 hover:bg-gray-800"
            style={{ backgroundColor: '#2a2a2a' }}
          >
            <span className="inline-flex items-center gap-2 text-lg"><IoMusicalNotes className="text-purple-700" size={24} /> Change Your Song <IoMusicalNotes className="text-purple-700" size={24} /></span>
          </button>
        )}

        {/* Change Pitch Button — available whenever the entry is editable
            (same rules as Change Song; pos-1 is blocked). */}
        {canChangePitch && (
          <button
            data-testid="user-change-pitch-btn"
            onClick={handleOpenPitchModal}
            className="w-full py-2 rounded-lg font-semibold mt-2 hover:bg-gray-800 transition-colors border-0"
            style={{ backgroundColor: '#2a2a2a' }}
          >
            <span className="text-base text-purple-400">
              Change Pitch{pitchOffset !== 0 ? ` (${formatPitch(pitchOffset)})` : ''}
            </span>
          </button>
        )}

        {/* Message when song change is locked (only when at pos 1 / currently performing) */}
        {!canChangeSong && (
          <p className="text-gray-400 text-base text-center mt-4">
            Song changes are locked while you're currently performing. Please see the host if you need assistance.
          </p>
        )}

        {/* Pitch Modal */}
        {isPitchModalOpen && (
          <div
            data-testid="pitch-modal-backdrop"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) handleCancelPitch(); }}
          >
            <div
              data-testid="pitch-modal"
              className="card rounded-lg shadow-2xl p-6 max-w-sm w-full border-2 border-purple-500"
            >
              <h3 className="text-xl font-bold text-white mb-1 text-center">Adjust Pitch</h3>
              <p className="text-gray-400 text-sm text-center mb-5">
                Shift your song up or down in semitones (max ±6).
              </p>

              <div className="flex items-center justify-center gap-4 mb-5">
                <button
                  data-testid="pitch-step-down"
                  onClick={stepDown}
                  disabled={pitchDraft <= -6}
                  className="btn btn-secondary w-14 h-14 rounded-full text-3xl font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Lower pitch by one semitone"
                >
                  −
                </button>
                <div
                  data-testid="pitch-value"
                  className="min-w-[5rem] text-center text-4xl font-bold text-purple-300 tabular-nums"
                >
                  {formatPitch(pitchDraft)}
                </div>
                <button
                  data-testid="pitch-step-up"
                  onClick={stepUp}
                  disabled={pitchDraft >= 6}
                  className="btn btn-secondary w-14 h-14 rounded-full text-3xl font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Raise pitch by one semitone"
                >
                  +
                </button>
              </div>
              <p className="text-center text-xs text-gray-500 mb-5">
                {pitchDraft === 0 ? 'Original key' : `${Math.abs(pitchDraft)} semitone${Math.abs(pitchDraft) === 1 ? '' : 's'} ${pitchDraft > 0 ? 'higher' : 'lower'}`}
              </p>

              <div className="flex gap-2">
                <button
                  data-testid="pitch-confirm-btn"
                  onClick={handleConfirmPitch}
                  disabled={isSavingPitch}
                  className="btn btn-primary flex-1 py-2 rounded-lg font-semibold disabled:opacity-50"
                >
                  {isSavingPitch ? 'Saving...' : 'Confirm'}
                </button>
                <button
                  data-testid="pitch-cancel-btn"
                  onClick={handleCancelPitch}
                  disabled={isSavingPitch}
                  className="btn btn-secondary flex-1 py-2 rounded-lg font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-3">
        <IoMusicalNotes className="text-purple-700" size={32} />
        <span>{formTitle}</span>
        <IoMusicalNotes className="text-purple-700" size={32} />
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-base font-medium text-white mb-2">
            Your Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Your Name"
            className="form-input w-full p-3 rounded-lg"
            required
          />
        </div>

        <div className="relative">
          <label className="block text-base font-medium text-white mb-2">
            Search for a Song
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            placeholder="Enter Song Title or Artist Name"
            className="form-input w-full p-3 rounded-lg"
            autoComplete="off"
          />
          
          {isSearching && (
            <div className="absolute right-3 top-11 text-gray-400">
              Searching...
            </div>
          )}

          {/* Dropdown Results */}
          {showDropdown && searchResults.length > 0 && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                className="fixed inset-0 z-10"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowDropdown(false);
                }}
              />
              
              {/* Results dropdown */}
              <div 
                className="absolute z-20 w-full mt-1 bg-gray-800 border-2 border-gray-600 rounded-lg shadow-2xl max-h-[28rem] overflow-y-auto"
                onMouseDown={(e) => e.stopPropagation()}
              >
                {searchResults.map((song, index) => (
                  <div
                    key={`${song.id}-${index}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectSong(song);
                    }}
                    className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                  >
                    <div className="font-semibold text-white">{song.title}</div>
                    <div className="text-sm text-gray-400">{song.artist}</div>
                    {song.year && (
                      <div className="text-xs text-gray-500 mt-1">
                        {song.year} {song.styles && `• ${song.styles}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Select from history — button + dropdown (Fix #23 follow-up) */}
          {deviceFingerprint && (
            <div className="relative mt-4">
              <button
                type="button"
                data-testid="open-history-dropdown-user"
                onClick={openHistoryDropdown}
                disabled={isFetchingHistory}
                className={`w-full text-left px-3 py-3 bg-purple-700/60 border border-purple-500 rounded-lg text-base text-white hover:bg-purple-600 transition-colors flex items-center justify-between ${showHistoryDropdown ? 'relative z-30' : ''}`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">🎤</span>
                  {isFetchingHistory ? 'Loading history…' : 'Select A Song From Your History'}
                </span>
                <span aria-hidden="true" className="text-purple-200">▾</span>
              </button>

              {showHistoryDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowHistoryDropdown(false);
                    }}
                  />
                  <div
                    data-testid="history-dropdown-user"
                    className="absolute z-20 w-full mt-1 bg-gray-800 border-2 border-gray-600 rounded-lg shadow-2xl max-h-[28rem] overflow-y-auto"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {historyResults.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        No songs in your history yet. Sing a song to start building your list.
                      </div>
                    ) : (
                      historyResults.map((h, index) => (
                        <HistoryDropdownRow
                          key={`${h.song_id ?? h.song_title}-${index}`}
                          item={h}
                          index={index}
                          testIdPrefix="history-item-user"
                          onSelect={handleSelectHistorySong}
                          onLongPress={handleHistoryLongPress}
                          className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                        />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <HistoryDeleteConfirmModal
            item={historyPendingDelete}
            subjectLabel="your"
            isSubmitting={isDeletingHistory}
            onConfirm={handleConfirmDeleteHistory}
            onCancel={() => setHistoryPendingDelete(null)}
          />

          {selectedSong && (
            <div className="mt-2 p-3 bg-green-900/30 border border-green-500 rounded-lg">
              <div className="text-sm text-green-500">✓ Selected:</div>
              <div className="font-semibold text-white">{selectedSong.title}</div>
              <div className="text-sm text-gray-300">{selectedSong.artist}</div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          {isEditing && (
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                setSearchQuery('');
                setSelectedSong(null);
                setSearchResults([]);
              }}
              className="btn btn-secondary flex-1 py-3 rounded-lg font-semibold"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || !selectedSong}
            onClick={(e) => {
              // Ensure reliable click handling
              const submittedName = (isEditing && userEntry) ? userEntry.user_name : name;
              if (!isLoading && selectedSong && submittedName && submittedName.trim()) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            className={`btn btn-primary py-3 rounded-lg font-semibold disabled:bg-gray-600 ${isEditing ? 'flex-1' : 'w-full'}`}
          >
            {isLoading ? loadingText : buttonText}
          </button>
        </div>
      </form>
    </div>
  );
};

// BACKUP: Original text-input form (saved for potential revert)
const UserFormTextInput = ({ name, setName, songTitle, setSongTitle, songArtist, setSongArtist, onSubmit, onUpdateSong, onSelectNextSong, userEntry, isLoading }) => {
  // Show "Change Your Song" if user has an entry they can edit, otherwise "Add Your Song"
  const isChangingSong = userEntry && userEntry.can_edit;
  const formTitle = isChangingSong ? "Change Your Song" : "Add Your Song";
  const buttonText = isChangingSong ? "Change Song" : "Add to Queue";
  const loadingText = isChangingSong ? "Updating..." : "Adding...";

  const [isEditing, setIsEditing] = useState(false);
  const [editSongTitle, setEditSongTitle] = useState('');
  const [editSongArtist, setEditSongArtist] = useState('');
  const [isSelectingNext, setIsSelectingNext] = useState(false);
  const [nextSongTitle, setNextSongTitle] = useState('');
  const [nextSongArtist, setNextSongArtist] = useState('');
  // Change-song double-tap guard (2026-08-21): prevents the user from
  // firing two `PUT /api/queue/update-song` requests in rapid
  // succession. Two overlapping requests could each register a KaraFun
  // pending-match on the same entry, and even with the backend
  // supersede-and-lock defense (see server.py + karafun_pw_ws_service.py
  // change-song race fix), keeping the second click from ever landing
  // is the simplest belt-and-suspenders. See
  // DB_LOAD_HYPOTHESIS_FOLLOWUPS.md "HELD — Singer-side change-song
  // duplicate-Add race" for the RCA.
  const [isSavingSongEdit, setIsSavingSongEdit] = useState(false);

  const handleEdit = () => {
    setEditSongTitle('');
    setEditSongArtist('');
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (isSavingSongEdit) return;
    if (editSongTitle.trim() && editSongArtist.trim()) {
      setIsSavingSongEdit(true);
      try {
        await onUpdateSong(editSongTitle.trim(), editSongArtist.trim());
      } finally {
        setIsEditing(false);
        // Cooldown: keep the guard armed for 1s after the request
        // completes so a stale finger-double-tap can't trigger a
        // second network call.
        setTimeout(() => setIsSavingSongEdit(false), 1000);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditSongTitle('');
    setEditSongArtist('');
  };

  const handleSelectNext = () => {
    setIsSelectingNext(true);
    setNextSongTitle('');
    setNextSongArtist('');
  };

  const handleSaveNextSong = () => {
    if (nextSongTitle.trim() && nextSongArtist.trim()) {
      onSelectNextSong(nextSongTitle.trim(), nextSongArtist.trim());
      setIsSelectingNext(false);
    }
  };

  const handleCancelNextSelection = () => {
    setIsSelectingNext(false);
    setNextSongTitle('');
    setNextSongArtist('');
  };

  if (userEntry) {
    return (
      <div className="card rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Your Entry</h2>
        
        {/* Current Queue Status */}
        <div
          className="border border-green-500 rounded-lg p-4 mb-4"
          style={{ backgroundColor: '#2a2a2a' }}
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-semibold text-green-500 text-xl">You're In The Queue!</h3>
              <p className="flex"><span className="text-white flex-shrink-0">Name:</span> <span className="text-gray-300 ml-2">{userEntry.user_name}</span></p>
              <p className="flex"><span className="text-white flex-shrink-0">Song:</span> <span className="text-gray-300 ml-2">{userEntry.song_title} - {userEntry.song_artist}</span></p>
              {userEntry.is_next && !userEntry.has_played && (
                <div className="mt-2 p-2 bg-orange-600 rounded">
                  <span className="text-white font-semibold">🔔 You're up next! Get ready!</span>
                </div>
              )}
            </div>
            {userEntry.can_edit && (
              <button
                onClick={handleEdit}
                className="btn btn-primary px-3 py-1 rounded text-sm"
              >
                {userEntry.has_played ? "Add Next Song" : "Change Song"}
              </button>
            )}
          </div>
          
          {isEditing && (
            <div className="mt-4 p-4 bg-black/50 rounded border border-blue-400">
              <h4 className="font-medium text-blue-400 mb-2">
                {userEntry.has_played ? "Add Your Next Song" : "Change Your Song"}
              </h4>
              {!userEntry.has_played && (
                <div className="mb-3 p-2 bg-yellow-600/20 border border-yellow-400 rounded">
                  <p className="text-yellow-300 text-sm">
                    ⚠️ Admin will be notified of this song change
                  </p>
                </div>
              )}
              <div className="mb-3">
                <label className="block text-sm font-medium text-white mb-2">
                  Song Title
                </label>
                <input
                  type="text"
                  value={editSongTitle}
                  onChange={(e) => setEditSongTitle(e.target.value)}
                  placeholder="Enter song title"
                  className="form-input w-full p-3 rounded-lg"
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-white mb-2">
                  Artist Name
                </label>
                <input
                  type="text"
                  value={editSongArtist}
                  onChange={(e) => setEditSongArtist(e.target.value)}
                  placeholder="Enter artist name"
                  className="form-input w-full p-3 rounded-lg"
                />
              </div>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={handleSaveEdit}
                  disabled={!editSongTitle.trim() || !editSongArtist.trim() || isSavingSongEdit}
                  data-testid="user-save-song-change-btn"
                  className="btn btn-primary px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingSongEdit ? 'Saving…' : 'Save Change'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="btn btn-secondary px-4 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Next Rotation Selection */}
        {userEntry.can_select_next_song && (
          <div className="bg-purple-900/30 border border-purple-400 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-purple-400">🎉 Your turn is complete!</h3>
                <p className="text-purple-300">You can now select a song for the next rotation</p>
              </div>
              <button
                onClick={handleSelectNext}
                className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
              >
                Select Song for Next Rotation
              </button>
            </div>
            
            {isSelectingNext && (
              <div className="mt-4 p-4 bg-black/50 rounded border border-purple-400">
                <h4 className="font-medium text-purple-400 mb-2">Choose Song for Next Rotation</h4>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-white mb-2">
                    Song Title
                  </label>
                  <input
                    type="text"
                    value={nextSongTitle}
                    onChange={(e) => setNextSongTitle(e.target.value)}
                    placeholder="Enter song title"
                    className="form-input w-full p-3 rounded-lg"
                  />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-white mb-2">
                    Artist Name
                  </label>
                  <input
                    type="text"
                    value={nextSongArtist}
                    onChange={(e) => setNextSongArtist(e.target.value)}
                    placeholder="Enter artist name"
                    className="form-input w-full p-3 rounded-lg"
                  />
                </div>
                <div className="flex space-x-2 mt-3">
                  <button
                    onClick={handleSaveNextSong}
                    disabled={!nextSongTitle.trim() || !nextSongArtist.trim()}
                    className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:bg-gray-400"
                  >
                    Confirm Song for Next Rotation
                  </button>
                  <button
                    onClick={handleCancelNextSelection}
                    className="btn btn-secondary px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-bold text-white mb-4">{formTitle}</h2>
      <form onSubmit={onSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-white mb-2">
            Your Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="form-input w-full p-3 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your name"
            disabled={isLoading}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-white mb-2">
            Song Title
          </label>
          <input
            type="text"
            value={songTitle}
            onChange={(e) => setSongTitle(e.target.value)}
            placeholder="Enter song title"
            disabled={isLoading}
            className="form-input w-full p-3 rounded-lg"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-white mb-2">
            Artist Name
          </label>
          <input
            type="text"
            value={songArtist}
            onChange={(e) => setSongArtist(e.target.value)}
            placeholder="Enter artist name"
            disabled={isLoading}
            className="form-input w-full p-3 rounded-lg"
          />
        </div>
        
        <button
          type="submit"
          disabled={!name.trim() || !songTitle.trim() || !songArtist.trim() || isLoading}
          className="btn btn-primary w-full py-3 px-4 rounded-lg font-semibold"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="loading-spinner"></div>
              {loadingText}
            </span>
          ) : (
            buttonText
          )}
        </button>
      </form>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const hasAutoSubmittedRef = useRef(false);
  
  const handleSubmit = async (e, passwordValue) => {
    if (e) e.preventDefault();
    const pwd = passwordValue || (inputRef.current ? inputRef.current.value : '');
    if (!pwd || isLoading || hasAutoSubmittedRef.current) return;
    
    hasAutoSubmittedRef.current = true;
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        password: pwd
      });
      
      if (response.data.success) {
        sessionStorage.setItem('adminSession', response.data.session_token);
        // Persist to localStorage for cross-reload persistence
        localStorage.setItem('karaoke_admin_session', response.data.session_token);
        onLogin(true);
        
        if ('Notification' in window && Notification.permission === 'default') {
          setTimeout(async () => {
            await requestNotificationPermission();
          }, 500);
        }
      } else {
        hasAutoSubmittedRef.current = false;
        setError('Error');
      }
    } catch (err) {
      hasAutoSubmittedRef.current = false;
      setError('Error');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit when password is filled (via autofill or typing)
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    
    const handleInput = () => {
      // When input changes and has value, auto-submit
      if (input.value && input.value.length > 0 && !hasAutoSubmittedRef.current) {
        // Small delay to ensure autofill completes
        setTimeout(() => {
          if (input.value && !hasAutoSubmittedRef.current) {
            handleSubmit(null, input.value);
          }
        }, 100);
      }
    };
    
    const handleAnimationStart = (e) => {
      // Chrome fires this animation when autofilling
      if (e.animationName === 'onAutoFillStart') {
        handleInput();
      }
    };
    
    // Listen for input changes (covers most autofill scenarios)
    input.addEventListener('input', handleInput);
    input.addEventListener('change', handleInput);
    input.addEventListener('animationstart', handleAnimationStart);
    
    // Also check periodically for autofill that doesn't fire events
    const checkAutofill = () => {
      if (input.value && input.value.length > 0 && !hasAutoSubmittedRef.current) {
        handleSubmit(null, input.value);
      }
    };
    
    const intervals = [200, 500, 1000, 1500, 2000];
    const timers = intervals.map(delay => setTimeout(checkAutofill, delay));
    
    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('change', handleInput);
      input.removeEventListener('animationstart', handleAnimationStart);
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <div className="card rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <form onSubmit={(e) => handleSubmit(e)} autoComplete="on" method="post" action="#">
        <label htmlFor="admin-password" className="sr-only">Password</label>
        
        <input
          type="text"
          name="username"
          id="admin-username"
          autoComplete="username"
          defaultValue="admin"
          tabIndex={-1}
          className="sr-only"
        />
        <input
          ref={inputRef}
          id="admin-password"
          type="password"
          name="password"
          placeholder="Password"
          className="form-input w-full p-3 rounded-lg"
          autoComplete="current-password"
          autoFocus
          disabled={isLoading}
        />
        {error && (
          <div className="text-red-400 text-sm mt-3">
            {error}
          </div>
        )}
        <button type="submit" className="sr-only">Login</button>
      </form>
      <style>{`
        #admin-password::placeholder {
          color: transparent;
        }
        @keyframes onAutoFillStart { from {} to {} }
        #admin-password:-webkit-autofill {
          animation-name: onAutoFillStart;
        }
      `}</style>
    </div>
  );
};

// Start Your Song Button Component (for users)
const StartYourSongButton = ({ userEntry, queue, deviceFingerprint }) => {
  const [isStarting, setIsStarting] = useState(false);
  const [message, setMessage] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Poll sync status to know if KaraFun sync is active
  // FIXED: Added debouncing to prevent UI flickering from rapid status changes
  useEffect(() => {
    let debounceTimer = null;
    let lastStatus = null;
    
    const fetchSyncStatus = async () => {
      try {
        // Use consistent endpoint: /api/karafun/status
        const response = await fetch(`${BACKEND_URL}/api/karafun/status`);
        const data = await response.json();
        
        // Debounce status updates to prevent flickering
        // Only update if status has been stable for at least 500ms
        const newStatus = {
          is_syncing: data.is_running,
          karafun_connected: data.is_connected
        };
        
        const statusChanged = !lastStatus || 
          lastStatus.is_syncing !== newStatus.is_syncing || 
          lastStatus.karafun_connected !== newStatus.karafun_connected;
        
        if (statusChanged) {
          // Clear any pending debounce timer
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          
          // Set new debounce timer - only update UI after 500ms of stable status
          debounceTimer = setTimeout(() => {
            setSyncStatus(newStatus);
            lastStatus = newStatus;
          }, 500);
        } else {
          // Status unchanged, update immediately (keeps UI responsive for stable states)
          lastStatus = newStatus;
        }
      } catch (err) {
        console.error('Error fetching sync status:', err);
      }
    };
    
    fetchSyncStatus();
    const interval = setInterval(fetchSyncStatus, 5000);
    return () => {
      clearInterval(interval);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);
  
  // Check if user is at position 1 AND marked as current
  const isAtPosition1AndCurrent = useMemo(() => {
    if (!queue || queue.length === 0 || !userEntry) return false;
    
    const position1Entry = queue[0];
    return (
      position1Entry.device_fingerprint === deviceFingerprint &&
      position1Entry.is_current === true
    );
  }, [queue, userEntry, deviceFingerprint]);
  
  // Only show if sync is active AND user is at position 1
  const isSyncing = syncStatus?.is_syncing || false;
  if (!isSyncing || !isAtPosition1AndCurrent) {
    return null; // Don't show button if sync not active or not at position 1
  }
  
  const handleStartSong = async () => {
    setIsStarting(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/karafun/play-song`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start song');
      }
      
      setMessage({ type: 'success', text: 'Your song is starting!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
      
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Could not start song. Make sure KaraFun sync is running.'
      });
    } finally {
      setIsStarting(false);
    }
  };
  
  return (
    <div className="mt-6 mb-4">
      <button
        onClick={handleStartSong}
        disabled={isStarting}
        className={`w-full px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
          isStarting
            ? 'bg-gray-600 cursor-not-allowed'
            : 'bg-gradient-to-r from-green-700 to-blue-600 hover:from-green-600 hover:to-blue-500 shadow-lg'
        } text-white`}
      >
        {isStarting ? '⏳ Starting...' : '🎤 Start Your Song'}
      </button>
      
      {message && (
        <div className={`mt-3 text-center text-sm p-3 rounded ${
          message.type === 'success' 
            ? 'bg-green-900/30 text-green-300 border border-green-500' 
            : 'bg-red-900/30 text-red-300 border border-red-500'
        }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};



// Generate random 10-character alphanumeric string for unique KaraFun session
const generateRandomNickname = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 10; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// KaraFun Manual Login Component
const KaraFunManualLogin = ({ onLoginComplete }) => {
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nickname, setNickname] = useState(() => generateRandomNickname());
  const [loginComplete, setLoginComplete] = useState(false);
  const [autoLoginError, setAutoLoginError] = useState(null);
  
  const refreshScreenshot = async () => {
    try {
      const response = await fetch(`${API}/karafun/login/screenshot`);
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setScreenshot(url);
      }
    } catch (err) {
      console.error('Error loading screenshot:', err);
    }
  };
  
  useEffect(() => {
    refreshScreenshot();
    const interval = setInterval(refreshScreenshot, 2000);
    return () => clearInterval(interval);
  }, []);
  
  // Auto-login on component mount with the random nickname
  useEffect(() => {
    const performAutoLogin = async () => {
      // Small delay to ensure the page is ready
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setLoading(true);
      try {
        console.log(`🔐 Auto-logging in with nickname: ${nickname}`);
        
        // Type the random nickname
        await fetch(`${API}/karafun/login/type`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: nickname })
        });
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Press Enter to submit
        await fetch(`${API}/karafun/login/press-key`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: 'Enter' })
        });
        
        // Wait and check if login succeeded (check for authentication)
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        const checkResponse = await fetch(`${API}/karafun/login/check`);
        const checkData = await checkResponse.json();
        
        if (checkData.login_complete || checkData.is_authenticated) {
          console.log('✅ Auto-login successful! WebSocket authenticated.');
          setLoginComplete(true);
          setAutoLoginError(null);
          setTimeout(() => onLoginComplete(), 1000);
        } else if (checkData.is_connected && !checkData.is_authenticated) {
          console.log('⚠️ WebSocket connected but not authenticated yet. Waiting...');
          // Wait a bit more and check again
          await new Promise(resolve => setTimeout(resolve, 3000));
          const retryResponse = await fetch(`${API}/karafun/login/check`);
          const retryData = await retryResponse.json();
          
          if (retryData.is_authenticated || retryData.login_complete) {
            console.log('✅ Authentication confirmed on retry!');
            setLoginComplete(true);
            setAutoLoginError(null);
            setTimeout(() => onLoginComplete(), 1000);
          } else {
            setAutoLoginError('WebSocket connected but authentication not confirmed. Please try the manual Login button.');
          }
        } else {
          console.log('⚠️ Auto-login may not have completed');
          setAutoLoginError('Auto-login may not have completed. Please try the manual Login button or click on the screenshot to interact.');
        }
      } catch (err) {
        console.error('Auto-login error:', err);
        setAutoLoginError(`Auto-login failed: ${err.message}. Please try the manual Login button.`);
      } finally {
        setLoading(false);
      }
    };
    
    performAutoLogin();
  }, []);
  
  const handleQuickLogin = async () => {
    setLoading(true);
    try {
      // Type nickname
      await fetch(`${API}/karafun/login/type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: nickname })
      });
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Press Enter
      await fetch(`${API}/karafun/login/press-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: 'Enter' })
      });
      
      // Wait and check if login succeeded
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const checkResponse = await fetch(`${API}/karafun/login/check`);
      const checkData = await checkResponse.json();
      
      if (checkData.login_complete || checkData.is_authenticated) {
        setLoginComplete(true);
        setTimeout(() => onLoginComplete(), 1000);
      } else if (checkData.is_connected && !checkData.is_authenticated) {
        // Wait a bit more for authentication
        await new Promise(resolve => setTimeout(resolve, 3000));
        const retryResponse = await fetch(`${API}/karafun/login/check`);
        const retryData = await retryResponse.json();
        
        if (retryData.is_authenticated || retryData.login_complete) {
          setLoginComplete(true);
          setTimeout(() => onLoginComplete(), 1000);
        } else {
          alert('WebSocket connected but authentication not confirmed. Please try clicking manually on the "Join Session" button.');
        }
      } else {
        alert('Login may not have completed. Try clicking manually on the "Join Session" button.');
      }
    } catch (err) {
      alert('Error during login: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImageClick = async (e) => {
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Show visual feedback
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: absolute;
      left: ${e.clientX}px;
      top: ${e.clientY}px;
      width: 20px;
      height: 20px;
      border: 3px solid #22c55e;
      border-radius: 50%;
      pointer-events: none;
      z-index: 9999;
      animation: pulse 0.5s ease-out;
    `;
    document.body.appendChild(indicator);
    setTimeout(() => indicator.remove(), 500);
    
    try {
      await fetch(`${API}/karafun/login/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ x: Math.round(x), y: Math.round(y) })
      });
      
      setTimeout(refreshScreenshot, 500);
    } catch (err) {
      console.error('Error clicking:', err);
    }
  };
  
  if (loginComplete) {
    return (
      <div className="bg-green-900/30 p-4 rounded text-center">
        <div className="text-2xl mb-2">✅</div>
        <div className="text-green-300 font-semibold">Login Complete!</div>
        <div className="text-xs text-gray-400 mt-2">Browser will remain active in background</div>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="bg-blue-900/30 p-3 rounded">
        <div className="text-sm font-semibold text-blue-300 mb-2">🔐 KaraFun Manual Login</div>
        <div className="text-xs text-gray-400 mb-3">
          Login to KaraFun remote so the system can sync your queue
        </div>
        
        {/* Quick Login Option */}
        <div className="mb-3 p-2 bg-gray-800/50 rounded">
          <div className="text-xs text-gray-400 mb-2">Quick Login:</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter nickname"
              className="flex-1 px-2 py-1 rounded bg-gray-700 text-white text-sm"
            />
            <button
              onClick={handleQuickLogin}
              disabled={loading || !nickname}
              className="px-3 py-1 bg-green-700 hover:bg-green-800 disabled:bg-gray-600 text-white rounded text-sm font-semibold"
            >
              {loading ? '⏳ Logging in...' : 'Login'}
            </button>
          </div>
          {/* Auto-login error message */}
          {autoLoginError && (
            <div className="mt-2 p-2 bg-red-900/50 border border-red-700 rounded text-xs text-red-300">
              ⚠️ {autoLoginError}
            </div>
          )}
        </div>
        
        {/* Screenshot */}
        {screenshot && (
          <div className="space-y-2">
            <div className="text-xs text-gray-400">Or click directly on the login page:</div>
            <div className="border-2 border-gray-600 rounded overflow-hidden">
              <img
                src={screenshot}
                alt="KaraFun Login"
                onClick={handleImageClick}
                className="w-full cursor-pointer"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            <div className="text-xs text-gray-500 text-center">
              Click on the input field, type your nickname, then click "Join session"
            </div>
          </div>
        )}
        
        <button
          onClick={refreshScreenshot}
          className="w-full mt-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
        >
          🔄 Refresh Screenshot
        </button>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────
// AutoInsertButton — standalone component 2026-09-27
//
// Long-press (1s) opens a purple-bordered modal with Enable/Disable +
// Cancel. Short-press is a no-op. Status widget below the button shows
// counter and last shift.
//
// Extracted from `KaraFunPlaybackControls` so it can live in the Admin
// Controls area (always visible, not tied to KaraFun sync state).
// Modal renders via `createPortal` to `document.body` to survive the
// Admin Controls collapse (which uses `display: none`).
// ─────────────────────────────────────────────────────────────────────
const AutoInsertButton = () => {
  const HOLD_DURATION_ADVANCE = 1000;
  const [isHoldingInsert, setIsHoldingInsert] = useState(false);
  const [insertHoldProgress, setInsertHoldProgress] = useState(0);
  const insertHoldTimeoutRef = useRef(null);
  const insertHoldIntervalRef = useRef(null);
  const insertHoldFiredRef = useRef(false);
  const [showAutoInsertModal, setShowAutoInsertModal] = useState(false);
  const [autoInsertEnabled, setAutoInsertEnabled] = useState(false);
  const [autoInsertLoading, setAutoInsertLoading] = useState(false);
  const [autoInsertState, setAutoInsertState] = useState({ counter: 0, threshold: 6, threshold_min: 2, threshold_max: 20, last_shift: null });
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchAutoInsert = async () => {
      try {
        const r = await axios.get(`${API}/admin/auto-insert`);
        setAutoInsertEnabled(!!r.data?.auto_insert_enabled);
        setAutoInsertState({
          counter: r.data?.counter || 0,
          threshold: r.data?.threshold || 6,
          threshold_min: r.data?.threshold_min || 2,
          threshold_max: r.data?.threshold_max || 20,
          last_shift: r.data?.last_shift || null,
        });
      } catch (e) {}
    };
    fetchAutoInsert();
    // Fix #45 (shipped 2026-09-28): re-fetch on every queue-change SSE event so
    // the counter reflects the backend in real time (was up to 15s
    // stale with polling-only). Retains the 15s poll as a safety net
    // if the SSE stream is silently disconnected.
    const onQueueChanged = () => { fetchAutoInsert(); };
    window.addEventListener('karaoke:queue-changed', onQueueChanged);
    const interval = setInterval(fetchAutoInsert, 15000);
    return () => {
      window.removeEventListener('karaoke:queue-changed', onQueueChanged);
      clearInterval(interval);
    };
  }, []);

  const startHoldInsert = (e) => {
    e.preventDefault();
    insertHoldFiredRef.current = false;
    setIsHoldingInsert(true);
    setInsertHoldProgress(0);
    insertHoldIntervalRef.current = setInterval(() => {
      setInsertHoldProgress((prev) => {
        const next = prev + (50 / HOLD_DURATION_ADVANCE) * 100;
        return next >= 100 ? 100 : next;
      });
    }, 50);
    insertHoldTimeoutRef.current = setTimeout(() => {
      insertHoldFiredRef.current = true;
      setIsHoldingInsert(false);
      setInsertHoldProgress(0);
      clearInterval(insertHoldIntervalRef.current);
      setShowAutoInsertModal(true);
    }, HOLD_DURATION_ADVANCE);
  };

  const cancelHoldInsert = (e) => {
    if (e) e.preventDefault();
    setIsHoldingInsert(false);
    setInsertHoldProgress(0);
    if (insertHoldTimeoutRef.current) clearTimeout(insertHoldTimeoutRef.current);
    if (insertHoldIntervalRef.current) clearInterval(insertHoldIntervalRef.current);
  };

  const handleToggleAutoInsert = async (nextEnabled) => {
    setAutoInsertLoading(true);
    try {
      const r = await axios.post(`${API}/admin/auto-insert`, { enabled: nextEnabled });
      setAutoInsertEnabled(!!r.data?.auto_insert_enabled);
      setShowAutoInsertModal(false);
    } catch (err) {
      setMessage({
        text: `Failed to toggle auto-insert: ${err.response?.data?.detail || err.message}`,
        type: 'error',
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setAutoInsertLoading(false);
    }
  };

  // Fix #50 (shipped 2026-09-28): admin-configurable threshold. Auto-saves on
  // +/- click. Bounds enforced client-side + server-side.
  const handleUpdateThreshold = async (delta) => {
    const next = autoInsertState.threshold + delta;
    if (next < autoInsertState.threshold_min || next > autoInsertState.threshold_max) return;
    setThresholdSaving(true);
    // Optimistic UI update
    setAutoInsertState((prev) => ({ ...prev, threshold: next }));
    try {
      const r = await axios.post(`${API}/admin/auto-insert/threshold`, { threshold: next });
      setAutoInsertState((prev) => ({
        ...prev,
        threshold: r.data?.threshold ?? next,
        threshold_min: r.data?.threshold_min ?? prev.threshold_min,
        threshold_max: r.data?.threshold_max ?? prev.threshold_max,
      }));
    } catch (err) {
      // Roll back optimistic update on failure
      setAutoInsertState((prev) => ({ ...prev, threshold: next - delta }));
      setMessage({
        text: `Failed to update threshold: ${err.response?.data?.detail || err.message}`,
        type: 'error',
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setThresholdSaving(false);
    }
  };

  return (
    <>
      <button
        onMouseDown={startHoldInsert}
        onMouseUp={cancelHoldInsert}
        onMouseLeave={() => {
          if (isHoldingInsert) {
            insertHoldFiredRef.current = true;
            setIsHoldingInsert(false);
            setInsertHoldProgress(0);
            if (insertHoldTimeoutRef.current) clearTimeout(insertHoldTimeoutRef.current);
            if (insertHoldIntervalRef.current) clearInterval(insertHoldIntervalRef.current);
          }
        }}
        onTouchStart={startHoldInsert}
        onTouchEnd={cancelHoldInsert}
        onContextMenu={(e) => e.preventDefault()}
        data-testid="auto-insert-button"
        title={
          autoInsertEnabled
            ? `Auto-insert is ENABLED. Hold ${HOLD_DURATION_ADVANCE / 1000}s to open options (Disable / Cancel).`
            : `Auto-insert is DISABLED. Hold ${HOLD_DURATION_ADVANCE / 1000}s to open settings.`
        }
        className={`relative overflow-hidden w-full px-5 py-3 rounded font-semibold transition-colors text-white text-base flex items-center justify-center gap-2 ${
          isHoldingInsert ? 'bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {isHoldingInsert && (
          <div
            className="absolute inset-0 bg-purple-700/60 transition-all duration-75 pointer-events-none"
            style={{ width: `${insertHoldProgress}%`, opacity: 0.6 }}
          />
        )}
        <span className="relative z-10 inline-flex items-center gap-2">
          {isHoldingInsert ? (
            'Hold for settings…'
          ) : autoInsertEnabled ? (
            <span data-testid="auto-insert-button-label-enabled">Auto-Insert is Enabled</span>
          ) : (
            <span data-testid="auto-insert-button-label-disabled">Auto-Insert is Disabled</span>
          )}
        </span>
      </button>
      <div className="text-xs text-gray-500 text-center -mt-1 mb-2" data-testid="auto-insert-status">
        Counter: {autoInsertState.counter} / {Math.max(1, (autoInsertState.threshold || 6) - 1)}
        {autoInsertState.last_shift && (
          <>
            {' · '}Last shift: K={autoInsertState.last_shift.K} after{' '}
            <span className="text-gray-400">{autoInsertState.last_shift.target_user_name}</span>
          </>
        )}
      </div>
      {message && (
        <div className={`text-xs text-center p-2 rounded mb-2 ${
          message.type === 'error' ? 'text-red-400 bg-red-900/20' : 'text-green-400 bg-green-900/20'
        }`}>
          {message.text}
        </div>
      )}
      {showAutoInsertModal && createPortal(
        <div
          data-testid="auto-insert-modal-backdrop"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAutoInsertModal(false); }}
        >
          <div
            data-testid="auto-insert-modal"
            className="card rounded-lg shadow-2xl p-6 max-w-sm w-full border-2 border-purple-500"
          >
            <h3 className="text-xl font-bold text-white mb-1 text-center">Auto-Insert Point</h3>
            <p className="text-gray-400 text-sm text-center mb-4">
              Auto-Insert is currently{' '}
              <span
                data-testid="auto-insert-state-label"
                className={`font-bold ${autoInsertEnabled ? 'text-green-400' : 'text-gray-400'}`}
              >
                {autoInsertEnabled ? 'ON' : 'OFF'}
              </span>
              .
            </p>
            <p className="text-center text-xs text-gray-500 mb-3">
              When ON, once <span className="font-semibold text-gray-300">{Math.max(1, (autoInsertState.threshold || 6) - 1)}</span> new singers stack in a row, the next new singer gets pushed down past a waiting returner. Manual insert point always wins.
            </p>
            {/* Fix #50 (shipped 2026-09-28): admin-configurable threshold.
                Fix #51 (shipped 2026-09-28): UI shows "stack count" = threshold − 1
                so admin sees "5 stack → 6th shifts" as "5" (the visible
                pile), matching the host's mental model. Server still
                stores/uses the actual trigger threshold (6). */}
            <div
              data-testid="auto-insert-threshold-editor"
              className="flex items-center justify-between gap-2 rounded-md bg-gray-800/60 border border-gray-700 px-3 py-2 mb-4"
            >
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wide text-gray-400">Stack this many</span>
                <span className="text-xs text-gray-500">before shifting</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="auto-insert-threshold-decrement-btn"
                  onClick={() => handleUpdateThreshold(-1)}
                  disabled={thresholdSaving || autoInsertLoading || autoInsertState.threshold <= autoInsertState.threshold_min}
                  className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-white font-bold text-lg leading-none flex items-center justify-center"
                  aria-label="Decrease stack count"
                >
                  −
                </button>
                <span
                  data-testid="auto-insert-threshold-value"
                  className={`text-2xl font-bold text-white min-w-[2ch] text-center ${thresholdSaving ? 'opacity-50' : ''}`}
                >
                  {Math.max(1, (autoInsertState.threshold || 6) - 1)}
                </span>
                <button
                  data-testid="auto-insert-threshold-increment-btn"
                  onClick={() => handleUpdateThreshold(1)}
                  disabled={thresholdSaving || autoInsertLoading || autoInsertState.threshold >= autoInsertState.threshold_max}
                  className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-white font-bold text-lg leading-none flex items-center justify-center"
                  aria-label="Increase stack count"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                data-testid="auto-insert-toggle-btn"
                onClick={() => handleToggleAutoInsert(!autoInsertEnabled)}
                disabled={autoInsertLoading}
                className={`w-full py-2 rounded-lg font-semibold disabled:opacity-50 text-white ${
                  autoInsertEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {autoInsertLoading
                  ? 'Saving…'
                  : (autoInsertEnabled ? 'Disable Auto-Insert' : 'Enable Auto-Insert')}
              </button>
              <button
                data-testid="auto-insert-close-btn"
                onClick={() => setShowAutoInsertModal(false)}
                disabled={autoInsertLoading}
                className="btn btn-secondary w-full py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────
// AutoAdvanceButton — standalone component 2026-09-27
//
// Combines "Advance to Next Turn" manual action with the auto-advance
// toggle. Short-click: manual advance (when OFF) / no-op (when ON).
// Long-press (1s): opens Advance Once / Toggle / Cancel modal.
//
// Extracted from `KaraFunPlaybackControls` so it can live in the Admin
// Controls area alongside Stop KaraFun Sync. Only rendered when
// KaraFun sync is active. Modal renders via `createPortal`.
// ─────────────────────────────────────────────────────────────────────
const AutoAdvanceButton = ({ onAdvanceQueue }) => {
  const HOLD_DURATION_ADVANCE = 1000;
  const [isHoldingAdvance, setIsHoldingAdvance] = useState(false);
  const [advanceHoldProgress, setAdvanceHoldProgress] = useState(0);
  const advanceHoldTimeoutRef = useRef(null);
  const advanceHoldIntervalRef = useRef(null);
  const advanceHoldFiredRef = useRef(false);
  const [showAutoAdvanceModal, setShowAutoAdvanceModal] = useState(false);
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);
  const [autoAdvanceLoading, setAutoAdvanceLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/auto-advance`);
        setAutoAdvanceEnabled(!!r.data?.auto_advance_enabled);
      } catch (e) {}
    })();
  }, []);

  const startHoldAdvance = (e) => {
    e.preventDefault();
    advanceHoldFiredRef.current = false;
    setIsHoldingAdvance(true);
    setAdvanceHoldProgress(0);
    advanceHoldIntervalRef.current = setInterval(() => {
      setAdvanceHoldProgress((prev) => {
        const next = prev + (50 / HOLD_DURATION_ADVANCE) * 100;
        return next >= 100 ? 100 : next;
      });
    }, 50);
    advanceHoldTimeoutRef.current = setTimeout(() => {
      advanceHoldFiredRef.current = true;
      setIsHoldingAdvance(false);
      setAdvanceHoldProgress(0);
      clearInterval(advanceHoldIntervalRef.current);
      setShowAutoAdvanceModal(true);
    }, HOLD_DURATION_ADVANCE);
  };

  const cancelHoldAdvance = (e) => {
    if (e) e.preventDefault();
    const wasHolding = isHoldingAdvance;
    setIsHoldingAdvance(false);
    setAdvanceHoldProgress(0);
    if (advanceHoldTimeoutRef.current) clearTimeout(advanceHoldTimeoutRef.current);
    if (advanceHoldIntervalRef.current) clearInterval(advanceHoldIntervalRef.current);
    if (wasHolding && !advanceHoldFiredRef.current && !autoAdvanceEnabled) {
      onAdvanceQueue && onAdvanceQueue();
    }
  };

  const handleAdvanceOnce = async () => {
    setShowAutoAdvanceModal(false);
    onAdvanceQueue && onAdvanceQueue();
  };

  const handleToggleAutoAdvance = async (nextEnabled) => {
    setAutoAdvanceLoading(true);
    try {
      const r = await axios.post(`${API}/admin/auto-advance`, { enabled: nextEnabled });
      setAutoAdvanceEnabled(!!r.data?.auto_advance_enabled);
      setShowAutoAdvanceModal(false);
    } catch (err) {
      setMessage({
        text: `Failed to toggle auto-advance: ${err.response?.data?.detail || err.message}`,
        type: 'error',
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setAutoAdvanceLoading(false);
    }
  };

  return (
    <>
      <button
        onMouseDown={startHoldAdvance}
        onMouseUp={cancelHoldAdvance}
        onMouseLeave={() => {
          if (isHoldingAdvance) {
            advanceHoldFiredRef.current = true;
            setIsHoldingAdvance(false);
            setAdvanceHoldProgress(0);
            if (advanceHoldTimeoutRef.current) clearTimeout(advanceHoldTimeoutRef.current);
            if (advanceHoldIntervalRef.current) clearInterval(advanceHoldIntervalRef.current);
          }
        }}
        onTouchStart={startHoldAdvance}
        onTouchEnd={cancelHoldAdvance}
        onContextMenu={(e) => e.preventDefault()}
        data-testid="advance-button"
        title={
          autoAdvanceEnabled
            ? `Auto-advance is ENABLED. Hold ${HOLD_DURATION_ADVANCE / 1000}s to open options.`
            : `Short-press to advance. Hold ${HOLD_DURATION_ADVANCE / 1000}s to open settings.`
        }
        className={`relative overflow-hidden w-full px-5 py-3 rounded font-semibold transition-colors text-white text-base flex items-center justify-center gap-2 ${
          isHoldingAdvance ? 'bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {isHoldingAdvance && (
          <div
            className="absolute inset-0 bg-purple-700/60 transition-all duration-75 pointer-events-none"
            style={{ width: `${advanceHoldProgress}%`, opacity: 0.6 }}
          />
        )}
        <span className="relative z-10 inline-flex items-center gap-2">
          {isHoldingAdvance ? (
            'Hold for settings…'
          ) : autoAdvanceEnabled ? (
            <span data-testid="advance-button-label-enabled">Auto-Advance is Enabled</span>
          ) : (
            <span data-testid="advance-button-label-disabled">
              Advance <IoArrowForwardCircle size={20} />
            </span>
          )}
        </span>
      </button>
      {message && (
        <div className={`text-xs text-center p-2 rounded mb-2 ${
          message.type === 'error' ? 'text-red-400 bg-red-900/20' : 'text-green-400 bg-green-900/20'
        }`}>
          {message.text}
        </div>
      )}
      {showAutoAdvanceModal && createPortal(
        <div
          data-testid="auto-advance-modal-backdrop"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAutoAdvanceModal(false); }}
        >
          <div
            data-testid="auto-advance-modal"
            className="card rounded-lg shadow-2xl p-6 max-w-sm w-full border-2 border-purple-500"
          >
            <h3 className="text-xl font-bold text-white mb-1 text-center">Advance Queue</h3>
            <p className="text-gray-400 text-sm text-center mb-4">
              Auto-Advance is currently{' '}
              <span className={`font-bold ${autoAdvanceEnabled ? 'text-green-400' : 'text-gray-400'}`}>
                {autoAdvanceEnabled ? 'ON' : 'OFF'}
              </span>
              .
            </p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="advance-once-btn"
                onClick={handleAdvanceOnce}
                disabled={autoAdvanceLoading}
                className="w-full py-2 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                Advance Once
              </button>
              <button
                data-testid="auto-advance-toggle-btn"
                onClick={() => handleToggleAutoAdvance(!autoAdvanceEnabled)}
                disabled={autoAdvanceLoading}
                className={`w-full py-2 rounded-lg font-semibold disabled:opacity-50 text-white ${
                  autoAdvanceEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {autoAdvanceLoading ? 'Saving…' : (autoAdvanceEnabled ? 'Disable Auto-Advance' : 'Enable Auto-Advance')}
              </button>
              <button
                onClick={() => setShowAutoAdvanceModal(false)}
                disabled={autoAdvanceLoading}
                className="btn btn-secondary w-full py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};


// KaraFun Playback Controls Component
const KaraFunPlaybackControls = ({ onAdvanceQueue }) => {
  const [isLoading, setIsLoading] = useState(null); // Track which button is loading
  const [message, setMessage] = useState(null);

  const handlePlayback = async (action, endpoint) => {
    setIsLoading(action);
    setMessage(null);
    
    try {
      const response = await fetch(`${API}/karafun/playback/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${action}`);
      }
      
      const data = await response.json();
      setMessage({ type: 'success', text: data.message });
      
      // Clear message after 2 seconds
      setTimeout(() => setMessage(null), 2000);
      
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsLoading(null);
    }
  };

  const handleSmartPlay = async () => {
    setIsLoading('play');
    setMessage(null);
    
    try {
      // Send play command for immediate playback
      console.log('▶️ Play button: Starting playback...');
      const response = await fetch(`${API}/karafun/playback/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to play');
      }
      
      // DORMANT: Auto-advance on play (disabled for manual control)
      // Uncomment the lines below to re-enable auto-advance when play is pressed:
      // 
      // console.log('▶️ Play button: Advancing queue in background...');
      // await onAdvanceQueue();
      // setMessage({ type: 'success', text: 'Playing & queue advanced' });
      
      // Manual mode: Just play, no auto-advance
      setMessage({ type: 'success', text: 'Playing' });
      setTimeout(() => setMessage(null), 2000);
      
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsLoading(null);
    }
  };
  
  // Restart the currently-playing song from the beginning. Wired to the
  // dedicated 🔄 Restart button between Pause and Skip. Backend uses the
  // Add → Move → Skip pattern (see _replace_karafun_current_song) so the
  // app's queue state is preserved.
  const handleRestart = async () => {
    if (!window.confirm('Restart the current song from the beginning?')) return;
    setIsLoading('restart');
    setMessage(null);

    try {
      const response = await fetch(`${API}/karafun/playback/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to restart');
      }

      const data = await response.json();
      setMessage({ type: 'success', text: data.message || '🔄 Song restarted' });
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsLoading(null);
    }
  };
  
  // (Removed) Hold-pause-to-restart UX — replaced by the dedicated Restart button.

  // ── KaraFun "Next" / Skip ─────────────────────────────────────────────
  // Dedicated button. Requires confirmation since this skips past whatever
  // KaraFun currently has queued (including songs we just sent).
  const handleKarafunSkip = async () => {
    const ok = window.confirm("Skip current song in KaraFun?");
    if (!ok) return;

    setIsLoading('skip');
    setMessage(null);
    try {
      const response = await fetch(`${API}/karafun/playback/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to skip in KaraFun');
      }
      setMessage({ type: 'success', text: '⏭️ KaraFun skipped (app queue unchanged)' });
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="mb-4 space-y-2">
      <div className="text-xs text-gray-400 text-center mb-1">
        🎛️ KaraFun Playback Controls
      </div>
      
      {/* Play, Pause, Restart, and Skip Buttons - Four in a row */}
      <div className="grid grid-cols-4 gap-2">
        <button
          onClick={handleSmartPlay}
          disabled={isLoading === 'play'}
          data-testid="karafun-play-button"
          className={`px-3 py-4 rounded font-semibold transition-colors ${
            isLoading === 'play'
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-green-700 hover:bg-green-800'
          } text-white text-sm`}
          title="Play (playback only - use Advance Queue button to advance)"
        >
          {isLoading === 'play' ? '⏳' : '▶️ Play'}
        </button>
        
        <button
          onClick={() => handlePlayback('pause', 'pause')}
          disabled={isLoading === 'pause'}
          data-testid="karafun-pause-button"
          className={`px-3 py-4 rounded font-semibold transition-colors text-white text-sm ${
            isLoading === 'pause'
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-700'
          }`}
          title="Pause KaraFun playback"
        >
          {isLoading === 'pause' ? '⏳' : '⏸️ Pause'}
        </button>

        <button
          onClick={handleRestart}
          disabled={isLoading === 'restart'}
          data-testid="karafun-restart-button"
          className={`px-3 py-4 rounded font-semibold transition-colors text-white text-sm ${
            isLoading === 'restart'
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-amber-600 hover:bg-amber-700'
          }`}
          title="Restart the current song from the beginning"
        >
          {isLoading === 'restart' ? '⏳' : '🔄 Restart'}
        </button>

        <button
          onClick={handleKarafunSkip}
          disabled={isLoading === 'skip'}
          data-testid="karafun-skip-button"
          className={`px-3 py-4 rounded font-semibold transition-colors text-white text-sm flex items-center justify-center gap-1 ${
            isLoading === 'skip'
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700'
          }`}
          title="Skip current song in KaraFun (does NOT advance the app queue)"
        >
          {isLoading === 'skip' ? '⏳' : '⏭️ Skip'}
        </button>
      </div>
      
      {/* Status Message */}
      {message && (
        <div className={`text-xs text-center p-2 rounded ${
          message.type === 'success' 
            ? 'bg-green-900/30 text-green-300' 
            : 'bg-red-900/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

// KaraFun Sync Controls Component
const KaraFunSyncControls = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Confirmation modal for the Stop button — prevents accidental session
  // termination by requiring an explicit confirm click.
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  
  // FIXED: Removed duplicate polling - status is already polled in main App component (line 1693)
  // This eliminates race conditions causing UI flickering
  // Fetch once on mount to initialize state
  useEffect(() => {
    fetchSyncStatus();
    // ── 2026-07-24: also poll every 2s while syncing so the "Reconnecting…" pill
    // (below) stays fresh. The DEFERRED_FIXES #1 root-cause was silent
    // mid-session WS reconnects. Making them visible in real time is the
    // point of this indicator.
    const interval = setInterval(() => {
      fetchSyncStatus();
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchSyncStatus = async () => {
    try {
      const response = await fetch(`${API}/karafun/status`);
      const data = await response.json();
      setSyncStatus(data);
    } catch (err) {
      console.error('Error fetching KaraFun status:', err);
    }
  };
  
  const handleStartSync = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/karafun/start`, {}, {
        validateStatus: function (status) {
          return status >= 200 && status < 300; // Accept all 2xx including 202
        }
      });
      
      // Check if it's a 202 status (login required)
      if (response.status === 202 && response.data?.status === 'login_required') {
        setIsLoading(false);
        return {loginRequired: true};
      }
      
      // Success
      setError(null);
      await fetchSyncStatus();
      return {loginRequired: false};
      
    } catch (err) {
      console.error('Error starting sync:', err);
      setError(err.message || 'Failed to start sync');
      return {loginRequired: false, error: true};
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStopSync = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/karafun/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to stop sync');
      }
      
      await fetchSyncStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const isSyncing = syncStatus?.is_running || false;
  const isConnected = syncStatus?.is_connected || false;
  
  return {
    syncStatus,
    isSyncing,
    isConnected,
    isLoading,
    error,
    handleStartSync,
    handleStopSync,
    fetchSyncStatus,
    // 2026-09-14: split into two renders so the buttons can live inside
    // the Admin Controls collapsible section while the Synced &
    // Connected pill stays visible at the top of the admin view.
    // renderSyncButtons returns: Start/Stop button + KaraFun Login button
    //   + error toasts + Stop-Confirm modal.
    // renderSyncStatus returns: the status pill (Synced/Reconnecting/
    //   Stopped) + disconnect count. Cheap to render, safe to keep
    //   outside the collapse for at-a-glance visibility.
    renderSyncButtons: (setShowLoginModal) => (
      <div className="mb-4 space-y-2" data-testid="karafun-sync-buttons">
        {/* Sync Button */}
        <button
          onClick={async () => {
            if (isSyncing) {
              setShowStopConfirm(true);
            } else {
              const result = await handleStartSync();
              if (result && result.loginRequired) {
                setShowLoginModal(true);
              }
            }
          }}
          disabled={isLoading}
          data-testid="karafun-sync-toggle-btn"
          className={`w-full px-4 py-2 rounded font-semibold transition-colors ${
            isSyncing
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isLoading ? (
            isSyncing ? '⏳ Stopping...' : '⏳ Starting (15-20s)...'
          ) : isSyncing ? (
            '⏹️ Stop KaraFun Sync'
          ) : (
            '▶️ Start KaraFun Sync'
          )}
        </button>

        {/* Manual Login Button */}
        {isSyncing && (
          <button
            onClick={() => setShowLoginModal(true)}
            data-testid="karafun-login-btn"
            className="w-full px-4 py-2 mt-2 rounded font-semibold transition-colors bg-blue-600 hover:bg-blue-700 text-white"
          >
            🔐 KaraFun Login
          </button>
        )}

        {/* Error Message (from local sync operation) */}
        {error && (
          <div className="text-xs text-red-400 text-center bg-red-900/20 p-2 rounded">
            ⚠️ {error}
          </div>
        )}

        {/* Last Error from sync service */}
        {syncStatus?.last_error && (
          <div className="text-xs text-yellow-400 text-center bg-yellow-900/20 p-2 rounded">
            ⚠️ {syncStatus.last_error}
          </div>
        )}

        {/* Stop Sync Confirmation Modal (lives here so it survives
            collapse — modals are absolute-positioned overlays anyway) */}
        {showStopConfirm && (
          <div
            data-testid="stop-sync-confirm-backdrop"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowStopConfirm(false); }}
          >
            <div
              data-testid="stop-sync-confirm-modal"
              className="card rounded-lg shadow-2xl p-6 max-w-sm w-full border-2 border-red-500"
            >
              <h3 className="text-xl font-bold text-white mb-3 text-center">
                Stop KaraFun Sync?
              </h3>
              <p className="text-gray-300 text-base text-center mb-5">
                This will disconnect from KaraFun. You'll need to log in again to restart sync.
              </p>
              <div className="flex gap-2">
                <button
                  data-testid="stop-sync-confirm-btn"
                  onClick={async () => {
                    setShowStopConfirm(false);
                    await handleStopSync();
                  }}
                  disabled={isLoading}
                  className="flex-1 py-2 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  ⏹️ Stop Sync
                </button>
                <button
                  data-testid="stop-sync-cancel-btn"
                  onClick={() => setShowStopConfirm(false)}
                  disabled={isLoading}
                  className="btn btn-secondary flex-1 py-2 rounded-lg font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    ),
    // ── Status pill only — kept OUTSIDE Admin Controls collapse so
    // admin always has at-a-glance visibility of sync state. Only
    // renders when sync is active (2026-09-14: removed the "Sync
    // stopped" text — when sync is off, the Start button IS the
    // status indicator). ────────────────────────────────────────────
    renderSyncStatus: () => (
      syncStatus && isSyncing && (
        <div className="text-xs text-center space-y-1 mb-4" data-testid="karafun-sync-status">
          {syncStatus.is_reconnecting ? (
            <div
              data-testid="karafun-reconnecting-pill"
              className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500 text-amber-300 px-3 py-1 rounded-full font-semibold animate-pulse"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-ping" />
              Reconnecting to KaraFun…
            </div>
          ) : isConnected ? (
            <div
              data-testid="karafun-connected-pill"
              className="inline-flex items-center gap-2 bg-green-500/15 border border-green-500/50 text-green-400 px-3 py-1 rounded-full font-semibold"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
              Synced &amp; connected
            </div>
          ) : (
            <span className="text-gray-400">⏳ Connecting…</span>
          )}
          {(syncStatus.disconnect_count || 0) > 0 && (
            <div
              data-testid="karafun-disconnect-count"
              className="text-[10px] text-gray-500"
              title="Total WS reconnects since sync started. High counts on Standard tier can amplify latent settle-window issues — see /api/health/version."
            >
              {syncStatus.disconnect_count} reconnect{syncStatus.disconnect_count === 1 ? '' : 's'} this session
            </div>
          )}
        </div>
      )
    )
  };
};

// Disguised Admin Button Component (Musical Symbol with Long Press)
/* ── CatalogRemovalsModal ─────────────────────────────────────────────
   User-facing modal opened from the bottom-of-page "See which songs
   were recently removed from KaraFun" button. Fetches the JSON list
   from `/api/public/catalog-removals` every time it opens — always
   current, no client-side caching.

   Visual shape mirrors the Manage Users modal: full-viewport dark
   overlay, single close button (top-right ✕), tap-outside-to-close.
   The list itself keeps the plain-monospace-per-line feel that lived
   in the previous HTML endpoint (mobile text-base, sm+ text-lg,
   white-space nowrap so long titles don't wrap).                    */
/* CatalogRemovalsModal — pop-up list of songs removed from KaraFun's catalog.
   Fetches from `/api/public/catalog-removals` (user variant) or
   `/api/admin/catalog-removals` (admin variants) every time it opens —
   always current, no client-side caching.

   Variants (all render into the same shell):
     • variant="user" (default) — the singer-facing view. Public endpoint,
       alphabetical flat list of `{title, artist}`.
     • variant="admin-status" + statusFilter=<confirmed|suspected|restored>
       — admin drill-down for one status. Admin endpoint filtered by status,
       alphabetical flat list. Modal title reflects the status.
     • variant="admin-all" — admin's "View Full List." Admin endpoint (no
       filter), rendered as three grouped sections (Confirmed / Suspected /
       Restored), matching the .txt download layout.

   Visual shape mirrors the Manage Users modal: full-viewport dark
   overlay, single close button (top-right ✕), tap-outside-to-close.  */
const CatalogRemovalsModal = ({ onClose, variant = 'user', statusFilter = null }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let payload;
        if (variant === 'user') {
          const res = await axios.get(`${API}/public/catalog-removals`);
          // Shape: { updated_at, count, songs: [{title,artist}] }
          payload = res.data || { updated_at: '', count: 0, songs: [] };
        } else {
          // Admin endpoint returns rows with reconciled `status`.
          const url = statusFilter
            ? `${API}/admin/catalog-removals?status=${encodeURIComponent(statusFilter)}`
            : `${API}/admin/catalog-removals`;
          const res = await axios.get(url);
          // Shape: { count, rows: [{song_id,title,artist,status,...}], counts_by_status }
          payload = res.data || { count: 0, rows: [], counts_by_status: {} };
        }
        if (!cancelled) {
          setData(payload);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load list');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [variant, statusFilter]);

  const isAdmin = variant !== 'user';

  // Compute title, subtitle, and grouped/flat display data per variant.
  let title = 'Songs recently removed from KaraFun';
  let subtitle = '';
  let groups = []; // [{status, songs: [{title,artist}]}] — length 1 for flat, 3 for admin-all

  if (variant === 'user' && data) {
    subtitle = `Updated: ${data.updated_at} · ${data.count} ${data.count === 1 ? 'song' : 'songs'}`;
    groups = [{ status: null, songs: data.songs || [] }];
  } else if (variant === 'admin-status' && data) {
    const label = { confirmed: 'Confirmed', suspected: 'Suspected', restored: 'Restored' }[statusFilter] || statusFilter;
    title = `${label} — KaraFun Removed Songs`;
    const rows = (data.rows || []).slice().sort((a, b) => {
      const at = (a.title || '').toLowerCase();
      const bt = (b.title || '').toLowerCase();
      if (at !== bt) return at < bt ? -1 : 1;
      return (a.artist || '').toLowerCase().localeCompare((b.artist || '').toLowerCase());
    });
    subtitle = `${rows.length} ${rows.length === 1 ? 'song' : 'songs'}`;
    groups = [{ status: statusFilter, songs: rows.map(r => ({ title: r.title || '', artist: r.artist || '' })) }];
  } else if (variant === 'admin-all' && data) {
    title = 'KaraFun Removed Songs — Full List';
    const counts = data.counts_by_status || {};
    const sortRows = (arr) => arr.slice().sort((a, b) => {
      const at = (a.title || '').toLowerCase();
      const bt = (b.title || '').toLowerCase();
      if (at !== bt) return at < bt ? -1 : 1;
      return (a.artist || '').toLowerCase().localeCompare((b.artist || '').toLowerCase());
    });
    for (const s of ['confirmed', 'suspected', 'restored']) {
      const rows = sortRows((data.rows || []).filter(r => (r.status || '') === s));
      groups.push({
        status: s,
        songs: rows.map(r => ({ title: r.title || '', artist: r.artist || '' })),
      });
    }
    const total = (counts.confirmed || 0) + (counts.suspected || 0) + (counts.restored || 0);
    subtitle = `${total} total · Confirmed ${counts.confirmed || 0} · Suspected ${counts.suspected || 0} · Restored ${counts.restored || 0}`;
  }

  const totalSongs = groups.reduce((n, g) => n + g.songs.length, 0);

  return (
    <div
      data-testid="catalog-removals-modal"
      className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#050505] border border-gray-700 rounded-lg shadow-2xl w-full max-w-3xl h-[92vh] sm:h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-white text-lg sm:text-xl font-bold">{title}</h2>
            {!loading && !error && subtitle && (
              <p className="text-gray-500 text-xs sm:text-sm mt-1">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            data-testid="catalog-removals-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-gray-800 transition-colors flex-shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-4 sm:px-5 py-3">
          {loading && (
            <div className="text-gray-400 text-base py-8 text-center">Loading…</div>
          )}
          {error && (
            <div className="text-red-300 bg-red-900/40 border border-red-500/50 rounded px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {!loading && !error && data && totalSongs === 0 && (
            <div className="text-gray-400 text-base py-8 text-center">
              {isAdmin && variant === 'admin-status' ? `No ${statusFilter} entries.` : 'No songs currently flagged as removed.'}
            </div>
          )}
          {!loading && !error && data && totalSongs > 0 && (
            <div data-testid="catalog-removals-modal-list">
              {groups.map((g, gi) => (
                g.songs.length === 0 ? null : (
                  <div key={g.status || `g-${gi}`} className={gi > 0 ? 'mt-5' : ''}>
                    {variant === 'admin-all' && (
                      <h3
                        data-testid={`catalog-removals-modal-group-${g.status}`}
                        className="text-white text-sm sm:text-base font-bold uppercase tracking-wider mb-2 border-b border-gray-800 pb-1"
                      >
                        {g.status} ({g.songs.length})
                      </h3>
                    )}
                    <ul
                      className="font-mono text-base sm:text-lg text-gray-200 leading-relaxed whitespace-nowrap"
                      style={{ overflowX: 'auto' }}
                    >
                      {g.songs.map((s, i) => (
                        <li
                          key={`${g.status || 'flat'}-${s.title}-${s.artist}-${i}`}
                          data-testid={`catalog-removals-modal-item-${g.status || 'flat'}-${i}`}
                          className="py-0.5"
                        >
                          {s.title} — {s.artist}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="px-4 sm:px-5 py-2 border-t border-gray-800 text-gray-500 text-xs flex-shrink-0">
          Subject to change as KaraFun updates its catalog.
        </div>
      </div>
    </div>
  );
};


const DisguisedAdminButton = ({ isAdminMode, onToggle }) => {
  const [isHolding, setIsHolding] = useState(false);
  const holdTimeoutRef = useRef(null);
  const holdProgressRef = useRef(null);
  const buttonRef = useRef(null);
  const touchStartRef = useRef(null);
  const hasMoved = useRef(false);

  const startHold = (e) => {
    hasMoved.current = false;
    
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    touchStartRef.current = { x: startX, y: startY };
    
    setIsHolding(true);
    
    // Toggle admin mode after 1000ms hold
    holdTimeoutRef.current = setTimeout(() => {
      if (!hasMoved.current) {
        onToggle();
        setIsHolding(false);
      }
    }, 1000);
  };

  const handleMove = (e) => {
    if (!touchStartRef.current) return;
    
    const currentX = e.touches ? e.touches[0].clientX : e.clientX;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const deltaX = Math.abs(currentX - touchStartRef.current.x);
    const deltaY = Math.abs(currentY - touchStartRef.current.y);
    
    if (deltaX > 10 || deltaY > 10) {
      hasMoved.current = true;
      cancelHold();
    }
  };

  const cancelHold = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    setIsHolding(false);
    touchStartRef.current = null;
  };

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    };
  }, []);

  return (
    <div
      ref={buttonRef}
      onMouseDown={startHold}
      onMouseMove={handleMove}
      onMouseUp={cancelHold}
      onMouseLeave={cancelHold}
      onTouchStart={startHold}
      onTouchMove={handleMove}
      onTouchEnd={cancelHold}
      onTouchCancel={cancelHold}
      className={`text-4xl cursor-pointer select-none transition-all ${
        isHolding ? 'scale-110 opacity-70' : ''
      }`}
      style={{
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none'
      }}
      title={isAdminMode ? 'Long press to exit admin mode' : ''}
    >
      🎵
      {isHolding && (
        <div 
          className="absolute -bottom-1 left-0 right-0 h-1 bg-blue-500 rounded transition-all"
          style={{
            animation: 'holdProgress 1s linear forwards'
          }}
        />
      )}
    </div>
  );
};

// Main App Component
function App() {
  // Passkey authentication state
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authError, setAuthError] = useState('');
  const [authErrorDetails, setAuthErrorDetails] = useState(null);
  // In-app-browser guard (2026-08-25). If the user opened this link in
  // an embedded WebView (QR scanner app, Instagram/Facebook in-app
  // browser, etc.), WebAuthn is often broken AND localStorage doesn't
  // persist across app-open sessions, so returning users can't be
  // recognised. Rather than let them hit an inscrutable failure, we
  // block the sign-in flow with a full-screen notice.
  // Dismissing the notice hands them back to the normal sign-in screen
  // where the admin's manual `fallback` password long-press still
  // works if they need to override.
  // See /app/frontend/src/browserDetection.js for detection heuristics
  // and /app/memory/CRITICAL_AGENT_NOTES.md for the RCA link.
  const [inAppBrowserBlock, setInAppBrowserBlock] = useState(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [userId, setUserId] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  
  // Debug logging state
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false); // Hidden by default
  
  const addDebugLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] ${message}`);
  };
  
  const [songs, setSongs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [rotationInfo, setRotationInfo] = useState(null);
  const [name, setName] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [songArtist, setSongArtist] = useState('');
  const [userEntry, setUserEntry] = useState(null);
  const [userQueuePosition, setUserQueuePosition] = useState(null); // Track user's position in queue (1-indexed)
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isCatalogRemovalsModalOpen, setIsCatalogRemovalsModalOpen] = useState(false);
  
  // Insert point state for queue management
  const [insertPointEntryId, setInsertPointEntryId] = useState(null);

  // Pause state: when true, new signups are blocked (returning users still allowed)
  const [entriesPaused, setEntriesPaused] = useState(false);

  // Runtime feature flags from the backend (cached at mount).
  const [featureFlags, setFeatureFlags] = useState({ groups_enabled: false });
  useEffect(() => {
    axios.get(`${API}/config`)
      .then(r => setFeatureFlags(r.data || {}))
      .catch(() => setFeatureFlags({ groups_enabled: false }));
  }, []);
  const groupsEnabled = !!featureFlags.groups_enabled;
  
  // Restore admin session from localStorage on mount
  useEffect(() => {
    const savedAdminSession = localStorage.getItem('karaoke_admin_session');
    if (savedAdminSession) {
      // Restore admin state
      setIsAdminLoggedIn(true);
      setIsAdminMode(true);
      // Also restore to sessionStorage for consistency
      sessionStorage.setItem('adminSession', savedAdminSession);
    }
  }, []);
  
  // Long press state for banner (when admin not logged in)
  const [isHoldingBanner, setIsHoldingBanner] = useState(false);
  const bannerHoldTimeoutRef = useRef(null);
  const BANNER_HOLD_DURATION = 1500; // 1.5 seconds

  const startBannerHold = (e) => {
    // If admin is already logged in, use single click
    if (isAdminLoggedIn) {
      return; // Let the onClick handle it
    }
    
    e.preventDefault();
    setIsHoldingBanner(true);

    bannerHoldTimeoutRef.current = setTimeout(() => {
      setIsHoldingBanner(false);
      setIsAdminMode(true); // Show admin login
    }, BANNER_HOLD_DURATION);
  };

  const cancelBannerHold = () => {
    setIsHoldingBanner(false);
    if (bannerHoldTimeoutRef.current) {
      clearTimeout(bannerHoldTimeoutRef.current);
    }
  };

  // Handle banner click - single click only works when admin is logged in
  const handleBannerClick = () => {
    if (isAdminLoggedIn) {
      setIsAdminMode(!isAdminMode);
    }
    // If not logged in, long press handles it
  };
  const [notifications, setNotifications] = useState([]);
  const [canSelectNextSong, setCanSelectNextSong] = useState(false);
  const [showHostAssistPrompt, setShowHostAssistPrompt] = useState(false);
  const [hostAssistPassword, setHostAssistPassword] = useState('');
  const [hostAssistError, setHostAssistError] = useState('');
  
  // Long press state for fallback button on sign-in page
  const [isHoldingFallback, setIsHoldingFallback] = useState(false);
  const [fallbackProgress, setFallbackProgress] = useState(0);
  const fallbackHoldTimeoutRef = useRef(null);
  const fallbackProgressIntervalRef = useRef(null);
  
  const turnCompletionTimerRef = useRef(null);
  const hasShownReminderRef = useRef(false);
  const lastPlayedStateRef = useRef(false);
  // SSE health watchdog: wall-clock (ms) of the most recent event of
  // any type received on /api/events/queue (`queue-changed` or `ping`).
  // Consumed by the aux-poll interval below to gate the poll when
  // SSE is healthy. Initialised to 0 so polling runs until the first
  // event arrives (the SSE handler stamps this immediately on connect
  // via the initial `queue-changed reason=connected` event).
  const sseLastEventAtRef = useRef(0);
  // 🚨 Latest-fetchers ref: the SSE useEffect below mounts ONCE (empty
  // deps) and reads the current fetch callbacks from this ref every
  // time a `queue-changed` event arrives. If we depended on the
  // callbacks directly, `fetchNotifications`' own `useCallback` deps
  // include `notifications` — which changes every time it calls
  // `setNotifications` — making the SSE effect tear down and
  // reconnect the EventSource on every notification refresh. That
  // loop was the cause of the 2026-08-21 "blank page for several
  // seconds after page load" production incident. Do NOT wire the
  // callbacks back into the SSE effect's deps.
  const sseFetchersRef = useRef({
    fetchQueue: null,
    fetchRotationInfo: null,
    fetchNotifications: null,
  });
  
  // KaraFun state
  const [karafunStatus, setKarafunStatus] = useState({
    is_running: false,
    is_connected: false,
    queue_size: 0
  });
  const [karafunLoading, setKarafunLoading] = useState(false);
  
  // Listen for debug logs from passkeyUtils
  useEffect(() => {
    const handleDebugLog = (event) => {
      const { message, type, timestamp } = event.detail;
      setDebugLogs(prev => [...prev, { timestamp, message, type }]);
    };
    
    window.addEventListener('passkeyDebugLog', handleDebugLog);
    return () => window.removeEventListener('passkeyDebugLog', handleDebugLog);
  }, []);
  
  // Passkey authentication on app load
  useEffect(() => {
    const authenticate = async () => {
      // In-app-browser guard runs BEFORE any passkey call. Emits a
      // one-shot `client_session_start` telemetry event on every
      // fresh session so we can size the population by UA, then
      // hard-blocks if a known-broken in-app browser is detected.
      // Host decision 2026-09-08: HARD BLOCK, no dismiss escape hatch.
      // Users must open the app in Safari or Chrome to sign in.
      // Admin fallback long-press is also only reachable from a
      // legitimate browser — never from inside an in-app WebView.
      const inApp = detectInAppBrowser();
      const platform = getPlatformSummary();
      reportClientEvent('client_session_start', {
        user_agent: navigator.userAgent,
        is_in_app_browser: inApp.isInApp,
        in_app_name: inApp.appName,
        platform,
        is_pk_supported: passkeyUtils.isPasskeySupported(),
        has_local_pk_hint: !!localStorage.getItem('karaoke_has_passkey_v2'),
      });
      if (inApp.isInApp) {
        reportClientEvent('in_app_browser_blocked', {
          user_agent: navigator.userAgent,
          in_app_name: inApp.appName,
          matched_signal: inApp.matched,
        });
        setInAppBrowserBlock({ appName: inApp.appName });
        setIsAuthenticating(false);
        return;
      }

      setIsAuthenticating(true);
      setAuthError('');
      setDebugLogs([]);
      
      // Timing telemetry for the "bloated excludeCredentials causes
      // NotAllowedError" hypothesis 2026-09-08. Hoisted above the
      // outer try (2026-09-08) so the failure-path telemetry at the
      // catch below can read it. Previously it was `const`-scoped
      // inside the try and threw a silent ReferenceError in catch,
      // dropping `duration_ms` from every `passkey_auth_failed`
      // event. If iOS's exclude-list iteration is slow on the
      // current 1000+ credential DB, register-first ceremonies that
      // fail should show `duration_ms` values ≥1000. A fast throw
      // (<500ms) indicates iOS refused synchronously
      // (InvalidStateError-adjacent path).
      const _authStartMs = performance.now();
      try {
        addDebugLog('🔐 Starting passkey authentication...', 'info');
        addDebugLog(`Tab opened at: ${new Date().toISOString()}`, 'info');
        addDebugLog(`Session storage: fingerprint=${!!sessionStorage.getItem('karaoke_device_fingerprint')}, authenticated=${sessionStorage.getItem('karaoke_authenticated')}`, 'info');
        addDebugLog(`Backend URL: ${process.env.REACT_APP_BACKEND_URL}`, 'info');
        addDebugLog(`User Agent: ${navigator.userAgent}`, 'info');
        // Fix #6 (shipped 2026-08-25): removed auth failure count debug log — counter retired.
        
        const result = await passkeyUtils.authenticateUser();
        
        if (result.success) {
          setDeviceFingerprint(result.deviceFingerprint);
          setUserId(result.user_id || result.userId);
          setUsingFallback(result.usingFallback || false);
          
          if (result.lastUsedName) {
            setName(result.lastUsedName);
          }
          
          if (result.usingFallback) {
            addDebugLog('⚠️ Using fallback mode (no device lock or incompatible device)', 'warning');
          } else if (result.fromCache) {
            addDebugLog('✅ Authenticated from cache', 'success');
          } else {
            addDebugLog('✅ Passkey authentication successful', 'success');
          }
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        addDebugLog(`❌ Authentication error: ${error.message}`, 'error');
        addDebugLog(`Error name: ${error.name}`, 'error');
        addDebugLog(`Error stack: ${error.stack}`, 'error');
        
        // Fix #6 (shipped 2026-08-25): removed auth failure count increment — counter retired.
        
        // Report to backend telemetry so post-mortem analysis can
        // classify passkey failures by UA + error.name. See
        // /app/frontend/src/clientTelemetry.js.
        //
        // `duration_ms` 2026-08-29 captures the WebAuthn ceremony
        // duration end-to-end. Correlates with the "bloated exclude
        // list drives NotAllowedError" theory: fast throws (<500ms)
        // = OS refused synchronously (InvalidStateError-class),
        // slow throws (>1s) = OS iterated a large exclude list
        // and/or showed biometric UI before failing.
        try {
          const _authDurationMs = Math.round(performance.now() - _authStartMs);
          reportClientEvent('passkey_auth_failed', {
            error_name: error.name || 'Error',
            error_message: (error.message || '').slice(0, 300),
            // 2026-10-07 (Mike follow-up): Pass through the ORIGINAL
            // WebAuthn error name/message that was wrapped by
            // registerPasskey() / authenticateWithPasskey(). Lets us
            // distinguish NotAllowedError (biometric/cancel/timeout)
            // from SecurityError (rpId mismatch) from TimeoutError etc.
            // All previously logged as `error_name=Error` due to the
            // wrap. Safely null when caller didn't preserve originals.
            original_error_name: error.originalErrorName || null,
            original_error_message: error.originalErrorMessage || null,
            user_agent: navigator.userAgent,
            is_pk_supported: passkeyUtils.isPasskeySupported(),
            has_local_pk_hint: !!localStorage.getItem('karaoke_has_passkey_v2'),
            duration_ms: _authDurationMs,
          });
        } catch (_e) { /* telemetry is best-effort */ }
        
        // Store detailed error info for display
        const errorDetails = {
          message: error.message || 'Unknown error',
          name: error.name || 'Error',
          stack: error.stack ? error.stack.split('\n').slice(0, 3).join('\n') : 'No stack trace',
          timestamp: new Date().toISOString(),
        };
        
        // Provide more specific error messages
        let errorMessage = error.message || 'Authentication failed';
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Authentication cancelled or not allowed by browser.';
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'Biometric authentication not supported on this device/browser.';
        } else if (error.name === 'TypeError') {
          errorMessage = `Configuration error: ${error.message}`;
        } else if (error.message && error.message.includes('Failed to start')) {
          errorMessage = `Backend error: ${error.message}`;
        }
        
        setAuthError(errorMessage);
        setAuthErrorDetails(errorDetails);
      } finally {
        setIsAuthenticating(false);
      }
    };
    
    authenticate();
  }, []);

  // Fetch songs from database
  const fetchSongs = useCallback(async () => {
    // No longer using song database - using text input fields
    setSongs([]);
  }, []);

  // Fetch current queue
  const fetchQueue = useCallback(async () => {
    // Timing telemetry 2026-08-30 — sample-based to avoid flooding
    // the debug log on 20+ clients polling every 10s. Emits at most
    // once per client per ~60 seconds. Correlates with the "Saturday
    // felt sluggish at 50+ entries" hypothesis: if p95 duration is
    // >800ms, server or DB latency is real; if <400ms, sluggishness
    // is upstream (KaraFun) or client-side rendering.
    const _fetchStartMs = performance.now();
    const _shouldSample = !window._lastQueueFetchSample ||
      (Date.now() - window._lastQueueFetchSample) > 60000;
    try {
      const response = await axios.get(`${API}/queue`, {
        params: isAdminMode ? {} : { device_fingerprint: deviceFingerprint }
      });
      if (_shouldSample) {
        window._lastQueueFetchSample = Date.now();
        try {
          reportClientEvent('queue_fetch_timing', {
            duration_ms: Math.round(performance.now() - _fetchStartMs),
            entry_count: (response.data || []).length,
            is_admin: !!isAdminMode,
          });
        } catch (_e) { /* best-effort */ }
      }
      // Defensive dedup: never let two entries with the same id reach the
      // renderer. Same-name duplicates ARE allowed in some edge states; this
      // only guards against the rare case where the same row would render
      // twice and cause React key collisions. Preserve incoming order.
      const incoming = response.data || [];
      const seenIds = new Set();
      const deduped = [];
      for (const row of incoming) {
        if (row && row.id && !seenIds.has(row.id)) {
          seenIds.add(row.id);
          deduped.push(row);
        } else if (row && row.id) {
          console.warn('[queue] duplicate id dropped from render:', row.id, row.user_name);
        }
      }
      setQueue(deduped);

      // Refresh pause state on each queue poll (lightweight singleton lookup)
      try {
        const psResp = await axios.get(`${API}/queue/pause-state`);
        setEntriesPaused(!!psResp.data?.entries_paused);
      } catch (e) {
        // Non-fatal: keep current value
      }

      // Refresh insert point state in admin mode (auto-clears after consumption)
      if (isAdminMode) {
        try {
          const ipResp = await axios.get(`${API}/queue/insert-point`);
          setInsertPointEntryId(
            ipResp.data?.insert_point_set ? ipResp.data.insert_point_entry_id : null
          );
        } catch (e) {
          // Non-fatal: keep current value
        }
      }
      
      // Find user's entry and position
      const userIndex = response.data.findIndex(entry => entry.can_edit);
      const currentUserEntry = userIndex >= 0 ? response.data[userIndex] : null;
      setUserEntry(currentUserEntry);
      setUserQueuePosition(userIndex >= 0 ? userIndex + 1 : null); // 1-indexed position

      // Manage notifications for upcoming turns (ONLY for "up next" - position 2)
      if (!isAdminMode) {
        const userEntryWithIndex = response.data.find(entry => entry.is_next && entry.can_edit && !entry.has_played);

        // Close previous notification if it exists
        if (window.activeNotification) {
          window.activeNotification.close();
          window.activeNotification = null;
        }

        // Only send notification when user is "up next" (position 2) AND hasn't played
        if (userEntryWithIndex && Notification.permission === 'granted') {
          const notification = sendBrowserNotification(
            "🚨 You're Next!",
            "Get ready! You'll be singing soon! 🎤",
            "user-up-next"
          );
          window.activeNotification = notification;
          
          // Auto-close after 30 seconds
          if (notification) {
            setTimeout(() => {
              if (window.activeNotification === notification) {
                notification.close();
                window.activeNotification = null;
              }
            }, 30000);
          }
        }

        // NEW: Track turn completion and send reminder after 1 minute
        if (currentUserEntry) {
          const currentPlayedState = currentUserEntry.has_played;
          
          // Detect transition from unplayed to played
          if (currentPlayedState && !lastPlayedStateRef.current && !hasShownReminderRef.current) {
            // User just completed their turn! Set 1-minute timer
            console.log('Turn completed - setting 1 minute reminder timer');
            
            // Clear any existing timer
            if (turnCompletionTimerRef.current) {
              clearTimeout(turnCompletionTimerRef.current);
            }
            
            // Set new timer for 1 minute (60000ms)
            turnCompletionTimerRef.current = setTimeout(() => {
              // Check if user still hasn't updated their song
              // They should have can_select_next_song = true if they haven't updated
              if (Notification.permission === 'granted') {
                const notification = sendBrowserNotification(
                  "🎵 Time to Pick Your Next Song!",
                  "Don't forget to add a new song for your next turn in the rotation!",
                  "song-reminder"
                );
                
                hasShownReminderRef.current = true;
                
                // Auto-close after 30 seconds
                if (notification) {
                  setTimeout(() => {
                    notification.close();
                  }, 30000);
                }
              }
            }, 60000); // 60 seconds
          }
          
          // Update last played state
          lastPlayedStateRef.current = currentPlayedState;
          
          // Reset reminder flag when user updates their song (has_played becomes false again)
          if (!currentPlayedState && hasShownReminderRef.current) {
            hasShownReminderRef.current = false;
            console.log('User updated song - resetting reminder flag');
          }
        } else {
          // User no longer has an entry - reset states
          lastPlayedStateRef.current = false;
          hasShownReminderRef.current = false;
          if (turnCompletionTimerRef.current) {
            clearTimeout(turnCompletionTimerRef.current);
            turnCompletionTimerRef.current = null;
          }
        }
      }
    } catch (error) {
      // setError('Failed to load queue');
    }
  }, [isAdminMode, deviceFingerprint]);

  // Fetch rotation info
  const fetchRotationInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rotation/current`);
      setRotationInfo(response.data);
    } catch (error) {
      console.error('Error fetching rotation info:', error);
    }
  }, []);

  // KaraFun functions
  const fetchKarafunStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/karafun/status`);
      setKarafunStatus(response.data);
    } catch (error) {
      console.error('Error fetching KaraFun status:', error);
    }
  }, []);

  const startKarafunSync = async () => {
    setKarafunLoading(true);
    try {
      const response = await axios.post(`${API}/karafun/start`, {}, {
        validateStatus: function (status) {
          return status >= 200 && status < 300; // Accept all 2xx status codes including 202
        }
      });
      
      // Check if it's a 202 status (login required)
      if (response.status === 202 && response.data?.status === 'login_required') {
        // Return special value to indicate login is needed
        setKarafunLoading(false);
        return {loginRequired: true};
      }
      
      // If not 202, treat as success
      setSuccess('KaraFun sync started!');
      await fetchKarafunStatus();
      return {loginRequired: false};
    } catch (error) {
      setError('Failed to start KaraFun sync: ' + (error.response?.data?.detail || error.message));
      return {loginRequired: false, error: true};
    } finally {
      setKarafunLoading(false);
    }
  };

  const handleStartSync = async () => {
    const result = await startKarafunSync();
    return result;
  };

  const stopKarafunSync = async () => {
    setKarafunLoading(true);
    try {
      await axios.post(`${API}/karafun/stop`);
      setSuccess('KaraFun sync stopped');
      await fetchKarafunStatus();
    } catch (error) {
      setError('Failed to stop KaraFun sync: ' + (error.response?.data?.detail || error.message));
    } finally {
      setKarafunLoading(false);
    }
  };

  const karafunPlay = async () => {
    try {
      await axios.post(`${API}/karafun/playback/play`);
      setSuccess('Playing');
    } catch (error) {
      setError('Failed to play: ' + (error.response?.data?.detail || error.message));
    }
  };

  const karafunPause = async () => {
    try {
      await axios.post(`${API}/karafun/playback/pause`);
      setSuccess('Paused');
    } catch (error) {
      setError('Failed to pause: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Skip functionality removed - causes WebSocket disconnections in production



  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      if (isAdminLoggedIn) {
        // Admin notifications
        const response = await axios.get(`${API}/admin/notifications`);
        const newNotifications = response.data;
        
        // Check for new high-priority notifications for admin browser notifications
        // ONLY send for pre-turn song changes
        if ('Notification' in window && Notification.permission === 'granted') {
          const previousNotificationIds = notifications.map(n => n.id);
          const freshNotifications = newNotifications.filter(n => !previousNotificationIds.includes(n.id));
          
          freshNotifications.forEach(notification => {
            // Only notify admin for pre-turn song changes
            if (notification.type === 'song_change_pre_turn') {
              sendBrowserNotification(
                '🎵 Song Change Alert',
                notification.message,
                `admin-pre-turn-${notification.id}`
              );
            }
          });
        }
        
        setNotifications(newNotifications);
      } else {
        // Regular user notifications
        const response = await axios.get(`${API}/notifications`, {
          params: { device_fingerprint: deviceFingerprint, limit: 10 }
        });
        const userNotifications = response.data;
        
        // Send browser notifications for new user notifications
        if ('Notification' in window && Notification.permission === 'granted') {
          const previousNotificationIds = notifications.map(n => n.id);
          const freshNotifications = userNotifications.filter(n => !previousNotificationIds.includes(n.id));
          
          freshNotifications.forEach(notification => {
            // Only send browser notifications for up_next and song_reminder
            if (notification.type === 'up_next') {
              sendBrowserNotification(
                '🎤 You\'re Up Next!',
                notification.message,
                `user-up-next-${notification.id}`
              );
            } else if (notification.type === 'song_reminder') {
              sendBrowserNotification(
                '🎵 Song Reminder',
                notification.message,
                `user-reminder-${notification.id}`
              );
            }
          });
        }
        
        setNotifications(userNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [isAdminLoggedIn, notifications, deviceFingerprint]);

  // Initialize sample songs (first time setup)
  const initializeSongs = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/init-songs`);
      await fetchSongs();
    } catch (error) {
      console.error('Error initializing songs:', error);
    }
  }, [fetchSongs]);

  // Join queue directly (no confirmation modal)
  const handleJoinQueue = async (e, title = songTitle, artist = songArtist, songId = undefined) => {
    if (e) e.preventDefault();
    // Use provided title/artist or fall back to state values
    const finalTitle = title || songTitle;
    const finalArtist = artist || songArtist;
    // Check if we have a name (either from state or from userEntry when editing)
    const hasName = name.trim() || (userEntry && userEntry.user_name && userEntry.user_name.trim());
    if (!hasName || !finalTitle.trim() || !finalArtist.trim()) return;
    // Update state with the values being used
    setSongTitle(finalTitle);
    setSongArtist(finalArtist);
    
    // Directly join queue without confirmation
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // CRITICAL: Verify deviceFingerprint exists before submitting
      let finalDeviceFingerprint = deviceFingerprint;
      
      if (!finalDeviceFingerprint) {
        console.warn('⚠️ Device fingerprint missing - re-authenticating...');
        try {
          const authResult = await passkeyUtils.authenticateUser();
          if (authResult.success) {
            finalDeviceFingerprint = authResult.deviceFingerprint;
            setDeviceFingerprint(finalDeviceFingerprint);
            setUserId(authResult.userId);
            setUsingFallback(authResult.usingFallback || false);
            console.log('✅ Re-authentication successful, fingerprint recovered');
          } else {
            throw new Error('Re-authentication failed');
          }
        } catch (reAuthError) {
          try {
            reportClientEvent('passkey_auth_failed', {
              error_name: reAuthError.name || 'Error',
              error_message: (reAuthError.message || '').slice(0, 300),
              // 2026-10-07 (Mike follow-up): preserve original WebAuthn
              // error details through the wrap. See primary emitter
              // ~line 10135 for rationale.
              original_error_name: reAuthError.originalErrorName || null,
              original_error_message: reAuthError.originalErrorMessage || null,
              call_site: 'join_queue_reauth',
              user_agent: navigator.userAgent,
              is_pk_supported: passkeyUtils.isPasskeySupported(),
            });
          } catch (_e) { /* telemetry is best-effort */ }
          throw new Error('Lost device identity - please refresh the page');
        }
      }
      
      // Prefer the user's typed name (reflects any edit made in Change Song
      // mode). Fall back to userEntry.user_name only if the input was somehow
      // left empty. Restores the 2026-09-15 name-only-edit intent — the old
      // ternary here (`userEntry && userEntry.user_name ? userEntry.user_name : name.trim()`)
      // ignored the user's edit whenever they were already queued, so the
      // shipped fix appeared broken. Corrected 2026-09-22.
      const submittedName = name.trim() || (userEntry && userEntry.user_name) || '';
      
      const requestData = {
        name: submittedName,
        song_title: finalTitle.trim(),
        song_artist: finalArtist.trim(),
        device_fingerprint: finalDeviceFingerprint,
        using_fallback: usingFallback
      };
      // 2026-10-14 — forward the catalog song_id when the user picked
      // from search results. Omitted on Change-Song-with-no-reselect
      // (selectedSong pre-fill clears .id), where backend falls back
      // to find_karafun_song_id() by title/artist.
      if (songId !== undefined && songId !== null && songId !== '') {
        requestData.song_id = songId;
      }

      const response = await axios.post(`${API}/queue/join`, requestData);
      
      setSuccess(response.data.message || 'Successfully joined the queue!');
      
      // Store the last used name for this device
      if (submittedName) {
        localStorage.setItem('karaoke_last_used_name_v2', submittedName);
      }
      
      // Clear form and fetch updated queue
      setSongTitle('');
      setSongArtist('');
      await fetchQueue();
      await fetchRotationInfo();
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error joining queue:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Error joining queue';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Update song
  const handleUpdateSong = async (songTitle, songArtist) => {
    try {
      await axios.put(`${API}/queue/update-song`, {
        song_title: songTitle,
        song_artist: songArtist,
        device_fingerprint: deviceFingerprint
      });
      
      setSuccess('Success! Your song has been updated.');
      await fetchQueue();
      if (isAdminLoggedIn) {
        await fetchNotifications();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update song');
    }
  };

  // Update pitch offset for the user's own entry (private — only sender's
  // device_fingerprint can mutate). Backend caps to ±6 and rejects entries
  // already played / not owned.
  const handleUpdatePitch = async (entryId, pitchOffset) => {
    try {
      await axios.put(`${API}/queue/${entryId}/pitch`, {
        device_fingerprint: deviceFingerprint,
        pitch_offset: Number(pitchOffset),
      });
      const n = Number(pitchOffset);
      let msg;
      if (n === 0) {
        msg = 'Your song has been reset to its original pitch.';
      } else if (n > 0) {
        msg = `Your song has been pitched up +${n}`;
      } else {
        msg = `Your song has been pitched down ${n}`;
      }
      setSuccess(msg);
      await fetchQueue();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update pitch');
      throw error;
    }
  };

  // Select song for next rotation
  const handleSelectNextSong = async (songTitle, songArtist) => {
    try {
      await axios.post(`${API}/queue/select-next-song`, {
        song_title: songTitle,
        song_artist: songArtist,
        device_fingerprint: deviceFingerprint
      });
      
      setSuccess('Song selected for next rotation!');
      setName('');
      setSongTitle('');
      setSongArtist('');
      await fetchQueue();
      if (isAdminLoggedIn) {
        await fetchNotifications();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to select next song');
    }
  };

  // Removed complex handleNextSongFormSubmit function

  // Admin functions
  const handleMoveToPosition = async (entryId, newPosition) => {
    try {
      await axios.put(`${API}/admin/queue/move-to-position`, {
        entry_id: entryId,
        new_position: newPosition
      });
      setSuccess(`Entry moved to position ${newPosition}!`);
      await fetchQueue();
    } catch (error) {
      setError('Failed to move entry');
      await fetchQueue();
    }
  };

  const handleRemoveEntry = async (entryId) => {
    // Find the entry details for confirmation message
    const entry = queue.find(e => e.id === entryId);
    const userName = entry ? entry.user_name : 'this user';
    const songInfo = entry ? `${entry.song_title} - ${entry.song_artist}` : 'this song';
    
    // Require confirmation before removing
    const confirmed = window.confirm(
      `Are you sure you want to remove ${userName} from the queue?\n\nSong: ${songInfo}\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) {
      return; // User cancelled
    }
    
    try {
      await axios.delete(`${API}/admin/queue/remove/${entryId}`);
      setSuccess('Entry removed from queue!');
      await fetchQueue();
    } catch (error) {
      setError('Failed to remove entry');
    }
  };

  const handleEditEntry = async (entryId, userName, songTitle, songArtist, songId = undefined) => {
    try {
      const payload = {
        user_name: userName,
        song_title: songTitle,
        song_artist: songArtist
      };
      // 2026-10-14 — thread catalog song_id through so the backend
      // dispatch step can look up the KaraFun song directly instead of
      // re-resolving via title/artist string match.
      if (songId !== undefined && songId !== null && songId !== '') {
        payload.song_id = songId;
      }
      await axios.put(`${API}/admin/queue/${entryId}/edit`, payload);
      setSuccess(`Entry edited successfully!`);
      await fetchQueue();
    } catch (error) {
      setError('Failed to edit entry');
    }
  };

  const handleMarkCurrent = async (entryId) => {
    try {
      await axios.put(`${API}/admin/queue/mark-current`, {
        entry_id: entryId
      });
      setSuccess('Current song updated!');
      await fetchQueue();
    } catch (error) {
      setError('Failed to update current song');
    }
  };

  const handleMarkPlayed = async (entryId) => {
    try {
      await axios.put(`${API}/admin/queue/mark-played/${entryId}`);
      setSuccess('Song marked as played!');
      await fetchQueue();
    } catch (error) {
      setError('Failed to mark song as played');
    }
  };

  const handleSetInsertPoint = async (entryId) => {
    try {
      const response = await axios.post(`${API}/queue/insert-point`, { entry_id: entryId });
      setInsertPointEntryId(entryId);
      setSuccess(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to set insert point');
    }
  };

  const handleClearNotifications = async () => {
    try {
      await axios.delete(`${API}/admin/notifications/clear`);
      setNotifications([]);
    } catch (error) {
      setError('Failed to clear notifications');
    }
  };

  const handleAdvanceQueue = async () => {
    try {
      const response = await axios.post(`${API}/admin/queue/advance`);
      setSuccess(response.data.message);
      await fetchQueue();
      await fetchNotifications();
    } catch (error) {
      setError('Failed to advance queue');
    }
  };

  const handleAddToQueue = async (entryData) => {
    try {
      const response = await axios.post(`${API}/admin/queue/add`, entryData);
      setSuccess(`${response.data.message}: ${response.data.song}`);
      await fetchQueue();
      await fetchNotifications();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add to queue');
    }
  };

  // Admin Regulars feature (2026-09-17): persist an ephemeral admin-added
  // entry so it survives session reset with song history intact. Blocked
  // server-side at pos-1.
  const handleConvertToRegular = async (entryId) => {
    try {
      const response = await axios.post(`${API}/admin/queue-entry/${entryId}/convert-to-regular`);
      setSuccess(`Converted to Regular. Song history will persist across sessions.`);
      await fetchQueue();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to convert to Regular');
    }
  };

  const handleResetSession = async () => {
    // No additional confirmation needed - the hold-to-confirm button already provides safety
    try {
      const response = await axios.post(`${API}/admin/reset-session`);
      setSuccess(response.data.message || 'Session reset successfully');
      await fetchQueue();
      await fetchNotifications();
    } catch (error) {
      console.error('Reset session error:', error);
      setError(error.response?.data?.detail || 'Failed to reset session');
    }
  };

  // Check for overdue song selections
  const checkOverdueSelections = useCallback(async () => {
    if (isAdminLoggedIn) {
      try {
        await axios.get(`${API}/admin/overdue-notifications`);
        // Notifications are automatically created by the endpoint
        await fetchNotifications();
      } catch (error) {
        console.error('Error checking overdue selections:', error);
      }
    }
  }, [isAdminLoggedIn, fetchNotifications]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Karaoke Queue Notifications Enabled', {
            body: "You'll receive notifications about your turn and queue updates.",
            icon: '/favicon.ico'
          });
        }
      });
    }
  }, []);

  // ── Real-time queue updates via Server-Sent Events ─────────────────
  // Replaced 2s polling on 2026-07-24. The backend pushes a
  // 'queue-changed' event after every queue-mutating endpoint (and from
  // a handful of WS-driven code paths). We refetch immediately on each
  // event for near-instant UI updates.
  //
  // 2026-08-21 extension: the backend now also emits a `ping` event
  // every 10s. `sseLastEventAtRef.current` tracks the wall-clock time
  // of the most recent event of ANY type. The 5s aux poll below reads
  // this ref: if it's < 25s old, SSE is considered healthy and the
  // rotation/notifications fetch is skipped (dropping ~9 polls/user/min
  // → ~400 req/min saved on a 45-user busy night). If the ref goes
  // stale (>25s — SSE connection silently dropped, proxy idle-timeout,
  // etc.), polling resumes automatically on the next tick until SSE
  // recovers.
  //
  // 🚨 DO NOT REMOVE THE POLLING FALLBACK. It's the recovery path
  // when SSE silently drops.
  //
  // 🚨 DO NOT add fetch callbacks directly to this effect's deps.
  // `fetchNotifications` is `useCallback([isAdminLoggedIn,
  // notifications, deviceFingerprint])` — its `notifications`
  // dependency changes every time it calls `setNotifications`, which
  // would make this effect tear down and reconnect the EventSource
  // in a tight loop on page load. Instead, hold the latest callbacks
  // in a ref (`sseFetchersRef` above) and this effect depends only on
  // `BACKEND_URL` so it mounts once. Cost me a full production
  // incident 2026-08-21 (blank page for several seconds → user rolled
  // back to e3654d4).
  //
  // EventSource handles reconnect natively (browser will retry with
  // exponential backoff). On reconnect, the server's initial event
  // ("reason": "connected") triggers a fresh fetch so state is
  // self-healing.
  useEffect(() => {
    if (!BACKEND_URL) return;
    const url = `${BACKEND_URL}/api/events/queue`;
    let es;
    try {
      es = new EventSource(url);
    } catch (err) {
      console.warn('SSE EventSource failed to construct; falling back to polling only:', err);
      return;
    }

    const markAlive = () => { sseLastEventAtRef.current = Date.now(); };

    const handleQueueChanged = () => {
      markAlive();
      // Call the LATEST fetchers via the ref — see the effect's
      // header comment for why we don't depend on the callbacks
      // directly.
      sseFetchersRef.current.fetchQueue?.();
      sseFetchersRef.current.fetchRotationInfo?.();
      sseFetchersRef.current.fetchNotifications?.();
      // Fix #45 (shipped 2026-09-28): notify standalone widgets that piggyback on
      // queue-change signals (e.g., AutoInsertButton's counter). Uses a
      // window event so we don't have to prop-drill fetchers into every
      // child component. Any listener re-fetches its own state.
      try {
        window.dispatchEvent(new CustomEvent('karaoke:queue-changed'));
      } catch {}
    };

    const handlePing = () => { markAlive(); };

    es.addEventListener('queue-changed', handleQueueChanged);
    es.addEventListener('ping', handlePing);

    // Native error handler — EventSource auto-reconnects, but log
    // visible errors so we can spot persistent connection problems.
    es.onerror = (err) => {
      // readyState 0 = CONNECTING (auto-reconnecting), 2 = CLOSED
      if (es.readyState === 2) {
        console.warn('SSE connection closed; safety-net polling will keep state fresh.');
      }
      // CONNECTING is normal during reconnect — don't spam logs.
    };

    return () => {
      es.removeEventListener('queue-changed', handleQueueChanged);
      es.removeEventListener('ping', handlePing);
      es.close();
    };
  }, []);

  // ── Safety-net poll ─────────────────────────────────────────────────
  // Fallback for when the SSE connection silently drops (rare on stable
  // networks, possible on flaky ones or behind proxies with short idle
  // timeouts). Polls every 10s — frequent enough to recover gracefully,
  // infrequent enough to keep Mongo ops/sec well under M0/M2 fair-use
  // limits even at 45+ concurrent users.
  //
  // 🚨 DO NOT REMOVE THIS POLL. It's the recovery path for dropped SSE.
  useEffect(() => {
    const queueInterval = setInterval(() => {
      fetchQueue();
    }, 10000);

    return () => clearInterval(queueInterval);
  }, [fetchQueue]);

  // Keep sseFetchersRef pointing at the LATEST fetch callbacks after
  // every render. The SSE useEffect above reads from this ref inside
  // its event handler, so it always calls today's closures without
  // needing the callbacks in its own deps. See the SSE effect's
  // header comment (2026-08-21 RCA) for why this indirection exists.
  useEffect(() => {
    sseFetchersRef.current.fetchQueue = fetchQueue;
    sseFetchersRef.current.fetchRotationInfo = fetchRotationInfo;
    sseFetchersRef.current.fetchNotifications = fetchNotifications;
  }, [fetchQueue, fetchRotationInfo, fetchNotifications]);

  useEffect(() => {
    // 2026-08-21 SSE extension: aux polling now gated by SSE health.
    // When SSE is healthy (last event <25s ago), skip the aux poll —
    // rotation + notifications are refreshed on `queue-changed` events
    // in the SSE handler above. When SSE goes stale (silent drop /
    // proxy idle-out), polling resumes automatically on the next tick.
    const SSE_STALE_THRESHOLD_MS = 25000;
    const auxInterval = setInterval(() => {
      const sseAge = Date.now() - (sseLastEventAtRef.current || 0);
      const sseHealthy = sseAge < SSE_STALE_THRESHOLD_MS;
      // Admin overdue-check still runs unconditionally — it's admin-only
      // and cheap, and doesn't have an SSE-driven counterpart.
      if (isAdminLoggedIn) {
        checkOverdueSelections();
      }
      if (sseHealthy) {
        return;
      }
      fetchRotationInfo();
      fetchNotifications();
    }, 5000);

    return () => clearInterval(auxInterval);
  }, [fetchRotationInfo, fetchNotifications, checkOverdueSelections, isAdminLoggedIn]);

  // Initial load (only after authentication)
  useEffect(() => {
    if (!deviceFingerprint) return; // Wait for authentication
    
    const init = async () => {
      await fetchSongs();
      await fetchQueue();
      await fetchRotationInfo();
    };
    
    init();
  }, [fetchSongs, initializeSongs, fetchQueue, fetchRotationInfo, deviceFingerprint]);

  // Auto-clear success messages after 3 seconds (errors require manual dismissal)
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Cleanup turn completion timer on unmount
  useEffect(() => {
    return () => {
      if (turnCompletionTimerRef.current) {
        clearTimeout(turnCompletionTimerRef.current);
      }
    };
  }, []);

  // Show in-app-browser BLOCK screen (2026-08-25). Takes precedence
  // over the authenticating spinner because we short-circuited out
  // of the auth flow before it started. Dismissing clears the block
  // and drops through to the normal sign-in path (where the admin's
  // manual `fallback` password long-press still works as an override
  // for genuinely-stuck users).
  if (inAppBrowserBlock) {
    return (
      <div
        className="min-h-screen bg-black flex items-center justify-center p-4"
        data-testid="in-app-browser-block"
      >
        <div className="max-w-lg w-full">
          <div className="card rounded-lg shadow-lg p-8 text-center border-2 border-yellow-500">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-yellow-500/20 p-4">
                <svg
                  className="h-12 w-12 text-yellow-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">
              This browser is not supported
            </h2>
            <p className="text-gray-200 mb-4 leading-relaxed">
              Please use a compatible browser, such as{' '}
              <span className="font-semibold text-white">Safari</span> or{' '}
              <span className="font-semibold text-white">Chrome</span>.
            </p>
            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
              If you are scanning the QR code, please use the{' '}
              <span className="font-semibold text-white">default camera app</span>{' '}
              on your phone, instead of a QR code scanner app, as these apps
              are not always compatible.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show authentication loading screen
  if (isAuthenticating) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          <div className="card rounded-lg shadow-lg p-8 text-center mb-4">
            <div className="mb-4">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-400"></div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">🔐 Authenticating...</h2>
            <p className="text-gray-300 mb-4">
              {passkeyUtils.isPasskeySupported() 
                ? `Please authenticate with ${passkeyUtils.getAuthenticatorName()}`
                : 'Setting up your device...'
              }
            </p>
            <p className="text-sm text-gray-400">
              This identifies your device securely and works in private browsing.
            </p>
          </div>
          
          {/* Show Debug Logs Button */}
          {!showDebugPanel && debugLogs.length > 0 && (
            <button
              onClick={() => setShowDebugPanel(true)}
              className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 mb-4"
            >
              Show Debug Logs
            </button>
          )}
          
          {/* Debug Panel */}
          {showDebugPanel && debugLogs.length > 0 && (
            <div className="bg-gray-900 rounded-lg shadow-lg p-4 border-2 border-blue-500">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-blue-400">Debug Logs</h3>
                <button 
                  onClick={() => setShowDebugPanel(false)}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Hide
                </button>
              </div>
              <div className="bg-black rounded p-3 max-h-96 overflow-y-auto font-mono text-xs">
                {debugLogs.map((log, idx) => (
                  <div key={idx} className={`mb-1 ${
                    log.type === 'error' ? 'text-red-400' : 
                    log.type === 'warning' ? 'text-yellow-400' : 
                    log.type === 'success' ? 'text-green-500' : 
                    'text-gray-300'
                  }`}>
                    <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Show authentication error screen
  if (authError) {
    const FALLBACK_HOLD_DURATION = 1000; // 1.0 second

    const startFallbackHold = (e) => {
      e.preventDefault();
      setIsHoldingFallback(true);
      setFallbackProgress(0);

      fallbackProgressIntervalRef.current = setInterval(() => {
        setFallbackProgress(prev => {
          const newProgress = prev + (50 / FALLBACK_HOLD_DURATION) * 100;
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, 50);

      fallbackHoldTimeoutRef.current = setTimeout(() => {
        setIsHoldingFallback(false);
        setFallbackProgress(0);
        clearInterval(fallbackProgressIntervalRef.current);
        handleHostAssistance();
      }, FALLBACK_HOLD_DURATION);
    };

    const cancelFallbackHold = () => {
      setIsHoldingFallback(false);
      setFallbackProgress(0);
      if (fallbackHoldTimeoutRef.current) {
        clearTimeout(fallbackHoldTimeoutRef.current);
      }
      if (fallbackProgressIntervalRef.current) {
        clearInterval(fallbackProgressIntervalRef.current);
      }
    };

    const handleHostAssistance = () => {
      setShowHostAssistPrompt(true);
      setHostAssistPassword('');
      setHostAssistError('');
    };
    
    const handleAdminSubmit = async () => {
      // Use separate fallback password to protect admin password.
      // Value changed 2026-09-21 (host request): "fallback" → "letmein".
      // If ever needed, keep this in sync with any docs/tests that
      // reference the literal (search: `letmein` in memory/*.md).
      // Comparison is case-insensitive + whitespace-trimmed 2026-09-22:
      // mobile keyboards auto-capitalize the first letter of text inputs
      // and sometimes append trailing spaces via autocorrect. The input
      // itself also carries autoCapitalize="none" as defense-in-depth.
      if (hostAssistPassword.trim().toLowerCase() === 'letmein') {
        // Password correct - enable classic mode
        const result = passkeyUtils.enableFallbackMode();
        if (result.success) {
          setDeviceFingerprint(result.deviceFingerprint);
          setUserId(result.userId);
          setUsingFallback(true);
          setShowHostAssistPrompt(false);
          setAuthError(''); // Clear error to show main app
        }
      } else {
        setHostAssistError('Error');
      }
    };
    
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        {/* Admin Password Prompt Modal */}
        {showHostAssistPrompt && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 z-40"
              onClick={() => setShowHostAssistPrompt(false)}
            />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
              <div className="card rounded-lg shadow-2xl max-w-md w-full p-6 border border-gray-600 pointer-events-auto">
                <form onSubmit={(e) => { e.preventDefault(); handleAdminSubmit(); }} autoComplete="off" data-form-type="other">
                  <input
                    type="text"
                    name={`host_assist_${Date.now()}`}
                    value={hostAssistPassword}
                    onChange={(e) => setHostAssistPassword(e.target.value)}
                    placeholder=""
                    className="input w-full px-4 py-3 rounded-lg bg-gray-700 text-white border border-gray-600"
                    style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' }}
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    inputMode="text"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setShowHostAssistPrompt(false);
                    }}
                    autoFocus
                  />
                  {hostAssistError && (
                    <p className="text-red-400 text-sm mt-3">{hostAssistError}</p>
                  )}
                </form>
              </div>
            </div>
          </>
        )}
        
        <div className="max-w-4xl w-full">
          <div className="card rounded-lg shadow-lg p-8 mb-4">
            <h2 className="text-4xl font-bold text-blue-400 mb-6 text-center">SIGN IN</h2>
            
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary px-6 py-4 rounded-lg font-semibold w-full mb-4 text-lg"
            >
              🔐 Tap Here to Sign In
            </button>
            
            <button
              onMouseDown={startFallbackHold}
              onMouseUp={cancelFallbackHold}
              onMouseLeave={cancelFallbackHold}
              onTouchStart={startFallbackHold}
              onTouchEnd={cancelFallbackHold}
              onTouchCancel={cancelFallbackHold}
              className="btn px-6 py-4 rounded-lg font-semibold w-full mb-4 text-lg select-none"
              style={{ backgroundColor: '#4a4a4a' }}
            >
              🎤 Please See Karaoke Host If You Are Having Trouble
            </button>
          </div>
          
          {/* Show Debug Logs Button */}
          {!showDebugPanel && debugLogs.length > 0 && (
            <button
              onClick={() => setShowDebugPanel(true)}
              className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 mb-4"
            >
              Show Debug Logs
            </button>
          )}
          
          {/* Debug Panel */}
          {showDebugPanel && debugLogs.length > 0 && (
            <div className="bg-gray-900 rounded-lg shadow-lg p-4 border-2 border-blue-500">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-blue-400">Debug Logs</h3>
                <button 
                  onClick={() => setShowDebugPanel(false)}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Hide
                </button>
              </div>
              <div className="bg-black rounded p-3 max-h-96 overflow-y-auto font-mono text-xs">
                {debugLogs.map((log, idx) => (
                  <div key={idx} className={`mb-1 ${
                    log.type === 'error' ? 'text-red-400' : 
                    log.type === 'warning' ? 'text-yellow-400' : 
                    log.type === 'success' ? 'text-green-500' : 
                    'text-gray-300'
                  }`}>
                    <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black py-0 md:py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center -mt-2 md:mt-0 -mb-6 md:mb-6">
          <div className="flex justify-center items-center relative">
            <div className="relative inline-block">
              {/* Original header text - hidden but preserved for potential revert */}
              <h1 className="text-4xl font-bold text-white flex items-end gap-2 sr-only" aria-hidden="true">
                <span className="text-gray-700" style={{ fontSize: '48px' }}>🎤</span>
                <span className="header-title uppercase flex flex-col items-center">
                  <span className="text-3xl">SIGN UP FOR</span>
                  <span>KARAOKE</span>
                </span>
                <span className="text-gray-700" style={{ fontSize: '48px', transform: 'scaleX(-1)' }}>🎤</span>
              </h1>
              
              {/* Banner image container - overlay scales relative to this */}
              <div className="relative inline-block">
                <img 
                  src="/header-banner.png" 
                  alt="Sign Up For Karaoke" 
                  className="h-60 md:h-20 object-contain block select-none pointer-events-none"
                  draggable="false"
                />

                {/* Mobile-only click-shrink: the img has ~75px of transparent
                    padding above/below the visible banner because it's a
                    wide banner rendered with object-contain inside h-60.
                    That dead zone was overlapping the top of Admin Controls
                    (parent has -mb-6 pulling them up), causing accidental
                    view toggles. Overlay is centered on the visible banner
                    and only as tall as the banner itself; desktop reverts
                    to full-img coverage. */}
                <button
                  type="button"
                  data-testid="banner-toggle-click-area"
                  aria-label="Toggle admin/user view"
                  onClick={handleBannerClick}
                  onMouseDown={startBannerHold}
                  onMouseUp={cancelBannerHold}
                  onMouseLeave={cancelBannerHold}
                  onTouchStart={startBannerHold}
                  onTouchEnd={cancelBannerHold}
                  onTouchCancel={cancelBannerHold}
                  className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-28 md:h-full w-full bg-transparent border-0 p-0 m-0 cursor-pointer outline-none focus:outline-none select-none"
                />
                
                {/* Clickable microphone overlay - DEACTIVATED, kept for potential future use */}
                <canvas 
                  ref={(canvas) => {
                    if (canvas && !canvas.dataset.initialized) {
                      canvas.dataset.initialized = 'true';
                      const ctx = canvas.getContext('2d');
                      const img = new Image();
                      img.crossOrigin = 'anonymous';
                      img.onload = () => {
                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);
                        canvas.dataset.loaded = 'true';
                      };
                      img.src = '/mic-overlay.png';
                    }
                  }}
                  className="absolute opacity-0 pointer-events-none"
                  style={{ 
                    height: '75%',
                    right: '8.5%',
                    top: '62%',
                    transform: 'translateY(-50%)'
                  }}
                  title="Admin access (deactivated)"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Admin Mode */}
        {isAdminMode ? (
          !isAdminLoggedIn ? (
            <AdminLogin onLogin={setIsAdminLoggedIn} />
          ) : (
            <AdminPanel
              queue={queue}
              onMoveToPosition={handleMoveToPosition}
              onRemove={handleRemoveEntry}
              onMarkCurrent={handleMarkCurrent}
              onMarkPlayed={handleMarkPlayed}
              onAdvanceQueue={handleAdvanceQueue}
              onAddToQueue={handleAddToQueue}
              onResetSession={handleResetSession}
              onEditEntry={handleEditEntry}
              onConvertToRegular={handleConvertToRegular}
              onSetInsertPoint={handleSetInsertPoint}
              insertPointEntryId={insertPointEntryId}
              groupsEnabled={groupsEnabled}
              entriesPaused={entriesPaused}
              setEntriesPaused={setEntriesPaused}
              error={error}
              setError={setError}
              success={success}
              setSuccess={setSuccess}
            />
          )
        ) : (
          /* User Mode */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="relative">
              {entriesPaused && !userEntry ? (
                <div
                  data-testid="signups-paused-message"
                  className="card rounded-lg shadow-lg p-8 text-center"
                >
                  <div className="text-5xl mb-4">⏸️</div>
                  <h2 className="text-2xl font-bold text-amber-400 mb-2">
                    Incoming Requests Are Paused
                  </h2>
                  <p className="text-gray-300 text-lg">
                    New signups are temporarily unavailable.
                  </p>
                </div>
              ) : (
                <>
                  <SearchSongForm
                    name={name}
                    setName={setName}
                    onSubmit={(e, title, artist, songId) => {
                      if (e) e.preventDefault();
                      handleJoinQueue(e, title, artist, songId);
                    }}
                    userEntry={userEntry}
                    userQueuePosition={userQueuePosition}
                    isLoading={isLoading}
                    onUpdatePitch={handleUpdatePitch}
                    deviceFingerprint={deviceFingerprint}
                  />

                  {/* Start Your Song Button - Only shown when user is at position 1 AND current */}
                  <StartYourSongButton
                    userEntry={userEntry}
                    queue={queue}
                    deviceFingerprint={deviceFingerprint}
                  />
                </>
              )}
              
              {/* Error Message - Overlay on Form */}
              {error && (
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                  <div className="message-error message-enter p-4 rounded-lg border max-w-lg w-full">
                    <div className="flex justify-between items-start">
                      <span>{error}</span>
                      <button 
                        onClick={() => setError('')}
                        className="ml-4 px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white font-semibold flex-shrink-0"
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Success Message - Overlay on Form */}
              {success && (
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                  <div className="message-success message-enter p-4 rounded-lg border max-w-lg w-full">
                    {success}
                  </div>
                </div>
              )}
              
              {/* BACKUP: Uncomment to use original text input form
              <UserFormTextInput
                name={name}
                setName={setName}
                songTitle={songTitle}
                setSongTitle={setSongTitle}
                songArtist={songArtist}
                setSongArtist={setSongArtist}
                onSubmit={handleJoinQueue}
                onUpdateSong={handleUpdateSong}
                onSelectNextSong={handleSelectNextSong}
                userEntry={userEntry}
                isLoading={isLoading}
              />
              */}
            </div>

            <div>
              <QueueDisplay queue={queue} rotationInfo={rotationInfo} />
            </div>
          </div>
        )}

        {/* ── User-facing button opening the catalog-removals modal ─
            Fix #23-followup (2026-09-16); modal conversion (2026-09-22
            host request). Only rendered in user mode (not admin) —
            admin has the richer confirm/dismiss panel above. Modal
            fetches from `/api/public/catalog-removals` on every open
            so the list is always current. ── */}
        {!isAdminMode && (
          <div className="mt-8 mb-10 text-center">
            <button
              type="button"
              data-testid="user-catalog-removals-btn"
              onClick={() => setIsCatalogRemovalsModalOpen(true)}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-200 text-base underline underline-offset-2 decoration-gray-600 hover:decoration-gray-400 transition-colors bg-transparent border-0 p-0 cursor-pointer"
            >
              <span>See which songs were recently removed from the catalog</span>
              <span aria-hidden="true" className="text-xs">↗</span>
            </button>
          </div>
        )}

        {isCatalogRemovalsModalOpen && (
          <CatalogRemovalsModal onClose={() => setIsCatalogRemovalsModalOpen(false)} />
        )}

      </div>
    </div>
  );
}

export default App;